(function(root, factory) {
  var api = factory(root);
  if (root) {
    root.DYOFCoverEditor = api;
  }
  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }
})(typeof window !== "undefined" ? window : null, function(root) {
  "use strict";

  // Translations (assets/dyof-i18n.js, non-English stores only). The English
  // stays here and is used as is when no language pack is loaded.
  function dyofInterpolate(text, params) {
    return params ? String(text).replace(/\{([A-Za-z0-9_]+)\}/g, function(match, name) {
      return Object.prototype.hasOwnProperty.call(params, name) && params[name] != null ? String(params[name]) : match;
    }) : text;
  }
  function dyofT(key, english, params) {
    var i18n = root && root.DYOFI18n;
    return i18n ? i18n.t(key, english, params) : dyofInterpolate(english, params);
  }
  function dyofPlural(key, count, english, params) {
    var i18n = root && root.DYOFI18n;
    var values = Object.assign({ count: count }, params || {});
    return i18n ? i18n.plural(key, count, english, params) : dyofInterpolate(Number(count) === 1 ? english.one : english.other, values);
  }

  function dyofLanguageActive() {
    var i18n = root && root.DYOFI18n;
    return Boolean(i18n && i18n.isActive());
  }

  // Keep translated local/contract errors; unexpected browser errors get a
  // translated fallback only with an active language pack. UK errors stay as is.
  function dyofError(message) {
    var error = new Error(message);
    if (dyofLanguageActive()) {
      Object.defineProperty(error, "dyofTranslated", { value: true });
    }
    return error;
  }

  var MAX_FILE_BYTES = 10 * 1024 * 1024;
  var CART_THUMBNAIL_MAX_EDGE = 640;
  var CART_THUMBNAIL_MAX_BYTES = 180 * 1024;
  var CART_THUMBNAIL_MIN_EDGE = 96;
  var CART_THUMBNAIL_RESOURCE_MAX_BYTES = 12 * 1024 * 1024;
  var CART_THUMBNAIL_RESOURCE_TIMEOUT_MS = 6000;
  var CART_THUMBNAIL_SLOW_MS = 3000;
  // The Filofax back logo (contract BACK_LOGO). Its colour behind an uploaded
  // photo, or a catalogue design missing from the reviewed table, is sampled
  // from the background drawn as print places it, at the policy generator's
  // 36 dpi. A sample that cannot be read uses the old builder's white logo,
  // so a cover never silently loses it.
  var BACK_LOGO_SAMPLE_PX_PER_PT = 0.5;
  var BACK_LOGO_FALLBACK_COLOUR = "#FFFFFF";
  var BACK_LOGO_SAMPLE_CACHE_LIMIT = 24;
  // Only immutable theme mock-ups live beyond a capture. Customer artwork,
  // uploaded previews and fonts retain their existing per-capture lifetime.
  var cartThumbnailMockupDataUrls = new Map();
  var cartThumbnailMockupImages = new Map();
  var cartThumbnailDiagnosedErrors = new WeakSet();
  var CART_THUMBNAIL_MOCKUP_PATH = /\/assets\/(dyof-(?:a5|personal)-clipbook-closed-(?:classic|modern|neutral|playful)-(?:middle|top)\.png)$/i;

  function cartThumbnailMockupUrl(source) {
    try {
      var url = new URL(source, root && root.location && root.location.href || "https://dyof.invalid/");
      if (!/^https?:$/.test(url.protocol) || !CART_THUMBNAIL_MOCKUP_PATH.test(url.pathname)) { return null; }
      // Preserve the scene's 1600px geometry; its final encoded edge is <=640.
      // No crop/format conversion: the middle/top transparency stays intact.
      url.searchParams.set("width", String(CART_THUMBNAIL_MAX_EDGE));
      if (source.indexOf("//") === 0) { return "//" + url.host + url.pathname + url.search; }
      if (source.charAt(0) === "/") { return url.pathname + url.search; }
      return url.href;
    } catch (ignored) {
      return null;
    }
  }

  function cartThumbnailSourceHint(sourceInput) {
    var source = String(sourceInput || "");
    if (/^data:/i.test(source)) { return "inline image"; }
    if (/^blob:/i.test(source)) { return "local image"; }
    try {
      var url = new URL(source, "https://dyof.invalid/");
      var match = url.pathname.match(CART_THUMBNAIL_MOCKUP_PATH);
      if (match) { return match[1].toLowerCase(); }
      if (/\.(?:woff2?|otf|ttf)$/i.test(url.pathname)) { return "font resource"; }
    } catch (ignored) { /* Never include arbitrary URLs or customer filenames. */ }
    return "image resource";
  }

  function cartThumbnailAbortError(signal) {
    if (signal && signal.reason && signal.reason.name === "AbortError") { return signal.reason; }
    var error = dyofError(dyofT("cover.capture.cancelled", "The exact cart thumbnail capture was cancelled."));
    error.name = "AbortError";
    return error;
  }

  function cartThumbnailTimeoutError(kind) {
    var error = cartThumbnailAbortError();
    error.dyofCartThumbnailTimeout = kind;
    return error;
  }

  // Race every await, including response.blob()/FileReader/toBlob, which can
  // stall even when the browser has already received an abort signal.
  function awaitCartThumbnail(pending, signal) {
    if (!signal) { return Promise.resolve(pending); }
    return new Promise(function(resolve, reject) {
      var settled = false;
      var onAbort = function() { finish(cartThumbnailAbortError(signal)); };
      var finish = function(error, value) {
        if (settled) { return; }
        settled = true;
        signal.removeEventListener("abort", onAbort);
        if (error) { reject(error); } else { resolve(value); }
      };
      Promise.resolve(pending).then(function(value) { finish(null, value); }, finish);
      if (signal.aborted) { onAbort(); }
      else { signal.addEventListener("abort", onAbort, { once: true }); }
    });
  }

  function cartThumbnailNow() {
    return root && root.performance && typeof root.performance.now === "function"
      ? root.performance.now()
      : Date.now();
  }

  function markCartThumbnailDiagnosed(error) {
    if (error && typeof error === "object") {
      cartThumbnailDiagnosedErrors.add(error);
      try { error.dyofCartThumbnailDiagnosed = true; } catch (ignored) { /* Read-only browser error. */ }
    }
    return error;
  }

  function cartThumbnailWasDiagnosed(error) {
    return Boolean(error && typeof error === "object"
      && (error.dyofCartThumbnailDiagnosed || cartThumbnailDiagnosedErrors.has(error)));
  }

  function createCartThumbnailDiagnostics(kind) {
    var started = cartThumbnailNow();
    var timings = [];
    var failedStage = "";
    var reported = false;
    return {
      async run(stage, work, signal) {
        var stageStarted = cartThumbnailNow();
        try {
          if (signal && signal.aborted) { throw cartThumbnailAbortError(signal); }
          return await awaitCartThumbnail(work(), signal);
        } catch (error) {
          if (!failedStage) { failedStage = stage; }
          throw error;
        } finally {
          timings.push({ stage: stage, ms: Math.max(0, Math.round(cartThumbnailNow() - stageStarted)) });
        }
      },
      finish(error, signal) {
        if (reported) { return; }
        reported = true;
        var elapsed = Math.max(0, cartThumbnailNow() - started);
        var elapsedMs = Math.round(elapsed);
        if (!error && elapsed <= CART_THUMBNAIL_SLOW_MS) { return; }
        var slowest = timings.filter(function(timing) { return timing.stage !== "scene flatten"; }).reduce(function(previous, current) {
          return !previous || current.ms > previous.ms ? current : previous;
        }, null);
        var reason = signal && signal.aborted && signal.reason;
        var timeoutKind = error && error.dyofCartThumbnailTimeout || reason && reason.dyofCartThumbnailTimeout;
        var resource = error && error.dyofCartThumbnailResource;
        var errorName = error && /^(?:Error|AbortError|TypeError|NetworkError|SecurityError|EncodingError|InvalidStateError)$/.test(error.name)
          ? error.name : error ? "Error" : "";
        // No raw Error/message, scene, state, arbitrary URL or pixel data.
        var diagnostic = {
          capture: kind,
          status: error ? "failed" : "slow",
          stage: failedStage || slowest && slowest.stage || "scene",
          elapsedMs: elapsedMs,
          abort: timeoutKind ? timeoutKind + " timeout"
            : error && (error.name === "AbortError" || signal && signal.aborted) ? "external cancel" : "none",
          error: errorName,
          resource: resource && resource.hint || "",
          phase: resource && resource.phase || "",
          httpStatus: error && error.dyofCartThumbnailHttpStatus || undefined,
          timings: timings.map(function(timing) { return { stage: timing.stage, ms: timing.ms }; })
        };
        markCartThumbnailDiagnosed(error);
        var logger = root && root.console || (typeof console !== "undefined" ? console : null);
        if (logger && typeof logger.warn === "function") { logger.warn("[DYOF cart thumbnail]", diagnostic); }
      }
    };
  }

  async function boundedCartThumbnailResource(source, work, externalSignal) {
    var controller = new AbortController();
    var onAbort = function() { controller.abort(cartThumbnailAbortError(externalSignal)); };
    if (externalSignal) {
      if (externalSignal.aborted) { onAbort(); }
      else { externalSignal.addEventListener("abort", onAbort, { once: true }); }
    }
    var timeout = setTimeout(function() {
      controller.abort(cartThumbnailTimeoutError("resource"));
    }, CART_THUMBNAIL_RESOURCE_TIMEOUT_MS);
    var phase = "fetch";
    try {
      if (controller.signal.aborted) { throw cartThumbnailAbortError(controller.signal); }
      return await awaitCartThumbnail(work(controller.signal, function(next) { phase = next; }), controller.signal);
    } catch (error) {
      if (error && typeof error === "object") {
        try {
          error.dyofCartThumbnailResource = { hint: cartThumbnailSourceHint(source), phase: phase };
        } catch (ignored) { /* Read-only native error; layer still has a safe hint. */ }
      }
      throw error;
    } finally {
      clearTimeout(timeout);
      if (externalSignal) { externalSignal.removeEventListener("abort", onAbort); }
      controller.abort();
    }
  }
  var FONT_OPTIONS = [
    { id: "minimal", label: "Minimal" },
    { id: "condensed", label: "Condensed" },
    { id: "casual", label: "Casual" },
    { id: "lettering", label: "Lettering" },
    { id: "happy", label: "Happy" },
    { id: "instrument-serif", label: "Instrument Serif" },
    { id: "instrument-serif-italic", label: "Instrument Serif Italic" }
  ];
  // Cover fonts the theme serves itself instead of a merchant font block: the
  // same bytes the PDF renderer embeds (assets/fonts/instrument-serif), each
  // face as its own family and font choice. Instrument Serif has no bold.
  var BUNDLED_COVER_FONTS = {
    "instrument-serif": { family: "DYOF Instrument Serif PDF", datasetKey: "coverFontInstrumentSerif" },
    "instrument-serif-italic": { family: "DYOF Instrument Serif Italic PDF", datasetKey: "coverFontInstrumentSerifItalic" }
  };
  var SHAPE_OPTIONS = ["none", "circle", "square", "oval", "rectangle", "ribbon", "pill"];
  var SHAPE_BASE_DIMENSIONS = {
    circle: { width: 180, height: 180 },
    square: { width: 170, height: 170 },
    oval: { width: 210, height: 90 },
    rectangle: { width: 210, height: 90 },
    ribbon: { width: 210, height: 72 },
    pill: { width: 210, height: 90 }
  };
  var MASK_OPTIONS = ["original", "square", "circle"];
  var DEFAULT_TEXT_COLOURS = ["#262626", "#FFFFFF", "#531491"];
  var DEFAULT_SHAPE_COLOURS = ["#FFFFFF", "#E7D1FD", "#262626"];
  var FALLBACK_COLOURS_BY_TAG = {
    Black: ["#262626", "#FFFFFF", "#737373"],
    White: ["#FFFFFF", "#262626", "#E7D1FD"],
    Blue: ["#354360", "#91A2C2", "#FFFFFF"],
    Pink: ["#C6878F", "#E6B5B9", "#FFFFFF"],
    Green: ["#3D594B", "#9EA59C", "#FFFFFF"],
    Red: ["#BA1E1E", "#F5B1CF", "#FFFFFF"],
    Purple: ["#7A64A9", "#D8CAE5", "#FFFFFF"],
    Yellow: ["#D8A345", "#FFF1DE", "#294899"],
    Orange: ["#DF6300", "#F3C5A7", "#FFFFFF"],
    Brow: ["#704210", "#E5D1B7", "#FFFFFF"]
  };
  var TEXT_LINE_HEIGHT_MULTIPLIER = 1.08;
  var TEXT_LINE_GAP = 4;
  var TEXT_LAYOUT_VERSION = "wp5-tight-v1";
  // Alphabetic baseline below each line centre (SVG dominant-baseline
  // "middle"), mirroring browserMiddleBaselineEm in the backend cover renderer.
  // The PDF paints every glyph's ink at exactly this position.
  var TEXT_BASELINE_MIDDLE_EM = {
    minimal: 0.265,
    condensed: 0.248,
    casual: 0.1775496884414128,
    lettering: 0.1815,
    happy: 401 / 1024,
    "instrument-serif": 510 / 2000,
    "instrument-serif-italic": 516 / 2000
  };
  // The PDF renderer rejects a line when its painted glyph outlines leave the
  // shape by more than 0.5 pt. The web fonts carry the production glyph
  // outlines, but a browser may paint or measure them slightly differently, so
  // the editor widens the ink it measures by one preview pixel (0.65-0.91 pt)
  // and uses 0.5 preview px. Any layout the editor accepts therefore also
  // passes the PDF check.
  var TEXT_INK_MARGIN = 1;
  // pdf-lib prints cover text as the shaped glyph run (ligatures and other
  // default features applied) on unkerned advance widths. The preview paints
  // and measures it the same way, so it shows the printed spacing and sizes
  // its shape from the printed width.
  var UNKERNED_TEXT_STYLE = "font-kerning:none;font-feature-settings:\"kern\" 0";
  // Cover text lines whose measurements are kept (textLineMetrics).
  var TEXT_METRICS_CACHE_LIMIT = 256;
  var ROTATION_SNAP_THRESHOLD = 5;
  // Matches the phone @media (max-width: 720px) blocks in dyof-configurator.css.
  var PHONE_LAYOUT_QUERY = "(max-width: 720px)";
  var CROP_MARGIN_SWIPE_SLOP = 10;
  // Karen's 26 August 2026 email supersedes the provisional Figma elastic
  // treatment. The archive contains one photographed band for every approved
  // colour plus separate Gold and Silver swatch textures.
  var ELASTIC_TEXTURE_SOURCE = {
    gmailMessageId: "1a03e4a55ba0232f",
    archiveName: "DYOF Elastic Closure.zip",
    archiveSha256: "d8d3afac0d799e451f6efae08cd2afcb8fc2e32edd871e22a016981bcab13ba9",
    receivedOn: "2026-08-26"
  };
  var ELASTIC_TEXTURES = {
    "silver-metallic": {
      assetName: "dyof-elastic-swatch-silver.jpg",
      datasetKey: "elasticSwatchSilver",
      sourceFile: "colours swatch/silver.jpg",
      sha256: "783a141df703edaebe255386632eb8b34696c398e2ca8c761fbfee6b6b688b04",
      sourceWidth: 500,
      sourceHeight: 332,
      swatch: { width: 66, height: 44, x: -23, y: -8, flipX: true }
    },
    "gold-metallic": {
      assetName: "dyof-elastic-swatch-gold.jpg",
      datasetKey: "elasticSwatchGold",
      sourceFile: "colours swatch/gold.jpg",
      sha256: "fb6609b4813ebaeb0695dc9f5ae70c525b553ec956dd0109107c8c3a98a79d9e",
      sourceWidth: 500,
      sourceHeight: 334,
      swatch: { width: 70, height: 46, x: -24, y: -9, flipX: true }
    }
  };
  var ELASTIC_BANDS = {
    navy: { assetName: "dyof-elastic-band-blue-marine.webp", datasetKey: "elasticBandBlueMarine", sourceWidth: 115, sourceHeight: 1500, sha256: "139b8ded196e4552a49f3566157a7d9e7a66cd75f8505f79fcda0ebf9f82d74c" },
    tan: { assetName: "dyof-elastic-band-beige.webp", datasetKey: "elasticBandBeige", sourceWidth: 99, sourceHeight: 1500, sha256: "20ee51e196f5b3fcb5affd6b21c9e475c3df510fdc9800e072cbe1941f69d600" },
    pink: { assetName: "dyof-elastic-band-fuchsia.webp", datasetKey: "elasticBandFuchsia", sourceWidth: 86, sourceHeight: 1500, sha256: "2a66d7adde5a23fd7d354526fd95a69fd32e6e6ae521d59e3b5923125a840eae" },
    "golden-yellow": { assetName: "dyof-elastic-band-yellow.webp", datasetKey: "elasticBandYellow", sourceWidth: 97, sourceHeight: 1500, sha256: "1432c19bd7198739fc9da28ffa985f8f258b1cfc1d6f002fb7390486916e02e6" },
    black: { assetName: "dyof-elastic-band-black.webp", datasetKey: "elasticBandBlack", sourceWidth: 93, sourceHeight: 1500, sha256: "27867af51be42380ab9190bd73f4ff2e42e0fd1e84f4f7c4aacd19ba5996e66d" },
    "grey-green": { assetName: "dyof-elastic-band-nato-green.webp", datasetKey: "elasticBandNatoGreen", sourceWidth: 82, sourceHeight: 1500, sha256: "6abc3f22a34bed2a183ee143dc3fcffb317560ea959f3360f28391c7375f2129" },
    purple: { assetName: "dyof-elastic-band-violet.webp", datasetKey: "elasticBandViolet", sourceWidth: 93, sourceHeight: 1500, sha256: "72a11bb94c7eb4982dfdb978b5aab367c8e84c2e4a77696574459de5ccbb9815" },
    red: { assetName: "dyof-elastic-band-red.webp", datasetKey: "elasticBandRed", sourceWidth: 87, sourceHeight: 1500, sha256: "acd27bd161e16037e0eef698c5b4d1e992bcd4a0c27f099817e6af6d87831389" },
    "silver-metallic": { assetName: "dyof-elastic-band-silver.webp", datasetKey: "elasticBandSilver", sourceWidth: 92, sourceHeight: 1500, sha256: "ebe60170a5e5ac82aa12e4a82612e154b0f63d63532516628afabc521e1869c5" },
    "gold-metallic": { assetName: "dyof-elastic-band-gold.webp", datasetKey: "elasticBandGold", sourceWidth: 98, sourceHeight: 1500, sha256: "bc8f3d4272150051109ab3313966306946fc17ad51cb919a39d4067d290b810f" }
  };
  var FONT_FALLBACK_FAMILIES = {
    minimal: "'DYOF Montserrat PDF', Montserrat, Arial, sans-serif",
    condensed: "'DYOF Montserrat PDF', Montserrat, Arial, sans-serif",
    casual: "'DYOF Montserrat PDF', Montserrat, Arial, sans-serif",
    lettering: "'DYOF Meritocracy PDF', Georgia, cursive",
    happy: "'DYOF Montserrat PDF', Montserrat, Arial, sans-serif",
    "instrument-serif": "'DYOF Instrument Serif PDF', Georgia, serif",
    "instrument-serif-italic": "'DYOF Instrument Serif Italic PDF', Georgia, serif"
  };
  var SVG_NS = "http://www.w3.org/2000/svg";
  var SAFE_EDGE_MM = 10;
  var BINDING_ZONE_MM = {
    a5: 38.3,
    personal: 41.56
  };
  var COVER_FACE_MM = {
    a5: { width: 185, height: 212 },
    personal: { width: 135, height: 175 }
  };
  var PREVIEW_CANVAS_SIZE = 1600;
  var A5_BACK_SHELL_TRANSLATE_X = -26;
  var A5_COVER_RECTS = {
    clipbook: { x: 474, y: 417, width: 625, height: 763 },
    notebook: { x: 516, y: 417, width: 579, height: 763 }
  };
  var PERSONAL_CLIPBOOK_COVER_RECT = { x: 506, y: 407, width: 588, height: 777 };
  var FULL_WRAP_CROP_ASPECT = 1.82;
  var PERSONAL_FULL_WRAP_CROP_ASPECT = 54 / 35;
  var PREVIEW_TEMPLATES = ["classic", "playful", "neutral", "modern"];
  var CATALOGUE_KEYS = ["clipbookA5", "plannerA5", "personal"];
  var THEME_FILTER_GROUPS = [
    { id: "filter:colour-minimal", label: "COLOUR & MINIMAL", values: ["Colour & Minimal"] },
    { id: "filter:floral-nature", label: "FLORAL & NATURE", values: ["Floral & Nature"] },
    { id: "filter:quotes-messages", label: "QUOTES & MESSAGES", values: ["Quotes & Messages"] },
    { id: "filter:classic-elegant", label: "CLASSIC & ELEGANT", values: ["Classic & Elegant"] },
    { id: "filter:love-positivity", label: "LOVE & POSITIVITY", values: ["Love & Positivity"] },
    { id: "filter:pets", label: "PETS", values: ["Pets"] },
    { id: "filter:special-occasions", label: "SPECIAL OCCASIONS", values: ["Special Occasions"] }
  ];
  var COLOUR_FILTER_GROUPS = [
    { id: "colour:black", label: "BLACK", swatch: "#222222", values: ["Black", "Grey", "Night"] },
    { id: "colour:white", label: "WHITE", swatch: "#F5F3EE", values: ["White", "Sand"] },
    { id: "colour:blue", label: "BLUE", swatch: "#3D73B9", values: ["Blue", "Blue Grey", "Blue and Mint", "Blueberry", "Navy", "Sky", "Sky Blue", "Turquoise"] },
    { id: "colour:pink", label: "PINK", swatch: "#E98BAA", values: ["Pink"] },
    { id: "colour:green", label: "GREEN", swatch: "#4F8A68", values: ["Apple", "Green", "Mint", "Olive"] },
    { id: "colour:red", label: "RED", swatch: "#C94A4A", values: ["Red", "Coquelicot", "Coral", "Maroon"] },
    { id: "colour:purple", label: "PURPLE", swatch: "#8065A8", values: ["Lilac", "Purple", "Versailles", "Violet"] },
    { id: "colour:yellow", label: "YELLOW", swatch: "#E8C84A", values: ["Mustard", "Ochre", "Yellow"] },
    { id: "colour:orange", label: "ORANGE", swatch: "#E88A45", values: ["Orange", "Peach"] },
    { id: "colour:brow", label: "BROW", swatch: "#8A6650", values: ["Brow"] }
  ];
  var COVER_PREVIEW_TEMPLATE_BY_THEME = {
    "Colour Block": "modern",
    "Father's Day": "neutral",
    "Filofax Collections": "classic",
    "Floral": "modern",
    "Joy": "playful",
    "Love": "playful",
    "MALE": "neutral",
    "Male": "neutral",
    "Monogram": "modern",
    "Mother's Day": "playful",
    "Pets": "playful",
    "Plain": "classic",
    "Plants": "playful",
    "Quotes": "modern",
    "Timeless": "classic",
    "Women's Day": "playful"
  };
  var LEGACY_SEARCH_FILTER_BY_THEME = {
    "Calibration": "Classic & Elegant",
    "Colour Block": "Colour & Minimal",
    "Father's Day": "Special Occasions",
    "Filofax Collections": "Classic & Elegant",
    "Floral": "Floral & Nature",
    "Joy": "Love & Positivity",
    "Love": "Love & Positivity",
    "MALE": "Classic & Elegant",
    "Male": "Classic & Elegant",
    "Monogram": "Colour & Minimal",
    "Mother's Day": "Special Occasions",
    "Pets": "Pets",
    "Plain": "Colour & Minimal",
    "Plants": "Floral & Nature",
    "Quotes": "Quotes & Messages",
    "Special Date": "Special Occasions",
    "Timeless": "Classic & Elegant",
    "Women's Day": "Love & Positivity"
  };
  var FALLBACK_REFILL_STYLE_SCENES = {
    classic: { previewTemplate: "classic" },
    everyday: { previewTemplate: "classic" },
    waves: { previewTemplate: "playful" },
    "soft-stripes": { previewTemplate: "playful" },
    luxury: { previewTemplate: "neutral" },
    gradient: { previewTemplate: "neutral" },
    minimal: { previewTemplate: "modern" },
    essential: { previewTemplate: "modern" },
    blocks: { previewTemplate: "modern" }
  };
  var FALLBACK_JOURNAL_SCENES = {
    "baby-journal": { scene: "baby", dedicatedScene: true },
    "pet-journal": { scene: "pet", dedicatedScene: true },
    "gardening-journal": { scene: "gardening", dedicatedScene: true },
    "cookbook-journal": { scene: "cookbook", dedicatedScene: true },
    "wedding-journal": { scene: "wedding", dedicatedScene: true },
    "life-essentials": { scene: "life-essentials", dedicatedScene: true }
  };

  function own(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, Number(value)));
  }

  function resolvedCoverSize(state) {
    return state && state.journey && state.journey.productType === "planner"
      ? "a5"
      : state && state.selection && state.selection.size === "personal"
        ? "personal"
        : "a5";
  }

  function normalizeHexColour(value) {
    var colour = String(value || "").toUpperCase();
    return /^#[0-9A-F]{6}$/.test(colour) ? colour : "";
  }

  function normalizeConfiguredColour(value) {
    var colour = String(value || "").trim().toUpperCase();
    if (/^#[0-9A-F]{3}$/.test(colour)) {
      colour = "#" + colour.slice(1).split("").map(function(character) {
        return character + character;
      }).join("");
    }
    return normalizeHexColour(colour);
  }

  function configuredColours(value) {
    var values = Array.isArray(value) ? value : String(value || "").split(/[,;]/);
    var seen = {};
    return values.map(normalizeConfiguredColour).filter(function(colour) {
      if (!colour || seen[colour]) { return false; }
      seen[colour] = true;
      return true;
    });
  }

  function normalizedColourSettingKey(value) {
    return String(value || "")
      .replace(/[–—]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  function colourSettingCandidates(item, size) {
    if (!item) { return []; }
    var raw = item.rawMerchandising || {};
    var theme = String(raw.theme || item.theme || "").trim();
    var title = String(item.title || "").trim();
    var base = item.needsTitle ? theme : [theme, title].filter(Boolean).join(" ");
    var suffix = size === "personal" ? "Personal" : "A5";
    var candidates = [base + " " + suffix];
    if (suffix === "A5") {
      candidates.push(base + " Clipbook");
    }
    if (title) {
      candidates.push(title + " " + suffix);
    }
    return candidates.map(function(value) { return value.replace(/\s+/g, " ").trim(); });
  }

  function findColourSetting(item, size, settings) {
    var source = settings && typeof settings === "object" ? settings : {};
    var candidates = colourSettingCandidates(item, size);
    for (var index = 0; index < candidates.length; index += 1) {
      if (source[candidates[index]]) { return source[candidates[index]]; }
    }
    var normalizedCandidates = candidates.map(normalizedColourSettingKey);
    var keys = Object.keys(source);
    for (var keyIndex = 0; keyIndex < keys.length; keyIndex += 1) {
      if (normalizedCandidates.indexOf(normalizedColourSettingKey(keys[keyIndex])) !== -1) {
        return source[keys[keyIndex]];
      }
    }
    // The retained 290-entry Shopify table contains one historic theme-prefix
    // typo ("Timelessc Stripes Blush Personal"). Resolve an exact, unique
    // title-and-size suffix without weakening matching for duplicated titles.
    var title = String(item && item.title || "").trim();
    var suffix = size === "personal" ? "Personal" : "A5";
    var titleAndSize = normalizedColourSettingKey(title + " " + suffix);
    if (titleAndSize) {
      var suffixMatches = keys.filter(function(key) {
        var normalizedKey = normalizedColourSettingKey(key);
        return normalizedKey === titleAndSize
          || normalizedKey.slice(-(titleAndSize.length + 1)) === " " + titleAndSize;
      });
      if (suffixMatches.length === 1) {
        return source[suffixMatches[0]];
      }
    }
    return null;
  }

  function fillPalette(values, fallback) {
    var palette = [];
    configuredColours(values).concat(configuredColours(fallback)).forEach(function(colour) {
      if (palette.length < 3 && palette.indexOf(colour) === -1) {
        palette.push(colour);
      }
    });
    DEFAULT_TEXT_COLOURS.concat(DEFAULT_SHAPE_COLOURS).forEach(function(colour) {
      if (palette.length < 3 && palette.indexOf(colour) === -1) {
        palette.push(colour);
      }
    });
    return palette.slice(0, 3);
  }

  function fallbackPaletteForItem(item) {
    var tags = item && Array.isArray(item.colourTags) ? item.colourTags : [];
    for (var index = 0; index < tags.length; index += 1) {
      if (FALLBACK_COLOURS_BY_TAG[tags[index]]) {
        return FALLBACK_COLOURS_BY_TAG[tags[index]].slice();
      }
    }
    return DEFAULT_TEXT_COLOURS.slice();
  }

  function resolveDesignPalettes(item, size, settings) {
    var configured = findColourSetting(item, size, settings) || {};
    var fallback = fallbackPaletteForItem(item);
    var configuredShapeColours = configuredColours(configured.Shapes || item && item.shapeColours);
    var configuredTextColours = configuredColours(configured.Color || item && item.textColour);
    var shape = fillPalette(
      configuredShapeColours.concat(configuredTextColours),
      fallback.concat(DEFAULT_SHAPE_COLOURS)
    );
    var text = fillPalette(
      configuredTextColours.concat(configuredShapeColours),
      fallback.concat(DEFAULT_TEXT_COLOURS)
    );
    return { text: text, shape: shape };
  }

  // Only the colours a design's colour table configures (Shapes, then Color),
  // with no defaults: the design's own palette for the back-logo rule.
  function designColourTable(item, size, settings) {
    var configured = findColourSetting(item, size, settings) || {};
    return configuredColours(
      configuredColours(configured.Shapes || item && item.shapeColours)
        .concat(configuredColours(configured.Color || item && item.textColour))
    );
  }

  function snapRotation(value, threshold) {
    var rotation = Number(value) || 0;
    var snapDistance = Number.isFinite(Number(threshold)) ? Number(threshold) : ROTATION_SNAP_THRESHOLD;
    while (rotation > 180) { rotation -= 360; }
    while (rotation < -180) { rotation += 360; }
    var targets = [-180, -90, 0, 90, 180];
    for (var index = 0; index < targets.length; index += 1) {
      if (Math.abs(rotation - targets[index]) <= snapDistance) {
        return targets[index];
      }
    }
    return rotation;
  }

  function isPhoneLayout() {
    return Boolean(root && root.matchMedia && root.matchMedia(PHONE_LAYOUT_QUERY).matches);
  }

  // On the phone layout the margin around a crop frame is touch-action: pan-y
  // (dyof-configurator.css), so the page can be scrolled through the editor.
  // The frame itself stays put there (cropBoxMovable: false), so a drag inside
  // it moves the photo in any direction under touch-action: none. A one-finger
  // touch that starts in the margin is held until it has a direction: a
  // vertical swipe belongs to the page and never moves the photo, while a
  // sideways drag moves the photo exactly as before. The frame, its handles,
  // pinch zoom, the mouse and wider layouts are unaffected.
  function cropMarginSwipeStart(event) {
    var detail = event && event.detail;
    var pointer = detail && detail.originalEvent;
    if (!pointer || detail.action !== "move") { return null; }
    if (pointer.pointerType ? pointer.pointerType === "mouse" : !pointer.changedTouches) { return null; }
    if (!isPhoneLayout()) { return null; }
    var target = pointer.target;
    if (target && typeof target.closest === "function" && target.closest(".cropper-crop-box")) { return null; }
    var point = pointer.changedTouches ? pointer.changedTouches[0] : pointer;
    return { x: point.clientX, y: point.clientY, axis: null };
  }

  // True while Cropper must not move the photo for this margin swipe.
  function cropMarginSwipeHeld(swipe, event) {
    var pointer = event && event.detail && event.detail.originalEvent;
    var point = pointer && (pointer.changedTouches ? pointer.changedTouches[0] : pointer);
    if (!swipe.axis && point) {
      var deltaX = Math.abs(point.clientX - swipe.x);
      var deltaY = Math.abs(point.clientY - swipe.y);
      if (Math.max(deltaX, deltaY) >= CROP_MARGIN_SWIPE_SLOP) {
        swipe.axis = deltaY >= deltaX ? "y" : "x";
      }
    }
    return swipe.axis !== "x";
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function titleCase(value) {
    return String(value || "").replace(/-/g, " ").replace(/\b\w/g, function(letter) {
      return letter.toUpperCase();
    });
  }

  // Keep lookup labels and identifiers in English; these aliases are for UI
  // only. In particular, configuredFont matches the original FONT_OPTIONS.
  function fontDisplayLabel(id) {
    switch (id) {
      case "minimal": return dyofT("cover.font.minimal", "Minimal");
      case "condensed": return dyofT("cover.font.condensed", "Condensed");
      case "casual": return dyofT("cover.font.casual", "Casual");
      case "lettering": return dyofT("cover.font.lettering", "Lettering");
      case "happy": return dyofT("cover.font.happy", "Happy");
      case "instrument-serif": return dyofT("cover.font.instrument_serif", "Instrument Serif");
      case "instrument-serif-italic": return dyofT("cover.font.instrument_serif_italic", "Instrument Serif Italic");
      default: return id;
    }
  }

  function shapeDisplayLabel(id) {
    switch (id) {
      case "original": return dyofT("cover.shape.original", "Original");
      case "circle": return dyofT("cover.shape.circle", "Circle");
      case "square": return dyofT("cover.shape.square", "Square");
      case "oval": return dyofT("cover.shape.oval", "Oval");
      case "rectangle": return dyofT("cover.shape.rectangle", "Rectangle");
      case "ribbon": return dyofT("cover.shape.ribbon", "Ribbon");
      case "pill": return dyofT("cover.shape.pill", "Pill");
      default: return titleCase(id);
    }
  }

  function filterDisplayLabel(option) {
    switch (option.id) {
      case "filter:colour-minimal": return dyofT("cover.filters.colour_minimal", "COLOUR & MINIMAL");
      case "filter:floral-nature": return dyofT("cover.filters.floral_nature", "FLORAL & NATURE");
      case "filter:quotes-messages": return dyofT("cover.filters.quotes_messages", "QUOTES & MESSAGES");
      case "filter:classic-elegant": return dyofT("cover.filters.classic_elegant", "CLASSIC & ELEGANT");
      case "filter:love-positivity": return dyofT("cover.filters.love_positivity", "LOVE & POSITIVITY");
      case "filter:pets": return dyofT("cover.filters.pets", "PETS");
      case "filter:special-occasions": return dyofT("cover.filters.special_occasions", "SPECIAL OCCASIONS");
      case "colour:black": return dyofT("cover.filters.black", "BLACK");
      case "colour:white": return dyofT("cover.filters.white", "WHITE");
      case "colour:blue": return dyofT("cover.filters.blue", "BLUE");
      case "colour:pink": return dyofT("cover.filters.pink", "PINK");
      case "colour:green": return dyofT("cover.filters.green", "GREEN");
      case "colour:red": return dyofT("cover.filters.red", "RED");
      case "colour:purple": return dyofT("cover.filters.purple", "PURPLE");
      case "colour:yellow": return dyofT("cover.filters.yellow", "YELLOW");
      case "colour:orange": return dyofT("cover.filters.orange", "ORANGE");
      case "colour:brow": return dyofT("cover.filters.brow", "BROW");
      default: return option.label;
    }
  }

  function elasticDisplayLabel(id, fallback) {
    switch (id) {
      case "navy": return dyofT("cover.elastic.navy", "Blue Marine");
      case "tan": return dyofT("cover.elastic.tan", "Beige");
      case "pink": return dyofT("cover.elastic.pink", "Fuchsia");
      case "golden-yellow": return dyofT("cover.elastic.golden_yellow", "Yellow");
      case "black": return dyofT("cover.elastic.black", "Black");
      case "grey-green": return dyofT("cover.elastic.grey_green", "NATO Green");
      case "purple": return dyofT("cover.elastic.purple", "Violet");
      case "red": return dyofT("cover.elastic.red", "Red");
      case "silver-metallic": return dyofT("cover.elastic.silver", "Silver");
      case "gold-metallic": return dyofT("cover.elastic.gold", "Gold");
      default: return fallback;
    }
  }

  function objectDisplayName(object, assets) {
    if (object.type === "text" && object.name === "Text block") {
      return dyofT("cover.text.default_name", "Text block");
    }
    if (object.type === "image" && object.name === "Image") {
      var asset = assets && typeof assets.peek === "function" ? assets.peek(object.assetId) : null;
      // An actual uploaded filename, even "Image", belongs to the customer.
      if (!(asset && asset.name === object.name)) {
        return dyofT("cover.image.default_name", "Image");
      }
    }
    return object.name;
  }

  // Apostrophes in these text nodes were literal in the UK HTML. They are
  // safe outside attributes; preserve those bytes while escaping markup.
  function escapeHtmlText(value) {
    return escapeHtml(value).replace(/&#039;/g, "'");
  }

  function filterGroup(groups, value) {
    return groups.filter(function(group) {
      return group.id === value || group.values.indexOf(value) !== -1;
    })[0] || null;
  }

  function filterMatches(groups, selected, rawValue) {
    if (!selected || selected === "all") { return true; }
    var group = groups.filter(function(option) { return option.id === selected; })[0];
    var values = Array.isArray(rawValue) ? rawValue : [rawValue];
    return group
      ? values.some(function(value) { return group.values.indexOf(value) !== -1; })
      : values.indexOf(selected) !== -1;
  }

  function themeFilterMatches(selected, rawValue) {
    return filterMatches(THEME_FILTER_GROUPS, selected, rawValue);
  }

  function colourFilterMatches(selected, rawValue) {
    return filterMatches(COLOUR_FILTER_GROUPS, selected, rawValue);
  }

  function availableFilterGroups(groups, items, property) {
    return groups.filter(function(group) {
      return items.some(function(item) {
        return filterMatches(groups, group.id, item[property]);
      });
    });
  }

  function stablePatternSlug(value) {
    var normalized = String(value || "").trim();
    if (normalized.normalize) {
      normalized = normalized.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
    }
    return normalized
      .replace(/&/g, " and ")
      .replace(/[’']/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function blockIdForCatalogueItem(item) {
    if (item && item.blockId) { return String(item.blockId); }
    return String(item && item.id || "").replace(/^uk-theme:/, "");
  }

  function validPreviewTemplate(value) {
    return PREVIEW_TEMPLATES.indexOf(value) !== -1 ? value : "";
  }

  function resolveCoverPreviewTemplate(item, fallback) {
    var explicit = validPreviewTemplate(item && item.previewTemplate);
    if (explicit) { return explicit; }
    var theme = item && (item.newTheme || item.theme);
    var legacy = validPreviewTemplate(COVER_PREVIEW_TEMPLATE_BY_THEME[theme]);
    return legacy || validPreviewTemplate(fallback) || "classic";
  }

  function normalizeThumbnailFocalPoint(value) {
    var source = value && typeof value === "object" ? value : {};
    var x = Number(source.x);
    var y = Number(source.y);
    var zoom = Number(source.zoom);
    return {
      x: Number.isFinite(x) ? clamp(x, 0, 1) : 0.5,
      y: Number.isFinite(y) ? clamp(y, 0, 1) : 0.5,
      zoom: Number.isFinite(zoom) ? clamp(zoom, 1, 3) : 1
    };
  }

  function normalizeThumbnailFocal(value) {
    var source = value && typeof value === "object" ? value : {};
    if (own(source, "x") || own(source, "y") || own(source, "zoom")) {
      return normalizeThumbnailFocalPoint(source);
    }
    var normalized = {};
    CATALOGUE_KEYS.concat(["default"]).forEach(function(key) {
      if (source[key] && typeof source[key] === "object") {
        normalized[key] = normalizeThumbnailFocalPoint(source[key]);
      }
    });
    return Object.keys(normalized).length ? normalized : normalizeThumbnailFocalPoint(null);
  }

  function resolveThumbnailFocal(value, key) {
    var source = value && typeof value === "object" ? value : {};
    if (own(source, "x") || own(source, "y") || own(source, "zoom")) {
      return normalizeThumbnailFocalPoint(source);
    }
    return normalizeThumbnailFocalPoint(source[key] || source.default);
  }

  function productionUrlIdentity(value) {
    return normalizeProductionUrl(value).split(/[?#]/)[0];
  }

  function normalizePrintPreviewManifest(value) {
    var source = value && typeof value === "object" ? value : null;
    var valid = Boolean(
      source
      && source.schemaVersion === 1
      && source.strict === true
      && source.assets
      && typeof source.assets === "object"
      && !Array.isArray(source.assets)
    );
    return {
      schemaVersion: valid ? 1 : 0,
      release: valid ? String(source.release || "") : "",
      // WP4 must always fail closed. A missing, malformed, or partial manifest
      // can hide catalogue designs, but it must never revive unrelated legacy
      // preview rasters.
      strict: true,
      assets: valid ? source.assets : {}
    };
  }

  function normalizePrintAsset(source, key, blockId, printPreviewManifest) {
    var configured = source.printAsset && source.printAsset[key] || {};
    var verified = printPreviewManifest
      && printPreviewManifest.assets
      && printPreviewManifest.assets[blockId]
      && printPreviewManifest.assets[blockId][key]
      || null;
    var legacyPreview = source.preview && source.preview[key];
    var productionRef = String(
      configured.productionRef
      || source.production && source.production[key]
      || ""
    );
    var verifiedProductionRef = String(verified && verified.productionRef || "");
    var strictManifest = Boolean(printPreviewManifest && printPreviewManifest.strict);
    var verifiedId = String(verified && verified.id || "");
    var verifiedIdMatch = /^pa1_([a-f0-9]{64})$/.exec(verifiedId);
    var verifiedFront = String(verified && verified.preview && verified.preview.front || "");
    var verifiedBack = String(verified && verified.preview && verified.preview.back || "");
    var useVerifiedPreview = Boolean(
      verified
      && verified.preview
      && verifiedIdMatch
      && verifiedFront
      && verifiedBack
      && productionUrlIdentity(verifiedProductionRef)
      && productionUrlIdentity(verifiedProductionRef) === productionUrlIdentity(productionRef)
    );
    var rawPreview = useVerifiedPreview
      ? verified.preview
      : strictManifest ? {} : configured.preview || legacyPreview || {};
    var preview = typeof rawPreview === "string"
      ? { front: rawPreview, back: rawPreview }
      : {
          front: String(rawPreview.front || rawPreview.back || ""),
          back: String(rawPreview.back || rawPreview.front || "")
        };
    var sourceId = String(source.id || ("uk-theme:" + blockId));
    var normalized = {
      id: String(
        useVerifiedPreview
          ? verifiedId
          : strictManifest ? "" : configured.id || (sourceId + ":" + key)
      ),
      productionRef: productionRef,
      preview: preview
    };
    if (useVerifiedPreview) { normalized.previewDerivedFromProduction = true; }
    // The reviewed back-logo colour of this print asset ("" when its artwork
    // already carries the Filofax logo). Absent: the editor samples it.
    if (useVerifiedPreview && typeof verified.backLogo === "string" && /^(#[0-9A-F]{6})?$/.test(verified.backLogo)) {
      normalized.backLogo = verified.backLogo;
    }
    if (strictManifest && !useVerifiedPreview) { normalized.previewManifestMissing = true; }
    return normalized;
  }

  function normalizeCatalogueItem(item, manifest, printPreviewManifest) {
    var source = item && typeof item === "object" ? item : {};
    var blockId = blockIdForCatalogueItem(source);
    var blockPatterns = manifest && manifest.blockPatterns || {};
    var logicalPattern = blockPatterns[blockId]
      || stablePatternSlug(source.logicalPattern || source.title || source.theme);
    var metadata = manifest && manifest.covers && manifest.covers[logicalPattern];
    var rawTheme = String(source.theme || "");
    var rawColour = String(source.colour || "");
    var colourTags = metadata && Array.isArray(metadata.colourTags)
      ? metadata.colourTags.slice()
      : rawColour && rawColour !== "Other" ? [rawColour] : [];
    var printAsset = {};
    var preview = Object.assign({}, source.preview || {});
    var production = Object.assign({}, source.production || {});
    CATALOGUE_KEYS.forEach(function(key) {
      var asset = normalizePrintAsset(source, key, blockId, printPreviewManifest);
      printAsset[key] = asset;
      if (
        source.printAsset && source.printAsset[key]
        || asset.previewDerivedFromProduction
        || asset.previewManifestMissing
      ) {
        preview[key] = asset.preview;
        production[key] = asset.productionRef;
      }
    });
    return Object.assign({}, source, {
      logicalPattern: logicalPattern,
      newTheme: metadata && metadata.newTheme || source.newTheme || rawTheme,
      searchFilter: metadata && metadata.searchFilter
        || source.searchFilter
        || LEGACY_SEARCH_FILTER_BY_THEME[rawTheme]
        || rawTheme,
      previewTemplate: resolveCoverPreviewTemplate(metadata || source, "classic"),
      colourTags: colourTags,
      thumbnailFocal: normalizeThumbnailFocal(
        metadata && metadata.thumbnailFocal || source.thumbnailFocal
      ),
      printAsset: printAsset,
      preview: preview,
      production: production,
      rawMerchandising: {
        collection: String(source.collection || ""),
        theme: rawTheme,
        colour: rawColour
      }
    });
  }

  function normalizeCatalogue(catalogue, manifest, printPreviewManifest) {
    return (Array.isArray(catalogue) ? catalogue : []).map(function(item) {
      return normalizeCatalogueItem(item, manifest || {}, printPreviewManifest || {});
    });
  }

  function resolveRefillPreviewTemplate(manifest, state) {
    var style = state && state.selection && state.selection.style;
    var scenes = manifest && manifest.refillStyleScenes || FALLBACK_REFILL_STYLE_SCENES;
    var configured = scenes && scenes[style];
    var template = validPreviewTemplate(configured && configured.previewTemplate);
    var lifestyle = state && state.journey && state.journey.lifestyle;
    return template || validPreviewTemplate(lifestyle) || "classic";
  }

  function resolveJournalPreviewScene(manifest, fill) {
    var scenes = manifest && manifest.journalScenes || FALLBACK_JOURNAL_SCENES;
    var configured = scenes && scenes[fill];
    return configured ? Object.assign({}, configured) : null;
  }

  function selectionMenuHtml(config) {
    var selectedOption = config.options.filter(function(option) {
      return option.id === config.selected;
    })[0] || null;
    var html = '<div class="dyof__figma-menu dyof__figma-menu--' + config.kind + (config.open ? ' is-open' : '') + '" data-cover-menu="' + config.name + '">';
    html += '<button type="button" class="dyof__figma-menu-trigger" data-cover-menu-toggle="' + config.name + '" aria-haspopup="listbox" aria-expanded="' + config.open + '">';
    html += '<span class="dyof__figma-menu-label">' + escapeHtml(config.label) + '</span>';
    if (selectedOption && config.kind === "theme") {
      html += '<span class="dyof__figma-menu-selection dyof__figma-menu-selection--clearable">' + escapeHtml(filterDisplayLabel(selectedOption)) + '</span>';
    } else if (selectedOption && config.kind === "colour") {
      html += '<span class="dyof__figma-menu-selection-swatch' + (selectedOption.rainbow ? ' is-rainbow' : '') + '" style="--swatch:' + escapeHtml(selectedOption.swatch || "#fff") + '" aria-hidden="true"></span>';
    }
    html += '<i class="dyof__figma-menu-chevron" aria-hidden="true"></i></button>';
    if (selectedOption && (config.kind === "theme" || config.kind === "colour")) {
      var lightSelectedColour = config.kind === "colour"
        && selectedOption.id === "colour:white";
      html += '<button type="button" class="dyof__figma-menu-clear dyof__figma-menu-clear--' + config.kind + (lightSelectedColour ? ' is-light' : '') + '" data-cover-menu-clear="' + config.name + '" aria-label="' + escapeHtml(config.kind === "theme" ? dyofT("cover.filters.clear_theme", "Clear theme filter") : dyofT("cover.filters.clear_colour", "Clear colour filter")) + '"><span aria-hidden="true"></span></button>';
    }
    if (config.open) {
      html += '<div class="dyof__figma-menu-panel" role="listbox" aria-label="' + escapeHtml(config.label) + '">';
      config.options.forEach(function(option) {
        var isSelected = option.id === config.selected;
        if (config.kind === "colour") {
          html += '<button type="button" class="dyof__figma-menu-swatch' + (option.rainbow ? ' is-rainbow' : '') + '" data-cover-menu-option="' + config.name + '" data-cover-menu-value="' + escapeHtml(option.id) + '" role="option" aria-label="' + escapeHtml(filterDisplayLabel(option)) + '" aria-selected="' + isSelected + '" style="--swatch:' + escapeHtml(option.swatch || "#fff") + '">' + (isSelected ? '<span class="dyof__filter-swatch-remove-mark' + (option.id === "colour:white" ? ' is-light' : '') + '" aria-hidden="true">×</span>' : '') + '</button>';
        } else if (config.kind === "source") {
          html += '<button type="button" class="dyof__figma-menu-option dyof__figma-menu-source-option" data-cover-menu-option="' + config.name + '" data-cover-menu-value="' + escapeHtml(option.id) + '" role="option" aria-selected="' + isSelected + '">';
          html += '<strong>' + escapeHtml(filterDisplayLabel(option)) + '</strong>';
          html += option.id === "catalogue"
            ? '<span>' + dyofT("cover.selection.catalogue_help", "Start with <b>our design</b> and make it yours by adding your <b>name, photo or logo.</b>") + '</span>'
            : '<span>' + dyofT("cover.selection.upload_help", "Use your <b>own image</b> as the entire background and add your own <b>name, photo or logo.</b>") + '</span>';
          html += '</button>';
        } else {
          html += '<button type="button" class="dyof__figma-menu-option" data-cover-menu-option="' + config.name + '" data-cover-menu-value="' + escapeHtml(option.id) + '" role="option" aria-selected="' + isSelected + '">' + escapeHtml(filterDisplayLabel(option)) + '</button>';
        }
      });
      html += '</div>';
    }
    return html + '</div>';
  }

  function uploadChipLabel(value) {
    var name = String(value || dyofT("cover.image.file_name", "Uploaded file")).trim() || dyofT("cover.image.file_name", "Uploaded file");
    var characters = Array.from(name);
    if (characters.length <= 12) { return characters.join(""); }
    return characters.slice(0, 12).join("") + "...";
  }

  function shortHash(value) {
    var hash = 2166136261;
    var input = String(value || "");
    for (var index = 0; index < input.length; index += 1) {
      hash ^= input.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return "ov-" + (hash >>> 0).toString(16).padStart(8, "0");
  }

  function rangeFillStyle(value, min, max) {
    var percent = clamp((Number(value) - Number(min)) / (Number(max) - Number(min)) * 100, 0, 100);
    return ' style="--dyof-range-fill:' + percent.toFixed(2) + '%"';
  }

  function normalizeProductionUrl(value) {
    var url = String(value || "");
    return url.indexOf("//") === 0 ? "https:" + url : url;
  }

  function normalizeFontToken(value) {
    return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  }

  function quoteFontFamily(value) {
    var family = String(value || "").trim();
    if (!family) { return ""; }
    return "'" + family.replace(/\\/g, "\\\\").replace(/'/g, "\\'") + "'";
  }

  function readJson(element, selector, fallback) {
    var script = element.querySelector(selector) || document.querySelector(selector);
    if (!script) {
      return fallback;
    }
    try {
      return JSON.parse(script.textContent || "");
    } catch (error) {
      return fallback;
    }
  }

  function svgElement(name, attributes, text) {
    var element = document.createElementNS(SVG_NS, name);
    Object.keys(attributes || {}).forEach(function(key) {
      element.setAttribute(key, String(attributes[key]));
    });
    if (text != null) {
      element.textContent = String(text);
    }
    return element;
  }

  // The scene's shading layer darkens the cover by about 9%, so the artwork is
  // lifted to compensate. A flat brightness(1.08) pushed pale channels past
  // white and flattened pastels to near white; this gamma curve lifts the
  // mid-tones by the same amount but never past white, so light colours keep
  // their hue. Print never uses this: it only tones the desk-scene preview.
  function coverArtworkToneFilter(id) {
    var filter = svgElement("filter", { id: id, "color-interpolation-filters": "sRGB" });
    var transfer = svgElement("feComponentTransfer");
    ["feFuncR", "feFuncG", "feFuncB"].forEach(function(name) {
      transfer.appendChild(svgElement(name, { type: "gamma", amplitude: 1, exponent: 0.89, offset: 0 }));
    });
    filter.appendChild(transfer);
    filter.appendChild(svgElement("feColorMatrix", { type: "saturate", values: 1.08 }));
    return filter;
  }

  function shapeDimensions(shape) {
    return SHAPE_BASE_DIMENSIONS[shape] || SHAPE_BASE_DIMENSIONS.rectangle;
  }

  function shapeDescriptor(shape, width, height, fill) {
    var halfWidth = width / 2;
    var halfHeight = height / 2;
    if (shape === "circle") {
      return {
        name: "circle",
        attributes: { cx: 0, cy: 0, r: Math.min(halfWidth, halfHeight), fill: fill }
      };
    }
    if (shape === "oval") {
      return {
        name: "ellipse",
        attributes: { cx: 0, cy: 0, rx: halfWidth, ry: halfHeight, fill: fill }
      };
    }
    if (shape === "ribbon") {
      return {
        name: "polygon",
        attributes: {
          points: [
            -halfWidth + "," + -halfHeight,
            halfWidth + "," + -halfHeight,
            width * 0.36 + "," + halfHeight,
            width * -0.36 + "," + halfHeight
          ].join(" "),
          fill: fill
        }
      };
    }
    return {
      name: "rect",
      attributes: {
        x: -halfWidth,
        y: -halfHeight,
        width: width,
        height: height,
        rx: shape === "pill" ? halfHeight : 0,
        fill: fill
      }
    };
  }

  function shapeSvgElement(shape, width, height, fill) {
    var descriptor = shapeDescriptor(shape, width, height, fill);
    return svgElement(descriptor.name, descriptor.attributes);
  }

  function shapeIconHtml(shape) {
    var dimensions = shapeDimensions(shape);
    var descriptor = shapeDescriptor(shape, dimensions.width, dimensions.height, "currentColor");
    var attributes = Object.keys(descriptor.attributes).map(function(key) {
      return key + '="' + escapeHtml(descriptor.attributes[key]) + '"';
    }).join(" ");
    return '<svg viewBox="' + (-dimensions.width / 2) + ' ' + (-dimensions.height / 2) + ' ' + dimensions.width + ' ' + dimensions.height + '" width="' + dimensions.width + '" height="' + dimensions.height + '" aria-hidden="true" focusable="false"><' + descriptor.name + ' ' + attributes + '></' + descriptor.name + '></svg>';
  }

  function dataUrlForFile(file) {
    return new Promise(function(resolve, reject) {
      var reader = new FileReader();
      reader.onload = function() { resolve(reader.result); };
      reader.onerror = function() { reject(reader.error || dyofError(dyofT("cover.upload.read_failed", "The file could not be read."))); };
      reader.readAsDataURL(file);
    });
  }

  function imageDimensions(url) {
    return new Promise(function(resolve, reject) {
      var image = new Image();
      image.onload = function() {
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      };
      image.onerror = function() { reject(dyofError(dyofT("cover.upload.decode_failed", "The image could not be decoded."))); };
      image.src = url;
    });
  }

  function loadSampleImage(url) {
    return new Promise(function(resolve, reject) {
      var ImageConstructor = root && root.Image;
      if (!ImageConstructor) {
        reject(dyofError(dyofT("cover.upload.decode_failed", "The image could not be decoded.")));
        return;
      }
      var image = new ImageConstructor();
      // A catalogue preview is read back from a canvas, so it must load CORS-clean.
      if (!/^(data|blob):/i.test(String(url))) { image.crossOrigin = "anonymous"; }
      image.onload = function() { resolve(image); };
      image.onerror = function() { reject(dyofError(dyofT("cover.upload.decode_failed", "The image could not be decoded."))); };
      image.src = url;
    });
  }

  function bytesToDataUrl(blob) {
    return new Promise(function(resolve, reject) {
      var reader = new FileReader();
      reader.onload = function() { resolve(reader.result); };
      reader.onerror = function() { reject(reader.error || dyofError(dyofT("cover.upload.preview_store_failed", "The preview could not be stored."))); };
      reader.readAsDataURL(blob);
    });
  }

  function cartThumbnailCanvas(width, height, options) {
    var createCanvas = options && options.createCanvas;
    var canvas = createCanvas
      ? createCanvas()
      : root && root.document
        ? root.document.createElement("canvas")
        : null;
    if (!canvas) {
      throw dyofError(dyofT("cover.capture.canvas_unsupported", "Cart thumbnail capture requires browser canvas support."));
    }
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    return canvas;
  }

  function cartThumbnailBlob(canvas, contentType, quality) {
    return new Promise(function(resolve, reject) {
      if (!canvas || typeof canvas.toBlob !== "function") {
        reject(dyofError(dyofT("cover.capture.encoding_unavailable", "Cart thumbnail encoding is unavailable in this browser.")));
        return;
      }
      canvas.toBlob(function(blob) {
        if (!blob) {
          reject(dyofError(dyofT("cover.capture.encoding_failed", "The cart thumbnail could not be encoded.")));
          return;
        }
        resolve(blob);
      }, contentType, quality);
    });
  }

  async function cartThumbnailBase64(blob, options) {
    if (options && typeof options.base64Encode === "function") {
      return options.base64Encode(blob);
    }
    if (!blob || typeof blob.arrayBuffer !== "function" || !root || typeof root.btoa !== "function") {
      throw dyofError(dyofT("cover.capture.base64_unavailable", "Cart thumbnail base64 encoding is unavailable in this browser."));
    }
    var bytes = new Uint8Array(await blob.arrayBuffer());
    var binary = "";
    var chunkSize = 0x8000;
    for (var offset = 0; offset < bytes.length; offset += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(offset, offset + chunkSize));
    }
    return root.btoa(binary);
  }

  function drawScaledCartThumbnail(source, width, height, options) {
    var canvas = cartThumbnailCanvas(width, height, options);
    var context = canvas.getContext && canvas.getContext("2d");
    if (!context) {
      throw dyofError(dyofT("cover.capture.context_failed", "Cart thumbnail capture could not create a drawing context."));
    }
    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  async function encodeCartThumbnail(sourceCanvas, kind, optionsInput) {
    var options = optionsInput || {};
    var maxEdge = Math.min(
      CART_THUMBNAIL_MAX_EDGE,
      Math.max(CART_THUMBNAIL_MIN_EDGE, Math.round(Number(options.maxEdge) || CART_THUMBNAIL_MAX_EDGE))
    );
    var maxBytes = Math.min(
      CART_THUMBNAIL_MAX_BYTES,
      Math.max(1024, Math.round(Number(options.maxBytes) || CART_THUMBNAIL_MAX_BYTES))
    );
    var sourceWidth = Number(sourceCanvas && sourceCanvas.width);
    var sourceHeight = Number(sourceCanvas && sourceCanvas.height);
    if (!(sourceWidth > 0) || !(sourceHeight > 0)) {
      throw dyofError(dyofT("cover.capture.empty_canvas", "Cart thumbnail capture produced an empty canvas."));
    }
    var scale = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight));
    var width = Math.max(1, Math.round(sourceWidth * scale));
    var height = Math.max(1, Math.round(sourceHeight * scale));
    var qualitySteps = [0.84, 0.76, 0.68, 0.58, 0.48, 0.38];
    var contentTypes = ["image/webp", "image/png"];
    var result = null;

    for (var sizeAttempt = 0; sizeAttempt < 8 && !result; sizeAttempt += 1) {
      var canvas = width === sourceWidth && height === sourceHeight
        ? sourceCanvas
        : drawScaledCartThumbnail(sourceCanvas, width, height, options);
      for (var typeIndex = 0; typeIndex < contentTypes.length && !result; typeIndex += 1) {
        for (var qualityIndex = 0; qualityIndex < qualitySteps.length; qualityIndex += 1) {
          var blob;
          try {
            blob = await cartThumbnailBlob(canvas, contentTypes[typeIndex], qualitySteps[qualityIndex]);
          } catch (error) {
            if (typeIndex === contentTypes.length - 1) { throw error; }
            break;
          }
          if (blob.type !== "image/webp" && blob.type !== "image/png") {
            if (typeIndex === contentTypes.length - 1) {
              throw dyofError(dyofT("cover.capture.format_unsupported", "The browser returned an unsupported cart thumbnail format."));
            }
            break;
          }
          if (blob.size <= maxBytes) {
            result = { blob: blob, width: canvas.width, height: canvas.height };
            break;
          }
        }
      }
      if (!result) {
        var nextScale = Math.min(0.84, Math.sqrt(maxBytes / Math.max(maxBytes + 1, Number(blob && blob.size) || maxBytes * 2)) * 0.94);
        var nextWidth = Math.max(1, Math.floor(width * nextScale));
        var nextHeight = Math.max(1, Math.floor(height * nextScale));
        var nextLongEdge = Math.max(nextWidth, nextHeight);
        if (nextLongEdge < CART_THUMBNAIL_MIN_EDGE) {
          var minimumScale = CART_THUMBNAIL_MIN_EDGE / Math.max(1, nextLongEdge);
          nextWidth = Math.max(1, Math.round(nextWidth * minimumScale));
          nextHeight = Math.max(1, Math.round(nextHeight * minimumScale));
        }
        width = nextWidth;
        height = nextHeight;
      }
    }
    if (!result || result.blob.size > maxBytes) {
      throw dyofError(dyofT("cover.capture.size_limit", "The exact cart thumbnail could not be reduced to the safe upload limit."));
    }
    return {
      kind: String(kind || "thumbnail"),
      contentType: result.blob.type || "image/png",
      base64: await cartThumbnailBase64(result.blob, options),
      width: result.width,
      height: result.height
    };
  }

  function fileKind(file, bytes) {
    var isPng = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
    var isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
    var isPdf = bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
    if (isPng) { return "png"; }
    if (isJpeg) { return "jpeg"; }
    if (isPdf && file.type === "application/pdf") { return "pdf"; }
    return "";
  }

  function assetId() {
    if (root.crypto && root.crypto.randomUUID) {
      return "asset-" + root.crypto.randomUUID();
    }
    return "asset-" + Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  // The same shapes the private upload service issues and accepts.
  var UPLOAD_SCOPE_ID_PATTERN = /^dyofscope_[a-f0-9]{32}$/;
  var UPLOAD_RECEIPT_PATTERN = /^dyofreceipt_[A-Za-z0-9_-]{32,160}$/;
  var UPLOAD_OWNER_PROOF_PATTERN = /^dyofowner_[A-Za-z0-9_-]{32,160}$/;
  var UPLOAD_ASSET_ID_PATTERN = /^dyofasset_[a-f0-9]{32}$/;
  var UPLOAD_CLAIM_STORAGE_KEY = "dyof:upload-claims:v1";
  var UPLOAD_CLAIM_STORAGE_VERSION = 1;
  // Only a claim clearly past its session's expiry is dropped. Near the limit
  // the server decides, because this browser's clock may be wrong.
  var UPLOAD_CLAIM_EXPIRY_GRACE_MS = 60 * 1000;
  // The longest upload session the service issues
  // (DYOF_UPLOAD_SESSION_LIFETIME_MS). Time measured on this device since the
  // upload must pass it before a claim is dropped, whatever its clock says.
  var UPLOAD_SESSION_MAX_LIFETIME_MS = 12 * 60 * 60 * 1000;

  function isObjectUrl(url) {
    return /^blob:/i.test(String(url || ""));
  }

  function hasStorageFields(record) {
    return Boolean(record && record.storageKey && record.sha256 && record.sizeBytes);
  }

  function uploadClaimStorage() {
    // Reading the property itself throws where site data is blocked.
    try {
      return root && root.sessionStorage ? root.sessionStorage : null;
    } catch (error) {
      return null;
    }
  }

  function validUploadClaim(claim) {
    return Boolean(
      claim
      && typeof claim === "object"
      && UPLOAD_SCOPE_ID_PATTERN.test(String(claim.uploadScopeId || ""))
      && UPLOAD_RECEIPT_PATTERN.test(String(claim.receipt || ""))
      && UPLOAD_OWNER_PROOF_PATTERN.test(String(claim.ownerProof || ""))
    );
  }

  // An unknown expiry is null (the server decides); a malformed one is invalid.
  function uploadClaimExpiry(value) {
    if (value === undefined || value === null) { return null; }
    return typeof value === "string" && Number.isFinite(Date.parse(value)) ? value : undefined;
  }

  // When this device kept the claim, on its own clock (milliseconds). Unknown
  // is null; anything else is invalid.
  function uploadClaimRememberedAt(value) {
    if (value === undefined || value === null) { return null; }
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
  }

  // expiresAt is the server's time and this device's clock may be hours off
  // (set by hand, or wrong). A fast clock alone must never drop a live claim:
  // the save would then fail on every upload. So a claim is dead only when its
  // expiry has clearly passed on this clock and this device has also counted
  // more time since the upload than any session lasts. Otherwise the claim is
  // sent and the server decides.
  function uploadClaimExpired(entry, now) {
    return Boolean(entry && entry.expiresAt)
      && typeof entry.rememberedAt === "number"
      && Date.parse(entry.expiresAt) + UPLOAD_CLAIM_EXPIRY_GRACE_MS < now
      && now - entry.rememberedAt > UPLOAD_SESSION_MAX_LIFETIME_MS + UPLOAD_CLAIM_EXPIRY_GRACE_MS;
  }

  function sameUploadClaim(left, right) {
    return Boolean(left && right)
      && left.uploadScopeId === right.uploadScopeId
      && left.receipt === right.receipt
      && left.ownerProof === right.ownerProof;
  }

  // Returns server asset ID ->
  // { uploadScopeId, receipt, ownerProof, expiresAt, rememberedAt }.
  // Anything unreadable or malformed is ignored.
  function readStoredUploadClaims() {
    var claims = new Map();
    var storage = uploadClaimStorage();
    var stored;
    if (!storage) { return claims; }
    try {
      stored = JSON.parse(storage.getItem(UPLOAD_CLAIM_STORAGE_KEY) || "null");
    } catch (error) {
      return claims;
    }
    if (
      !stored
      || typeof stored !== "object"
      || stored.version !== UPLOAD_CLAIM_STORAGE_VERSION
      || !stored.claims
      || typeof stored.claims !== "object"
      || Array.isArray(stored.claims)
    ) {
      return claims;
    }
    Object.keys(stored.claims).forEach(function(id) {
      var claim = stored.claims[id];
      var expiresAt = claim && typeof claim === "object" ? uploadClaimExpiry(claim.expiresAt) : undefined;
      var rememberedAt = claim && typeof claim === "object"
        ? uploadClaimRememberedAt(claim.rememberedAt)
        : undefined;
      if (
        !UPLOAD_ASSET_ID_PATTERN.test(id)
        || !validUploadClaim(claim)
        || expiresAt === undefined
        || rememberedAt === undefined
      ) {
        return;
      }
      claims.set(id, {
        uploadScopeId: claim.uploadScopeId,
        receipt: claim.receipt,
        ownerProof: claim.ownerProof,
        expiresAt: expiresAt,
        rememberedAt: rememberedAt
      });
    });
    return claims;
  }

  function writeStoredUploadClaims(claims) {
    var storage = uploadClaimStorage();
    if (!storage) { return false; }
    try {
      if (!claims.size) {
        storage.removeItem(UPLOAD_CLAIM_STORAGE_KEY);
        return true;
      }
      var serialized = {};
      claims.forEach(function(claim, id) {
        serialized[id] = {
          uploadScopeId: claim.uploadScopeId,
          receipt: claim.receipt,
          ownerProof: claim.ownerProof,
          expiresAt: claim.expiresAt || null,
          rememberedAt: typeof claim.rememberedAt === "number" ? claim.rememberedAt : null
        };
      });
      storage.setItem(UPLOAD_CLAIM_STORAGE_KEY, JSON.stringify({
        version: UPLOAD_CLAIM_STORAGE_VERSION,
        claims: serialized
      }));
      return true;
    } catch (error) {
      // Quota or private mode: the claim stays in this page's memory only.
      return false;
    }
  }

  class AssetStore {
    constructor() {
      this.memory = new Map();
      this.dbPromise = null;
      // Object URLs a restore handed to this page and not yet released. Any
      // other object URL is dead (revoked, or from an earlier page).
      this.objectUrls = new Set();
      // Set while photos this browser keeps no copy of are fetched again;
      // get() answers for such a photo once that has finished.
      this.previewRestore = null;
    }

    usablePreview(url) {
      return Boolean(url) && (!isObjectUrl(url) || this.objectUrls.has(url));
    }

    peek(id) {
      if (!id || !this.memory.has(id)) {
        return null;
      }
      return this.memory.get(id) || null;
    }

    open() {
      if (!root.indexedDB) {
        return Promise.resolve(null);
      }
      if (this.dbPromise) {
        return this.dbPromise;
      }
      this.dbPromise = new Promise(function(resolve) {
        var request = root.indexedDB.open("dyof-cover-assets", 1);
        request.onupgradeneeded = function() {
          if (!request.result.objectStoreNames.contains("assets")) {
            request.result.createObjectStore("assets", { keyPath: "id" });
          }
        };
        request.onsuccess = function() { resolve(request.result); };
        request.onerror = function() { resolve(null); };
      });
      return this.dbPromise;
    }

    async put(record, options) {
      // Persist an explicit safe projection. In particular, upload receipts and
      // owner proofs cannot enter memory-backed AssetStore or IndexedDB even if
      // a future caller accidentally adds them to its input object. They are
      // bearer secrets kept per tab in sessionStorage instead (see Controller
      // privateUploadClaims); a record here outlives the tab, is shared by
      // every tab and ends up in the saved-design manifest.
      var safeRecord = {
        id: record.id,
        name: record.name,
        mime: record.mime,
        size: record.size,
        previewUrl: record.previewUrl,
        width: record.width,
        height: record.height,
        remoteUrl: record.remoteUrl || null,
        createdAt: record.createdAt
      };
      if (record.storageKey && record.sha256 && record.sizeBytes) {
        safeRecord.storageKey = record.storageKey;
        safeRecord.sha256 = record.sha256;
        safeRecord.sizeBytes = record.sizeBytes;
        if (record.versionId) { safeRecord.versionId = record.versionId; }
      }
      this.memory.set(safeRecord.id, safeRecord);
      var db = await this.open();
      if (!db) { return safeRecord; }
      // An object URL dies with this document, so memory may use one but
      // IndexedDB keeps a data: preview: the one supplied, else the one it
      // already holds.
      var durableRecord = Object.assign({}, safeRecord);
      var durablePreviewUrl = options && options.durablePreviewUrl
        ? options.durablePreviewUrl
        : safeRecord.previewUrl;
      var keepStoredPreview = !durablePreviewUrl || isObjectUrl(durablePreviewUrl);
      if (!keepStoredPreview) { durableRecord.previewUrl = durablePreviewUrl; }
      await new Promise(function(resolve) {
        var objectStore = db.transaction("assets", "readwrite").objectStore("assets");
        var write = function(stored) {
          if (keepStoredPreview) {
            durableRecord.previewUrl = stored && stored.previewUrl && !isObjectUrl(stored.previewUrl)
              ? stored.previewUrl
              : undefined;
          }
          try {
            var request = objectStore.put(durableRecord);
            request.onsuccess = function() { resolve(); };
            request.onerror = function() { resolve(); };
          } catch (error) {
            resolve();
          }
        };
        if (!keepStoredPreview) {
          write(null);
          return;
        }
        var read = objectStore.get(safeRecord.id);
        read.onsuccess = function() { write(read.result || null); };
        read.onerror = function() { write(null); };
      });
      return safeRecord;
    }

    async get(id) {
      var record = await this.stored(id);
      var restoring = this.previewRestore;
      if (restoring && !(record && this.usablePreview(record.previewUrl))) {
        // Renders see the photo once it is back. Production metadata uses
        // stored() so a preview download never delays saving or purchasing.
        await restoring;
        record = this.memory.get(id) || record;
      }
      return record;
    }

    // This page's record, else IndexedDB's, without waiting for a restore.
    async stored(id) {
      if (this.memory.has(id)) {
        return this.memory.get(id);
      }
      var db = await this.open();
      if (!db) { return null; }
      var record = await new Promise(function(resolve) {
        var request = db.transaction("assets", "readonly").objectStore("assets").get(id);
        request.onsuccess = function() { resolve(request.result || null); };
        request.onerror = function() { resolve(null); };
      });
      if (record && isObjectUrl(record.previewUrl)) {
        // An earlier version stored restore object URLs, which died with
        // their page. Showing or capturing one only fails.
        record = Object.assign({}, record, { previewUrl: undefined });
      }
      if (record) { this.memory.set(id, record); }
      return record;
    }

    // Forgets the object URLs a caller revoked, and this page's records that
    // used them, so IndexedDB's copy (if any) is read again.
    release(urls) {
      var released = new Set((urls || []).filter(isObjectUrl));
      if (!released.size) { return; }
      released.forEach(function(url) { this.objectUrls.delete(url); }, this);
      Array.from(this.memory.keys()).forEach(function(id) {
        var record = this.memory.get(id);
        if (record && released.has(record.previewUrl)) { this.memory.delete(id); }
      }, this);
    }

    async manifest(ids) {
      var records = await Promise.all((ids || []).map(this.stored.bind(this)));
      return records.filter(Boolean).map(function(record) {
        var asset = {
          id: record.id,
          name: record.name,
          mime: record.mime,
          width: record.width,
          height: record.height,
          remoteUrl: record.remoteUrl || null
        };
        if (
          record.storageKey
          && record.sha256
          && Number.isSafeInteger(record.sizeBytes)
          && record.sizeBytes > 0
        ) {
          asset.storageKey = record.storageKey;
          asset.sha256 = record.sha256;
          asset.sizeBytes = record.sizeBytes;
          if (record.versionId) { asset.versionId = record.versionId; }
        }
        return asset;
      });
    }
  }

  class Controller {
    constructor(host, store, options) {
      this.host = host;
      this.store = store;
      this.options = options || {};
      this.contract = root.DYOFCoverContract;
      this.merchandising = readJson(host, "[data-dyof-cover-merchandising]", {});
      this.colourSettings = readJson(host, "[data-dyof-cover-colour-settings]", {});
      this.printPreviewManifest = normalizePrintPreviewManifest(
        readJson(host, "[data-dyof-cover-print-previews]", null)
      );
      this.catalogue = normalizeCatalogue(
        readJson(host, "[data-dyof-cover-catalog]", []),
        this.merchandising,
        this.printPreviewManifest
      );
      this.shells = readJson(host, "[data-dyof-cover-shells]", {});
      this.fonts = readJson(host, "[data-dyof-cover-fonts]", []);
      this.elasticTextures = {};
      Object.keys(ELASTIC_TEXTURES).forEach(function(id) {
        var texture = ELASTIC_TEXTURES[id];
        this.elasticTextures[id] = host.dataset[texture.datasetKey] || "";
      }, this);
      this.elasticBands = {};
      Object.keys(ELASTIC_BANDS).forEach(function(id) {
        var band = ELASTIC_BANDS[id];
        this.elasticBands[id] = host.dataset[band.datasetKey] || "";
      }, this);
      this.fontLoadPromises = new Map();
      this.fontLoadSettled = new Set();
      this.fontPreviewRefreshSignature = null;
      this.destroyed = false;
      this.coverThumbs = readJson(host, "[data-dyof-cover-thumbs]", []);
      this.didInitializeBackground = false;
      this.didChooseSize = Boolean(
        store
        && typeof store.getState === "function"
        && store.getState()
        && store.getState().journey
        && store.getState().journey.productType === "planner"
      );
      this.didChooseSource = false;
      this.didChooseDesign = false;
      this.openSelectionMenu = null;
      this.coverGridScrollPositions = new Map();
      this.coverSelectionStructureSignature = "";
      this.coverSelectionHiddenKey = null;
      this.coverTooltipDisclosure = null;
      this.coverTooltipHover = null;
      this.coverTooltipFocus = null;
      this.coverTooltipFocusSuppressed = null;
      this.sizeTooltipOpen = false;
      this.uploadTooltipOpen = false;
      this.assets = new AssetStore();
      this.previewToken = 0;
      this.uploading = new Set();
      this.fileProcessing = false;
      // Upload receipts and owner proofs claim an upload for the next save.
      // They are held by this controller and, per tab, in sessionStorage
      // ("dyof:upload-claims:v1") with their session expiry, so they survive
      // a reload (iPhone Safari reloads background tabs) but die with the tab
      // and are never shared with another tab. They are never put in
      // AssetStore, IndexedDB, editor state, localStorage, history.state,
      // URLs, events or logs, and are removed once a save has claimed them.
      // privateUploadClaimTimes holds each one's { expiresAt, rememberedAt }.
      this.privateUploadClaims = new Map();
      this.privateUploadClaimTimes = new Map();
      this.moveableTarget = null;
      this.interaction = null;
      this.pointerDrag = null;
      this.rangePointer = null;
      this.rangeDraft = null;
      this.rangeLastCommit = null;
      this.rangePreviewFrame = 0;
      this.customColourDraft = null;
      this.customColourCommitSide = null;
      this.automaticPaletteColours = new Map();
      this.expandedTextBlocks = { front: null, back: null };
      this.pendingTextBlockExpansionSide = null;
      this.cropperEntries = [];
      this.elements = {
        selection: host.querySelector("[data-dyof-cover-selection]"),
        front: host.querySelector("[data-dyof-cover-side='front']"),
        back: host.querySelector("[data-dyof-cover-side='back']")
      };
      this.boundPointerDown = this.onPointerDown.bind(this);
      this.boundPointerMove = this.onPointerMove.bind(this);
      this.boundPointerEnd = this.onPointerEnd.bind(this);
      this.boundBlur = this.onBlur.bind(this);
      this.boundDragOver = this.onDragOver.bind(this);
      this.boundDragLeave = this.onDragLeave.bind(this);
      this.boundDrop = this.onDrop.bind(this);
      this.boundSelectionScroll = this.onSelectionScroll.bind(this);
      this.boundTooltipPointerOver = this.onTooltipPointerOver.bind(this);
      this.boundTooltipPointerOut = this.onTooltipPointerOut.bind(this);
      this.boundTooltipFocusIn = this.onTooltipFocusIn.bind(this);
      this.boundTooltipFocusOut = this.onTooltipFocusOut.bind(this);
      this.boundDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.boundDocumentPointerRelease = this.onDocumentPointerRelease.bind(this);
      this.pointerPressed = false;
      this.pendingResolutionReveal = null;
      this.shownResolutionWarnings = Object.create(null);
      this.uploadRefusal = null;
      this.boundKeyDown = this.onKeyDown.bind(this);
      this.document = host.ownerDocument || (root && root.document) || null;
      this.host.addEventListener("pointerdown", this.boundPointerDown);
      this.host.addEventListener("pointermove", this.boundPointerMove);
      this.host.addEventListener("pointerup", this.boundPointerEnd);
      this.host.addEventListener("pointercancel", this.boundPointerEnd);
      this.host.addEventListener("lostpointercapture", this.boundPointerEnd);
      this.host.addEventListener("blur", this.boundBlur, true);
      this.host.addEventListener("dragenter", this.boundDragOver);
      this.host.addEventListener("dragover", this.boundDragOver);
      this.host.addEventListener("dragleave", this.boundDragLeave);
      this.host.addEventListener("drop", this.boundDrop);
      this.host.addEventListener("scroll", this.boundSelectionScroll, true);
      this.host.addEventListener("pointerover", this.boundTooltipPointerOver);
      this.host.addEventListener("pointerout", this.boundTooltipPointerOut);
      this.host.addEventListener("focusin", this.boundTooltipFocusIn);
      this.host.addEventListener("focusout", this.boundTooltipFocusOut);
      this.host.addEventListener("keydown", this.boundKeyDown);
      if (this.document && this.document.addEventListener) {
        this.document.addEventListener("pointerdown", this.boundDocumentPointerDown, true);
        this.document.addEventListener("pointerup", this.boundDocumentPointerRelease, true);
        this.document.addEventListener("pointercancel", this.boundDocumentPointerRelease, true);
      }
      if (root.pdfjsLib && host.dataset.pdfWorker) {
        root.pdfjsLib.GlobalWorkerOptions.workerSrc = host.dataset.pdfWorker;
      }
    }

    destroy() {
      this.destroyed = true;
      this.setSafeGuideVisible(false);
      this.rememberCoverGridScroll(
        this.elements.selection && this.elements.selection.querySelector
          ? this.elements.selection.querySelector(".dyof__cover-grid")
          : null
      );
      this.host.removeEventListener("pointerdown", this.boundPointerDown);
      this.host.removeEventListener("pointermove", this.boundPointerMove);
      this.host.removeEventListener("pointerup", this.boundPointerEnd);
      this.host.removeEventListener("pointercancel", this.boundPointerEnd);
      this.host.removeEventListener("lostpointercapture", this.boundPointerEnd);
      this.host.removeEventListener("blur", this.boundBlur, true);
      this.host.removeEventListener("dragenter", this.boundDragOver);
      this.host.removeEventListener("dragover", this.boundDragOver);
      this.host.removeEventListener("dragleave", this.boundDragLeave);
      this.host.removeEventListener("drop", this.boundDrop);
      this.host.removeEventListener("scroll", this.boundSelectionScroll, true);
      this.host.removeEventListener("pointerover", this.boundTooltipPointerOver);
      this.host.removeEventListener("pointerout", this.boundTooltipPointerOut);
      this.host.removeEventListener("focusin", this.boundTooltipFocusIn);
      this.host.removeEventListener("focusout", this.boundTooltipFocusOut);
      this.host.removeEventListener("keydown", this.boundKeyDown);
      if (this.document && this.document.removeEventListener) {
        this.document.removeEventListener("pointerdown", this.boundDocumentPointerDown, true);
        this.document.removeEventListener("pointerup", this.boundDocumentPointerRelease, true);
        this.document.removeEventListener("pointercancel", this.boundDocumentPointerRelease, true);
      }
      this.destroyTransformOverlay();
      this.destroyCroppers();
      if (this.rangePreviewFrame) {
        root.cancelAnimationFrame(this.rangePreviewFrame);
        this.rangePreviewFrame = 0;
      }
      this.rangePointer = null;
      this.rangeDraft = null;
      this.rangeLastCommit = null;
      this.customColourDraft = null;
      this.customColourCommitSide = null;
      this.fontLoadPromises.clear();
      this.fontLoadSettled.clear();
      this.fontPreviewRefreshSignature = null;
      this.clearTextMetrics();
      // Only this controller's copy: the tab's stored claims must still be
      // there for the next page load.
      if (this.privateUploadClaims) { this.privateUploadClaims.clear(); }
      if (this.privateUploadClaimTimes) { this.privateUploadClaimTimes.clear(); }
      if (this.coverGridScrollPositions) { this.coverGridScrollPositions.clear(); }
      this.coverSelectionStructureSignature = "";
      this.coverSelectionHiddenKey = null;
      if (this.host && this.host.removeAttribute) {
        this.host.removeAttribute("data-cover-uploading");
        this.host.removeAttribute("aria-busy");
      }
      this.dismissCoverTooltips();
    }

    setStatus(message, status) {
      if (!this.options.setStatus) { return; }
      // On phones the preview's Retry cannot redo a photo choice: every editor
      // status asks the customer to act (choose, crop or upload again)
      // instead. Tablet and desktop keep their Retry unchanged.
      if (isPhoneLayout()) {
        this.options.setStatus(message, status || "idle", { retry: false });
      } else {
        this.options.setStatus(message, status || "idle");
      }
    }

    invalidatePreview() {
      this.previewToken += 1;
      this.setSafeGuideVisible(false);
      this.destroyTransformOverlay();
    }

    setSafeGuideVisible(visible) {
      if (this.host && this.host.classList) {
        this.host.classList.toggle("is-cover-object-moving", Boolean(visible));
      }
    }

    previewRequestKey(state, sideName) {
      var side = sideName || (state && state.screen === "back-cover" ? "back" : "front");
      return JSON.stringify({
        screen: state && state.screen,
        journey: state && state.journey,
        selection: state && state.selection,
        background: state && state.cover && state.cover.background,
        elastic: state && state.cover && state.cover.elastic,
        side: state && state.cover && state.cover[side]
      });
    }

    isCurrentPreviewRequest(token, state, elements, requestKey) {
      var screen = state && state.screen;
      var expectedMode = screen === "cover-selection"
        ? "sizes"
        : screen === "front-cover" || screen === "back-cover"
          ? "closed-cover"
          : "";
      var current = this.store && this.store.getState
        ? this.store.getState()
        : state;
      var previewStage = elements && elements.previewStage;

      if (
        this.destroyed
        || token !== this.previewToken
        || !expectedMode
        || !current
        || current.screen !== screen
        || this.previewRequestKey(current, screen === "back-cover" ? "back" : "front")
          !== (requestKey || this.previewRequestKey(state, screen === "back-cover" ? "back" : "front"))
      ) {
        return false;
      }
      if (!previewStage || !previewStage.dataset) {
        return true;
      }
      return previewStage.dataset.screen === screen
        && previewStage.dataset.previewMode === expectedMode;
    }

    dispatch(type, payload) {
      this.store.dispatch({ type: type, payload: payload });
    }

    hasExplicitSizeChoice() {
      var state = this.store && typeof this.store.getState === "function"
        ? this.store.getState()
        : null;
      return Boolean(
        this.didChooseSize
        || (state && state.journey && state.journey.productType === "planner")
      );
    }

    getExplicitChoices() {
      return {
        sizeChosen: this.hasExplicitSizeChoice(),
        sourceChosen: Boolean(this.didChooseSource),
        designChosen: Boolean(this.didChooseDesign)
      };
    }

    restoreExplicitChoices(choicesInput) {
      var choices = choicesInput && typeof choicesInput === "object"
        ? choicesInput
        : {};
      var state = this.store && typeof this.store.getState === "function"
        ? this.store.getState()
        : null;
      this.didChooseSize = Boolean(
        choices.sizeChosen
        || (state && state.journey && state.journey.productType === "planner")
      );
      this.didChooseSource = Boolean(choices.sourceChosen);
      this.didChooseDesign = Boolean(choices.designChosen);
      this.didInitializeBackground = this.didChooseSource || this.didChooseDesign;
      this.openSelectionMenu = null;
      this.dismissCoverTooltips();
    }

    markRestoredChoices() {
      this.didChooseSize = true;
      this.didChooseSource = true;
      this.didChooseDesign = true;
    }

    resetExplicitChoices() {
      var state = this.store && typeof this.store.getState === "function"
        ? this.store.getState()
        : null;
      this.didChooseSize = Boolean(
        state && state.journey && state.journey.productType === "planner"
      );
      this.didChooseSource = false;
      this.didChooseDesign = false;
      this.didInitializeBackground = false;
      this.openSelectionMenu = null;
      if (this.coverGridScrollPositions) { this.coverGridScrollPositions.clear(); }
      this.coverSelectionStructureSignature = "";
      this.coverSelectionHiddenKey = null;
      this.dismissCoverTooltips();
    }

    fontOption(fontId) {
      return FONT_OPTIONS.filter(function(option) {
        return option.id === fontId;
      })[0] || FONT_OPTIONS[0];
    }

    configuredFont(fontId) {
      if (Object.prototype.hasOwnProperty.call(BUNDLED_COVER_FONTS, fontId)) {
        var bundled = BUNDLED_COVER_FONTS[fontId];
        var dataset = this.host && this.host.dataset || {};
        return {
          semanticId: fontId,
          title: this.fontOption(fontId).label,
          family: bundled.family,
          webUrl: dataset[bundled.datasetKey] || ""
        };
      }
      if (fontId === "lettering" && this.storeFontProfile() === "latin-ce") {
        var profile = this.host && this.host.storeProfile;
        return {
          semanticId: "lettering",
          title: "Lettering",
          family: "DYOF Meritocracy PDF",
          webUrl: profile && profile.coverLetteringFontUrl || ""
        };
      }
      var option = this.fontOption(fontId);
      var fonts = Array.isArray(this.fonts) ? this.fonts : [];
      var semanticTitle = normalizeFontToken(option.label);
      var semanticId = normalizeFontToken(option.id);
      var match = fonts.filter(function(font) {
        return normalizeFontToken(font && font.title) === semanticTitle
          || normalizeFontToken(font && font.semanticId) === semanticId;
      })[0];
      return match || fonts[FONT_OPTIONS.indexOf(option)] || null;
    }

    configuredFontFamily(fontId) {
      if (Object.prototype.hasOwnProperty.call(BUNDLED_COVER_FONTS, fontId)) {
        return BUNDLED_COVER_FONTS[fontId].family;
      }
      if (fontId === "lettering" && this.storeFontProfile() === "latin-ce") {
        return "DYOF Meritocracy PDF";
      }
      var font = this.configuredFont(fontId);
      return String(font && (font.family || font.id) || "").trim();
    }

    fontFamily(fontId) {
      var option = this.fontOption(fontId);
      if (Object.prototype.hasOwnProperty.call(BUNDLED_COVER_FONTS, option.id)
        || (option.id === "lettering" && this.storeFontProfile() === "latin-ce")) {
        return FONT_FALLBACK_FAMILIES[option.id];
      }
      var configuredFamily = quoteFontFamily(this.configuredFontFamily(option.id));
      var fallback = FONT_FALLBACK_FAMILIES[option.id] || FONT_FALLBACK_FAMILIES.minimal;
      return configuredFamily ? configuredFamily + ", " + fallback : fallback;
    }

    storeFontProfile() {
      var printable = root && root.DYOFPrintableText;
      return printable && typeof printable.storeFontProfile === "function"
        ? printable.storeFontProfile(this.host && this.host.storeProfile)
        : "latin-western";
    }

    textBaselineMiddleEm(fontId) {
      return fontId === "lettering" && this.storeFontProfile() === "latin-ce"
        ? 0.1335 // Meritocracy's drawn x-height: 267 / (2 * 1000).
        : TEXT_BASELINE_MIDDLE_EM[this.fontOption(fontId).id];
    }

    fontPreviewSignature(state, sideName) {
      if (!state || !state.cover || !state.cover[sideName]) { return ""; }
      var lines = [];
      state.cover[sideName].objects.forEach(function(object) {
        if (object.type !== "text") { return; }
        object.lines.forEach(function(line, index) {
          if (!String(line.text || "").trim()) { return; }
          lines.push([
            object.id,
            index,
            line.text,
            line.fontId,
            Number(line.fontSize) || 0
          ]);
        });
      });
      return JSON.stringify([state.screen, sideName, lines]);
    }

    ensureFontLoaded(fontId, sample) {
      var fontDocument = root && root.document;
      var fontSet = fontDocument && fontDocument.fonts;
      var family = this.configuredFontFamily(fontId);
      if (!fontSet || typeof fontSet.load !== "function" || !family) { return null; }
      var descriptor = "16px " + quoteFontFamily(family);
      // Which faces a text needs depends only on its characters, so each
      // character is checked or loaded once per family. (Checking the whole
      // line cost about a millisecond per keystroke.) An empty sample keeps
      // its own entry.
      var settled = this.fontLoadSettled;
      var characters = String(sample || "") ? Array.from(String(sample)) : [""];
      var pending = characters.filter(function(character, index) {
        return characters.indexOf(character) === index && !settled.has(family + "\u0000" + character);
      });
      if (!pending.length) { return null; }
      var text = pending.join("");
      var settle = function() {
        pending.forEach(function(character) { settled.add(family + "\u0000" + character); });
      };
      if (typeof fontSet.check === "function" && fontSet.check(descriptor, text)) {
        settle();
        return null;
      }
      var key = family + "\u0000" + text;
      if (this.fontLoadPromises.has(key)) {
        return this.fontLoadPromises.get(key);
      }
      var promise;
      try {
        promise = Promise.resolve(fontSet.load(descriptor, text));
      } catch (error) {
        settle();
        return null;
      }
      promise = promise.then(function() {
        settle();
        this.fontLoadPromises.delete(key);
        return true;
      }.bind(this), function() {
        settle();
        this.fontLoadPromises.delete(key);
        return false;
      }.bind(this));
      this.fontLoadPromises.set(key, promise);
      return promise;
    }

    scheduleFontPreviewRefresh(state, sideName, elements) {
      if (!state || !state.cover || !state.cover[sideName]) { return; }
      var signature = this.fontPreviewSignature(state, sideName);
      var samples = new Map();
      state.cover[sideName].objects.forEach(function(object) {
        if (object.type !== "text") { return; }
        object.lines.forEach(function(line) {
          var text = String(line.text || "");
          if (text.trim() && !samples.has(line.fontId)) {
            samples.set(line.fontId, text);
          }
        });
      });
      var pending = [];
      samples.forEach(function(sample, fontId) {
        var request = this.ensureFontLoaded(fontId, sample);
        if (request) { pending.push(request); }
      }, this);
      if (!pending.length) { return; }
      Promise.all(pending).then(function(results) {
        if (this.destroyed || !results.some(Boolean)) { return; }
        var current = this.store.getState();
        var currentSide = current.screen === "back-cover" ? "back" : "front";
        if (currentSide !== sideName || this.fontPreviewSignature(current, sideName) !== signature) { return; }
        if (this.fontPreviewRefreshSignature === signature) { return; }
        this.fontPreviewRefreshSignature = signature;
        // The loaded font replaces any fallback the kept metrics saw.
        this.clearTextMetrics();
        this.renderPreview(current, elements);
      }.bind(this));
    }

    catalogueKey(state) {
      if (state && state.journey && state.journey.productType === "planner") {
        return "plannerA5";
      }
      return resolvedCoverSize(state) === "personal" ? "personal" : "clipbookA5";
    }

    cataloguePrintAsset(item, state) {
      return item && item.printAsset
        ? item.printAsset[this.catalogueKey(state)] || null
        : null;
    }

    cataloguePreviewUrl(item, state, sideName) {
      var printAsset = this.cataloguePrintAsset(item, state);
      var preview = printAsset && printAsset.preview
        ? printAsset.preview
        : item && item.preview ? item.preview[this.catalogueKey(state)] : null;
      if (typeof preview === "string") { return preview; }
      if (!preview) { return ""; }
      return preview[sideName || "front"] || preview.front || preview.back || "";
    }

    catalogueThumbnailUrl(item, state) {
      var url = this.cataloguePreviewUrl(item, state, "front");
      // Every WP4 preview is a Shopify image derivative. Request a gallery-
      // sized rendition instead of decoding the 567–825 px scene face in each
      // of the up-to-145 lazy cards.
      return String(url || "").replace(
        /_\d+x\d+_crop_(left|right)(?=\.[a-z0-9]+(?:\?|$))/i,
        "_320x414_crop_$1"
      );
    }

    catalogueThumbnailFocal(item, state) {
      return resolveThumbnailFocal(
        item && item.thumbnailFocal,
        this.catalogueKey(state)
      );
    }

    availableCatalogue(state, filters) {
      var key = this.catalogueKey(state);
      var theme = filters && own(filters, "theme") ? filters.theme : state.cover.filters.theme;
      var colour = filters && own(filters, "colour") ? filters.colour : state.cover.filters.colour;
      return this.catalogue.filter(function(item) {
        var printAsset = item.printAsset && item.printAsset[key];
        var preview = printAsset && printAsset.preview || item.preview && item.preview[key];
        var productionRef = printAsset && printAsset.productionRef || item.production && item.production[key];
        var available = preview && (typeof preview === "string" || (preview.front && preview.back)) && productionRef;
        return available
          && themeFilterMatches(theme, item.searchFilter)
          && colourFilterMatches(colour, item.colourTags);
      });
    }

    coverGridKey(state, items) {
      var cover = state && state.cover || {};
      var filters = cover.filters || {};
      return JSON.stringify([
        this.catalogueKey(state),
        cover.source || "",
        filters.theme || "all",
        filters.colour || "all",
        (items || []).map(function(item) { return item.id; })
      ]);
    }

    coverSelectionStructureKey(state, items) {
      var background = state.cover && state.cover.background;
      var uploadIdentity = background && background.source === "upload"
        ? [background.assetId || "", background.title || ""]
        : null;
      return JSON.stringify([
        this.coverGridKey(state, items),
        Boolean(this.didChooseSize),
        Boolean(this.didChooseSource),
        state.cover && state.cover.source || "",
        this.openSelectionMenu || "",
        uploadIdentity
      ]);
    }

    rememberCoverGridScroll(grid) {
      if (!grid || !grid.dataset) { return; }
      var key = String(grid.dataset.coverGridKey || "");
      var scrollTop = Number(grid.scrollTop);
      if (!key || !Number.isFinite(scrollTop)) { return; }
      this.coverGridScrollPositions.set(key, Math.max(0, scrollTop));
      while (this.coverGridScrollPositions.size > 12) {
        this.coverGridScrollPositions.delete(this.coverGridScrollPositions.keys().next().value);
      }
    }

    onSelectionScroll(event) {
      var grid = event && event.target && event.target.matches
        && event.target.matches(".dyof__cover-grid")
        ? event.target
        : null;
      if (grid) { this.rememberCoverGridScroll(grid); }
    }

    tooltipNameFromTarget(target) {
      if (!target || !target.closest) { return ""; }
      var region = target.closest("[data-cover-tooltip-trigger],[data-cover-tooltip]");
      if (!region || !this.host.contains(region)) { return ""; }
      return String(region.dataset.coverTooltipTrigger || region.dataset.coverTooltip || "");
    }

    tooltipIsOpen(name) {
      if (this.coverTooltipDisclosure) { return this.coverTooltipDisclosure === name; }
      if (this.coverTooltipHover) { return this.coverTooltipHover === name; }
      return this.coverTooltipFocus === name && this.coverTooltipFocusSuppressed !== name;
    }

    syncCoverTooltips() {
      this.sizeTooltipOpen = this.tooltipIsOpen("size");
      this.uploadTooltipOpen = this.tooltipIsOpen("upload");
      if (!this.host || typeof this.host.querySelector !== "function") { return; }
      ["size", "upload"].forEach(function(name) {
        var open = this.tooltipIsOpen(name);
        var trigger = this.host.querySelector('[data-cover-tooltip-trigger="' + name + '"]');
        var tooltip = this.host.querySelector('[data-cover-tooltip="' + name + '"]');
        if (trigger) { trigger.setAttribute("aria-expanded", String(open)); }
        if (tooltip) {
          tooltip.hidden = !open;
          tooltip.setAttribute("aria-hidden", String(!open));
          tooltip.dataset.open = String(open);
        }
      }, this);
    }

    setSizeTooltipOpen(open) {
      this.coverTooltipDisclosure = open ? "size" : null;
      if (!open) {
        this.coverTooltipHover = this.coverTooltipHover === "size" ? null : this.coverTooltipHover;
        this.coverTooltipFocus = this.coverTooltipFocus === "size" ? null : this.coverTooltipFocus;
        this.coverTooltipFocusSuppressed = "size";
      }
      this.syncCoverTooltips();
      return this.sizeTooltipOpen;
    }

    dismissSizeTooltip() {
      return this.setSizeTooltipOpen(false);
    }

    dismissCoverTooltips() {
      this.coverTooltipDisclosure = null;
      this.coverTooltipHover = null;
      this.coverTooltipFocus = null;
      this.coverTooltipFocusSuppressed = null;
      this.sizeTooltipOpen = false;
      this.uploadTooltipOpen = false;
      if (this.host && this.host.querySelector) { this.syncCoverTooltips(); }
    }

    onTooltipPointerOver(event) {
      if (event && event.pointerType && event.pointerType !== "mouse") { return; }
      var name = this.tooltipNameFromTarget(event && event.target);
      if (!name) { return; }
      this.coverTooltipHover = name;
      this.syncCoverTooltips();
    }

    onTooltipPointerOut(event) {
      var name = this.tooltipNameFromTarget(event && event.target);
      if (!name || this.tooltipNameFromTarget(event && event.relatedTarget) === name) { return; }
      if (this.coverTooltipHover === name) { this.coverTooltipHover = null; }
      this.syncCoverTooltips();
    }

    onTooltipFocusIn(event) {
      var name = this.tooltipNameFromTarget(event && event.target);
      if (!name) { return; }
      this.coverTooltipFocus = name;
      this.coverTooltipFocusSuppressed = null;
      this.syncCoverTooltips();
    }

    onTooltipFocusOut(event) {
      var name = this.tooltipNameFromTarget(event && event.target);
      if (!name || this.tooltipNameFromTarget(event && event.relatedTarget) === name) { return; }
      if (this.coverTooltipFocus === name) { this.coverTooltipFocus = null; }
      if (this.coverTooltipFocusSuppressed === name) { this.coverTooltipFocusSuppressed = null; }
      this.syncCoverTooltips();
    }

    onDocumentPointerRelease() {
      this.pointerPressed = false;
      var reveal = this.pendingResolutionReveal;
      this.pendingResolutionReveal = null;
      if (reveal) { root.setTimeout(reveal, 0); }
    }

    onDocumentPointerDown(event) {
      this.pointerPressed = true;
      if (!this.sizeTooltipOpen && !this.uploadTooltipOpen) { return false; }
      if (this.tooltipNameFromTarget(event && event.target)) { return false; }
      this.dismissCoverTooltips();
      return true;
    }

    onKeyDown(event) {
      if (event && event.key === "Escape" && (this.sizeTooltipOpen || this.uploadTooltipOpen)) {
        event.preventDefault();
        this.coverTooltipFocusSuppressed = this.coverTooltipFocus;
        this.coverTooltipDisclosure = null;
        this.coverTooltipHover = null;
        this.syncCoverTooltips();
        return true;
      }
      return false;
    }

    findCatalogue(id) {
      return this.catalogue.filter(function(item) { return item.id === id; })[0] || null;
    }

    designPalettes(state) {
      var background = state && state.cover && state.cover.background;
      var item = background && background.source === "catalogue"
        ? this.findCatalogue(background.designId)
        : null;
      return resolveDesignPalettes(item, resolvedCoverSize(state), this.colourSettings);
    }

    defaultTextObjectInput(state) {
      var palettes = this.designPalettes(state);
      return {
        lines: [0, 1, 2].map(function() {
          return { text: "", fontId: "minimal", fontSize: 24, colour: palettes.text[0] };
        }),
        shapeColour: palettes.shape[0]
      };
    }

    isPristineTextObject(object) {
      return Boolean(object
        && object.type === "text"
        && object.shape === "none"
        && normalizeHexColour(object.shapeColour) === "#E7D1FD"
        && object.lines.every(function(line) {
          return !String(line.text || "").trim()
            && line.fontId === "minimal"
            && Number(line.fontSize) === 24
            && normalizeHexColour(line.colour) === "#17545B";
        }));
    }

    automaticPaletteColourKey(sideName, objectId, field) {
      return sideName + ":" + objectId + ":" + field;
    }

    automaticPaletteColourBindings() {
      if (!(this.automaticPaletteColours instanceof Map)) {
        this.automaticPaletteColours = new Map();
      }
      return this.automaticPaletteColours;
    }

    rememberAutomaticPaletteObject(sideName, object) {
      if (!object || object.type !== "text") { return; }
      var bindings = this.automaticPaletteColourBindings();
      object.lines.forEach(function(line, lineIndex) {
        bindings.set(
          this.automaticPaletteColourKey(sideName, object.id, "line:" + lineIndex),
          normalizeHexColour(line.colour)
        );
      }, this);
      bindings.set(
        this.automaticPaletteColourKey(sideName, object.id, "shape"),
        normalizeHexColour(object.shapeColour)
      );
    }

    forgetAutomaticPaletteColour(sideName, objectId, field) {
      this.automaticPaletteColourBindings().delete(
        this.automaticPaletteColourKey(sideName, objectId, field)
      );
    }

    applyDesignPaletteToPristineObjects(state) {
      var palettes = this.designPalettes(state);
      var nextTextColour = normalizeHexColour(palettes.text[0]);
      var nextShapeColour = normalizeHexColour(palettes.shape[0]);
      var bindings = this.automaticPaletteColourBindings();
      ["front", "back"].forEach(function(sideName) {
        state.cover[sideName].objects.forEach(function(object) {
          if (!object || object.type !== "text") { return; }
          var legacyPristine = this.isPristineTextObject(object);
          var changedLines = false;
          var lines = object.lines.map(function(line, lineIndex) {
            var key = this.automaticPaletteColourKey(sideName, object.id, "line:" + lineIndex);
            var currentColour = normalizeHexColour(line.colour);
            var previousAutomaticColour = bindings.get(key);
            if (!legacyPristine && previousAutomaticColour && currentColour !== previousAutomaticColour) {
              bindings.delete(key);
              return line;
            }
            if (!legacyPristine && !previousAutomaticColour) { return line; }
            bindings.set(key, nextTextColour);
            if (currentColour === nextTextColour) { return line; }
            changedLines = true;
            return Object.assign({}, line, { colour: nextTextColour });
          }, this);
          var shapeKey = this.automaticPaletteColourKey(sideName, object.id, "shape");
          var currentShapeColour = normalizeHexColour(object.shapeColour);
          var previousAutomaticShapeColour = bindings.get(shapeKey);
          var updateShape = legacyPristine
            || Boolean(previousAutomaticShapeColour && currentShapeColour === previousAutomaticShapeColour);
          if (!legacyPristine && previousAutomaticShapeColour && !updateShape) {
            bindings.delete(shapeKey);
          }
          if (updateShape) {
            bindings.set(shapeKey, nextShapeColour);
          }
          var changedShape = updateShape && currentShapeColour !== nextShapeColour;
          if (changedLines || changedShape) {
            var patch = {};
            if (changedLines) { patch.lines = lines; }
            if (changedShape) { patch.shapeColour = nextShapeColour; }
            this.dispatch("COVER_UPDATE_OBJECT", {
              side: sideName,
              id: object.id,
              patch: patch
            });
          }
        }, this);
      }, this);
    }

    resolvedCoverPreviewTemplate(state) {
      var background = state && state.cover && state.cover.background;
      var item = background && background.source === "catalogue"
        ? this.findCatalogue(background.designId)
        : null;
      return resolveCoverPreviewTemplate(
        item,
        state && state.journey && state.journey.lifestyle
      );
    }

    render(state) {
      this.renderSelection(state);
      ["front", "back"].forEach(function(side) {
        if (state.screen !== side + "-cover") { return; }
        var active = document.activeElement;
        var container = this.elements[side];
        var rangeEditing = this.rangeDraft
          && this.rangeDraft.side === side
          && this.rangeDraft.target
          && container
          && container.contains(this.rangeDraft.target);
        var customColourEditing = this.customColourCommitSide === side
          || (this.customColourDraft
            && this.customColourDraft.side === side
            && this.customColourDraft.target
            && container
            && container.contains(this.customColourDraft.target))
          || (active
            && container
            && container.contains(active)
            && active.matches("[data-cover-custom-line-colour],[data-cover-custom-shape-colour]"));
        var editing = rangeEditing
          || customColourEditing
          || (active && container && container.contains(active) && active.matches("[data-cover-input]"));
        if (!editing) {
          this.renderSide(state, side);
        }
      }, this);
    }

    // The cover-selection panel is hidden on every other screen, where each
    // render (every keystroke) used to rebuild it: an uploaded photo's crop
    // editor and Cropper, or a catalogue grid whose scroll position is read.
    // There it is redrawn only when something it shows changes. On
    // cover-selection it is always drawn.
    renderSelection(state) {
      var hidden = state.screen !== "cover-selection";
      if (hidden && this.elements.selection
        && this.coverSelectionHiddenKey === this.coverSelectionRenderKey(state)) {
        return;
      }
      this.renderSelectionPanel(state);
      this.coverSelectionHiddenKey = hidden ? this.coverSelectionRenderKey(state) : null;
    }

    // Everything renderSelectionPanel reads.
    coverSelectionRenderKey(state) {
      var cover = state.cover || {};
      var background = cover.background || null;
      var memory = this.assets && this.assets.memory;
      var record = background && background.source === "upload" && memory && typeof memory.get === "function"
        ? memory.get(background.assetId)
        : null;
      return JSON.stringify([
        state.screen,
        state.journey || null,
        state.selection || null,
        cover.source || "",
        cover.filters || null,
        background,
        record ? [record.width, record.height, String(record.previewUrl || "").length] : null,
        this.coverGridKey(state, this.availableCatalogue(state)),
        Boolean(this.didInitializeBackground),
        Boolean(this.didChooseSize),
        Boolean(this.didChooseSource),
        Boolean(this.didChooseDesign),
        this.openSelectionMenu || "",
        Boolean(this.sizeTooltipOpen),
        Boolean(this.uploadTooltipOpen),
        this.privateStorageEnabled()
      ]);
    }

    renderSelectionPanel(state) {
      var container = this.elements.selection;
      if (!container) { return; }
      var previousGrid = container.querySelector ? container.querySelector(".dyof__cover-grid") : null;
      var items = this.availableCatalogue(state);
      var structureKey = this.coverSelectionStructureKey(state, items);
      var selectedId = state.cover.background && state.cover.background.source === "catalogue"
        ? state.cover.background.designId : "";
      if (
        previousGrid
        && this.coverSelectionStructureSignature === structureKey
        && previousGrid.dataset.coverGridKey === this.coverGridKey(state, items)
      ) {
        Array.from(previousGrid.querySelectorAll("[data-cover-design]")).forEach(function(card) {
          card.setAttribute("aria-pressed", String(this.didChooseDesign && card.dataset.coverDesign === selectedId));
        }, this);
        this.rememberCoverGridScroll(previousGrid);
        return;
      }
      this.coverSelectionStructureSignature = structureKey;
      var activeElement = root && root.document && root.document.activeElement;
      var previousFocusedDesign = activeElement
        && container.contains && container.contains(activeElement)
        && activeElement.dataset
        ? String(activeElement.dataset.coverDesign || "")
        : "";
      var previousGridState = previousGrid ? {
        key: previousGrid.dataset.coverGridKey || "",
        scrollTop: previousGrid.scrollTop
      } : null;
      this.rememberCoverGridScroll(previousGrid);
      this.destroyCroppers(container);
      if (!this.didInitializeBackground) {
        this.didInitializeBackground = true;
        if (state.cover.background) {
          this.didChooseSource = true;
          this.didChooseDesign = true;
        }
      }
      var visibleSelectedId = this.didChooseDesign ? selectedId : "";
      var html = "";
      var hostData = this.host && this.host.dataset ? this.host.dataset : {};
      var privateUploadReady = this.privateStorageEnabled();
      var plannerA5Only = Boolean(
        state.journey && state.journey.productType === "planner"
      );
      if (plannerA5Only) {
        this.didChooseSize = true;
      } else {
        var sizeInfoIcon = hostData.coverSizeInfoIcon || "";
        var sizeInfoBubble = hostData.coverSizeInfoBubble || "";
        html += '<section class="dyof__cover-selection-section dyof__cover-selection-section--size"><div class="dyof__cover-section-heading"><h3>' + escapeHtml(dyofT("cover.controls.size", "Size")) + '</h3><button type="button" class="dyof__cover-size-help" data-cover-size-help data-cover-tooltip-trigger="size" aria-label="' + escapeHtml(dyofT("cover.selection.size_help", "Show size information")) + '" aria-controls="dyof-cover-size-detail" aria-describedby="dyof-cover-size-detail" aria-expanded="' + this.sizeTooltipOpen + '"><img src="' + escapeHtml(sizeInfoIcon) + '" alt=""></button><small id="dyof-cover-size-detail" data-cover-tooltip="size" data-open="' + this.sizeTooltipOpen + '" role="tooltip" aria-hidden="' + (!this.sizeTooltipOpen) + '"' + (this.sizeTooltipOpen ? "" : " hidden") + '><img src="' + escapeHtml(sizeInfoBubble) + '" alt=""><span>' + escapeHtml(dyofT("cover.selection.dimensions", "A5 (212 x 185 mm) - Personal (175 x 135 mm)")) + '</span></small></div>';
        html += '<div class="dyof__cover-sizes" role="group" aria-label="' + escapeHtml(dyofT("cover.selection.cover_size", "Cover size")) + '">';
        ["a5", "personal"].forEach(function(size) {
          html += '<button type="button" data-cover-size="' + size + '" aria-pressed="' + (this.didChooseSize && state.selection.size === size) + '">' + escapeHtml(size === "a5" ? dyofT("cover.selection.a5", "A5") : dyofT("cover.selection.personal", "Personal")) + '</button>';
        }, this);
        html += "</div></section>";
      }
      // Clipbook starts with an explicit Size decision. Keep every Design
      // control out of the disclosure until A5 or Personal has been chosen;
      // Planner is intrinsically A5-only, so it proceeds directly to Design.
      if (!plannerA5Only && !this.didChooseSize) {
        container.innerHTML = html;
        return;
      }
      html += '<section class="dyof__cover-selection-section dyof__cover-selection-section--design"><h3>' + escapeHtml(dyofT("cover.selection.design", "Design")) + '</h3><p>' + escapeHtml(dyofT("cover.selection.design_help", "Choose the style that suits you and make it uniquely yours.")) + '</p>';
      html += '<div class="dyof__cover-source-select">' + selectionMenuHtml({
        name: "source",
        kind: "source",
        label: this.openSelectionMenu === "source" || !this.didChooseSource
          ? dyofT("cover.selection.select", "Select Cover")
          : state.cover.source === "upload" ? dyofT("cover.selection.upload", "Upload Your Own Cover") : dyofT("cover.selection.catalogue", "Personalise a Filofax Design"),
        selected: this.didChooseSource ? state.cover.source : null,
        open: this.openSelectionMenu === "source",
        options: [
          { id: "catalogue", label: dyofT("cover.selection.catalogue", "Personalise a Filofax Design") },
          { id: "upload", label: dyofT("cover.selection.upload", "Upload Your Own Cover") }
        ]
      }) + '</div>';
      if (this.openSelectionMenu === "source" || !this.didChooseSource) {
        container.innerHTML = html + "</section>";
        return;
      }
      if (state.cover.source === "upload") {
        var uploadedBackground = state.cover.background && state.cover.background.source === "upload"
          ? state.cover.background
          : null;
        var cropGuideProduct = state.journey && state.journey.productType === "planner"
          ? "planner"
          : "clipbook";
        var cropGuideSize = resolvedCoverSize(state);
        html += '<div class="dyof__cover-upload" data-cover-drop="background">';
        html += '<div class="dyof__cover-upload-heading"><h4>' + escapeHtml(dyofT("cover.upload.background_heading", "Upload your background image")) + '</h4><button type="button" class="dyof__cover-upload-help" data-cover-tooltip-trigger="upload" aria-label="' + escapeHtml(dyofT("cover.upload.background_help", "Show background image guidance")) + '" aria-controls="dyof-cover-upload-detail" aria-describedby="dyof-cover-upload-detail" aria-expanded="' + this.uploadTooltipOpen + '"><img src="' + escapeHtml(hostData.coverSizeInfoIcon || "") + '" alt=""></button><p id="dyof-cover-upload-detail" class="dyof__cover-upload-guidance" data-cover-tooltip="upload" data-open="' + this.uploadTooltipOpen + '" role="tooltip" aria-hidden="' + (!this.uploadTooltipOpen) + '"' + (this.uploadTooltipOpen ? "" : " hidden") + '>' + escapeHtmlText(dyofT("cover.upload.landscape", "Upload a landscape image. You'll be able to reposition it below.")) + '</p></div>';
        html += '<p class="dyof__cover-upload-copy">' + escapeHtmlText(dyofT("cover.upload.artwork_copy", "Add your Artwork or photo to create a cover that's uniquely yours.")) + '</p>';
        if (uploadedBackground) {
          var backgroundName = String(uploadedBackground.title || "");
          if (!backgroundName.trim()) { backgroundName = dyofT("cover.upload.cover_name", "Uploaded cover"); }
          html += '<span class="dyof__cover-upload-chip" title="' + escapeHtml(backgroundName) + '"><span aria-hidden="true">' + escapeHtml(uploadChipLabel(backgroundName)) + '</span><span class="dyof__visually-hidden">' + escapeHtml(dyofT("cover.image.uploaded_file", "Uploaded file: {name}", { name: backgroundName })) + '</span><button type="button" data-cover-remove-background aria-label="' + escapeHtml(dyofT("cover.controls.remove_named", "Remove {name}", { name: backgroundName })) + '">×</button></span>';
        } else if (privateUploadReady) {
          html += '<label class="dyof__cover-upload-button">' + escapeHtml(dyofT("cover.upload.image", "Upload image")) + '<input type="file" accept="image/jpeg,image/png" data-cover-file="background"></label>';
        } else {
          html += '<span class="dyof__cover-upload-button" aria-disabled="true">' + escapeHtml(dyofT("cover.upload.unavailable", "Upload unavailable")) + '</span>';
        }
        html += '<small>' + escapeHtml(privateUploadReady ? dyofT("cover.upload.formats", "JPG, PNG (max 10MB)") : dyofT("cover.upload.storage_inactive", "Secure photo storage is not active yet.")) + '</small>';
        html += this.uploadRefusalHtml("background", "dyof__quality-warning--background");
        if (uploadedBackground) {
          // Directly under the upload field, so a low-resolution warning is seen.
          html += '<p class="dyof__quality-warning dyof__quality-warning--background" data-cover-resolution-slot="background" hidden></p>';
          html += '<div class="dyof__background-crop-editor" data-cover-background-editor="' + escapeHtml(uploadedBackground.assetId) + '" data-cover-crop-product="' + escapeHtml(cropGuideProduct) + '" data-cover-crop-size="' + escapeHtml(cropGuideSize) + '" aria-label="' + escapeHtml(dyofT("cover.upload.crop_wrap", "Crop and reposition the full cover wrap")) + '"></div>';
          if (uploadedBackground.geometryVersion !== 2) {
            html += '<p class="dyof__cover-upload-copy">' + escapeHtml(dyofT("cover.upload.old_crop", "This saved cover uses the previous crop proportions. Update them below, then check the position of your image.")) + '</p>';
          }
          html += '<div class="dyof__background-crop-actions"><button type="button" data-cover-remove-background>' + escapeHtml(dyofT("cover.controls.remove", "Remove")) + '</button>' + (privateUploadReady ? '<label>' + escapeHtml(dyofT("cover.upload.replace", "Replace")) + '<input type="file" accept="image/jpeg,image/png" data-cover-file="background"></label>' : '') + '</div>';
          if (uploadedBackground.geometryVersion !== 2) {
            html += '<div class="dyof__background-crop-actions"><button type="button" data-cover-update-crop-proportions>' + escapeHtml(dyofT("cover.upload.update_crop", "Update crop proportions")) + '</button></div>';
          }
        }
        html += '</div>';
        container.innerHTML = html + "</section>";
        if (uploadedBackground) {
          this.syncBackgroundResolutionSlot(state);
          this.initializeBackgroundCropEditor(container, state);
        }
        return;
      }
      html += '<p class="dyof__cover-filter-copy">' + escapeHtml(dyofT("cover.filters.browse", "Browse our collection using filters.")) + '</p>';
      var themeOptions = availableFilterGroups(
        THEME_FILTER_GROUPS,
        this.availableCatalogue(state, { theme: "all", colour: state.cover.filters.colour }),
        "searchFilter"
      );
      var colourOptions = availableFilterGroups(
        COLOUR_FILTER_GROUPS,
        this.availableCatalogue(state, { theme: state.cover.filters.theme, colour: "all" }),
        "colourTags"
      );
      html += '<div class="dyof__cover-filters">';
      html += selectionMenuHtml({
        name: "theme",
        kind: "theme",
        label: dyofT("cover.filters.theme", "BY THEME"),
        selected: (filterGroup(THEME_FILTER_GROUPS, state.cover.filters.theme) || {}).id || null,
        open: this.openSelectionMenu === "theme",
        options: themeOptions
      });
      html += selectionMenuHtml({
        name: "colour",
        kind: "colour",
        label: dyofT("cover.filters.colour", "BY COLOUR"),
        selected: (filterGroup(COLOUR_FILTER_GROUPS, state.cover.filters.colour) || {}).id || null,
        open: this.openSelectionMenu === "colour",
        options: colourOptions
      });
      html += '</div>';
      var coverGridKey = this.coverGridKey(state, items);
      html += '<div class="dyof__cover-grid" data-cover-grid-key="' + escapeHtml(coverGridKey) + '">';
      items.forEach(function(item, index) {
        var preview = this.catalogueThumbnailUrl(item, state) || this.coverThumbs[index];
        var focal = this.catalogueThumbnailFocal(item, state);
        var focalX = (focal.x * 100).toFixed(2).replace(/\.00$/, "") + "%";
        var focalY = (focal.y * 100).toFixed(2).replace(/\.00$/, "") + "%";
        html += '<button type="button" class="dyof__cover-card" data-cover-design="' + escapeHtml(item.id) + '" aria-label="' + escapeHtml(item.title) + '" aria-pressed="' + (visibleSelectedId === item.id) + '" style="--dyof-thumbnail-focal-x:' + focalX + ';--dyof-thumbnail-focal-y:' + focalY + ';--dyof-thumbnail-zoom:' + focal.zoom + '"><img src="' + escapeHtml(preview) + '" alt="" loading="lazy" decoding="async" draggable="false"><span>' + escapeHtml(item.title) + '</span></button>';
      }, this);
      html += "</div>";
      if (!items.length) {
        html += '<p class="dyof__cover-empty">' + escapeHtml(dyofT("cover.selection.empty", "No production-ready covers match these filters for this size.")) + '</p>';
      }
      container.innerHTML = html + "</section>";
      var nextGrid = container.querySelector ? container.querySelector(".dyof__cover-grid") : null;
      if (previousGridState && nextGrid && nextGrid.dataset.coverGridKey === previousGridState.key) {
        nextGrid.scrollTop = previousGridState.scrollTop;
      } else if (nextGrid && this.coverGridScrollPositions.has(nextGrid.dataset.coverGridKey || "")) {
        nextGrid.scrollTop = this.coverGridScrollPositions.get(nextGrid.dataset.coverGridKey || "");
      }
      if (previousFocusedDesign && nextGrid && nextGrid.querySelectorAll) {
        var nextFocusedCard = Array.from(nextGrid.querySelectorAll("[data-cover-design]")).filter(function(card) {
          return card.dataset && card.dataset.coverDesign === previousFocusedDesign;
        })[0];
        if (nextFocusedCard && nextFocusedCard.focus) {
          var preservedScrollTop = nextGrid.scrollTop;
          nextFocusedCard.focus({ preventScroll: true });
          nextGrid.scrollTop = preservedScrollTop;
        }
      }
      this.rememberCoverGridScroll(nextGrid);
    }

    // Hidden at the owner's request on 2026-09-29; the one switch is
    // ELASTIC_CLOSURE_OFFERED in dyof-cover-contract.js.
    elasticClosureOffered() {
      return Boolean(this.contract && this.contract.ELASTIC_CLOSURE_OFFERED);
    }

    elasticSwatchHtml(id, elastic) {
      var metallic = /-metallic$/.test(id);
      var texture = ELASTIC_TEXTURES[id];
      var textureUrl = texture && this.elasticTextures ? this.elasticTextures[id] : "";
      var textureHtml = textureUrl
        ? '<span class="dyof__elastic-swatch-texture" aria-hidden="true"><img class="dyof__elastic-swatch-image dyof__elastic-swatch-image--' + id + '" src="' + escapeHtml(textureUrl) + '" alt="" width="' + texture.sourceWidth + '" height="' + texture.sourceHeight + '"></span>'
        : "";
      var label = elasticDisplayLabel(id, this.contract.ELASTIC_LABELS[id] || titleCase(id));
      return '<button type="button" class="dyof__elastic-swatch' + (metallic ? ' dyof__elastic-swatch--metallic' : '') + '" data-cover-elastic="' + id + '" aria-label="' + escapeHtml(dyofT("cover.elastic.colour", "{colour} elastic", { colour: label })) + '" aria-pressed="' + Boolean(elastic && elastic.enabled && elastic.colourId === id) + '" style="--swatch:' + this.contract.ELASTIC_COLOURS[id] + '">' + textureHtml + '</button>';
    }

    renderSide(state, sideName) {
      var container = this.elements[sideName];
      if (!container) { return; }
      var side = state.cover[sideName];
      var textObjects = side.objects.filter(function(object) { return object.type === "text"; });
      var imageObjects = side.objects.filter(function(object) { return object.type === "image"; });
      var palettes = this.designPalettes(state);
      var expandedTextBlock = this.expandedTextBlocks[sideName];
      if (this.pendingTextBlockExpansionSide === sideName) {
        var pendingActive = state.cover.activeObject
          && state.cover.activeObject.side === sideName
          && textObjects.filter(function(object) { return object.id === state.cover.activeObject.id; })[0];
        expandedTextBlock = pendingActive ? pendingActive.id : textObjects.length ? textObjects[textObjects.length - 1].id : "";
        this.pendingTextBlockExpansionSide = null;
      } else if (expandedTextBlock === null) {
        expandedTextBlock = textObjects.length ? textObjects[0].id : "";
      } else if (expandedTextBlock && !textObjects.some(function(object) { return object.id === expandedTextBlock; })) {
        expandedTextBlock = textObjects.length ? textObjects[textObjects.length - 1].id : "";
      }
      this.expandedTextBlocks[sideName] = expandedTextBlock;
      var html = "";
      if (sideName === "front" && this.elasticClosureOffered()) {
        html += '<section class="dyof__editor-section dyof__editor-section--elastic"><h3>' + escapeHtml(dyofT("cover.elastic.heading", "Elastic closure")) + '</h3><p>' + escapeHtml(dyofT("cover.elastic.help", "Add a coloured elastic band to complement your cover.")) + '</p><div class="dyof__elastic-swatches">';
        this.contract.ELASTIC_IDS.forEach(function(id) {
          html += this.elasticSwatchHtml(id, state.cover.elastic);
        }, this);
        html += '</div><label class="dyof__figma-toggle"><input type="checkbox" data-cover-elastic-disabled' + (!state.cover.elastic.enabled ? " checked" : "") + '><span aria-hidden="true"></span>' + escapeHtmlText(dyofT("cover.elastic.disabled", "I don't want to add an elastic closure")) + '</label></section>';
      }
      if (sideName === "back" && !side.textEnabled) {
        html += '<section class="dyof__editor-section dyof__editor-section--back-no-text"><label class="dyof__figma-toggle"><input type="checkbox" data-cover-text-disabled="back" checked><span aria-hidden="true"></span>' + escapeHtmlText(dyofT("cover.text.back_disabled", "I don't want to add a Text")) + '</label></section>';
      } else {
        html += '<section class="dyof__editor-section dyof__editor-section--text"><h3>' + escapeHtml(dyofT("cover.text.heading", "Add text to your cover")) + '</h3><label class="dyof__figma-toggle"><input type="checkbox" data-cover-text-disabled="' + sideName + '"' + (!side.textEnabled ? " checked" : "") + '><span aria-hidden="true"></span>' + escapeHtmlText(dyofT("cover.text.disabled", "I don't want to add text.")) + '</label>';
        if (side.textEnabled) {
          textObjects.forEach(function(object, index) {
            html += this.textObjectHtml(sideName, object, index, side.objects.length, palettes, object.id === expandedTextBlock, textObjects.length);
          }, this);
          html += '<div class="dyof__editor-add-row"><button class="dyof__editor-add" type="button" data-cover-add-text="' + sideName + '"' + (textObjects.length >= this.contract.MAX_TEXT_OBJECTS ? " disabled" : "") + '>' + escapeHtml(dyofT("cover.text.add_block", "Add another block")) + '</button></div>';
        }
        html += "</section>";
      }
      var images = container.querySelector('.dyof__editor-section--images');
      if (container._dyofTextControlsHtml !== html) {
        Array.from(container.children).forEach(function(child) {
          if (child !== images) { child.remove(); }
        });
        var textTemplate = document.createElement("template");
        textTemplate.innerHTML = html;
        container.insertBefore(textTemplate.content, images);
        container._dyofTextControlsHtml = html;
        // New inputs (for example after a block is expanded) keep the flag on
        // restored text the print font cannot draw.
        if (root && root.DYOFPrintableText) {
          root.DYOFPrintableText.flagInputs(container, state);
        }
      }
      var imageStructureKey = JSON.stringify({
        objects: imageObjects.map(function(object) {
          return [object.id, object.assetId, object.name, object.activeMask, object.qualityWarning];
        }),
        privateStorage: this.privateStorageEnabled()
      });
      var externalCropChange = images && this.cropperEntries.some(function(entry) {
        if (!images.contains(entry.element)) { return false; }
        var object = imageObjects.filter(function(item) { return item.id === entry.id; })[0];
        return object && entry.committedCrop !== this.imageCropKey(object.crops[object.activeMask]);
      }, this);
      if (images && images._dyofImageStructureKey === imageStructureKey && !externalCropChange) {
        imageObjects.forEach(function(object) {
          var input = images.querySelector('[data-cover-object="' + object.id + '"][data-cover-transform="scale"]');
          if (input && document.activeElement !== input) {
            input.value = object.transform.scale;
            this.updateRangeFill(input);
          }
          var backward = images.querySelector('[data-cover-object="' + object.id + '"][data-cover-move="backward"]');
          var forward = images.querySelector('[data-cover-object="' + object.id + '"][data-cover-move="forward"]');
          if (backward) { backward.disabled = object.order === 0; }
          if (forward) { forward.disabled = object.order === side.objects.length - 1; }
        }, this);
        this.syncPhotoResolutionSlots(images, state, sideName);
        return;
      }
      if (images) {
        this.destroyCroppers(images);
        images.remove();
      }
      html = '<section class="dyof__editor-section dyof__editor-section--images"><h3>' + escapeHtml(dyofT("cover.image.heading", "Add your picture or logo")) + '</h3>';
      imageObjects.forEach(function(object, index) {
        html += this.imageObjectHtml(sideName, object, index, side.objects.length);
      }, this);
      for (var slot = imageObjects.length; slot < this.contract.MAX_IMAGE_OBJECTS; slot += 1) {
        html += this.emptyImageSlotHtml(sideName, slot);
      }
      html += "</section>";
      var imageTemplate = document.createElement("template");
      imageTemplate.innerHTML = html;
      images = imageTemplate.content.firstElementChild;
      images._dyofImageStructureKey = imageStructureKey;
      container.appendChild(images);
      this.syncPhotoResolutionSlots(images, state, sideName);
      this.populateAssetThumbs(images).then(function() {
        this.syncPhotoResolutionSlots(images, this.store.getState(), sideName);
        this.initializeCropEditors(images, this.store.getState(), sideName);
      }.bind(this));
    }

    textObjectHtml(side, object, index, count, palettes, expanded, textBlockCount) {
      var textColours = palettes && palettes.text || DEFAULT_TEXT_COLOURS;
      var shapeColours = palettes && palettes.shape || DEFAULT_SHAPE_COLOURS;
      var hasMultipleBlocks = Number(textBlockCount) > 1;
      var bodyExpanded = hasMultipleBlocks ? expanded : true;
      var bodyId = "dyof-text-block-" + escapeHtml(object.id);
      var html = '<div class="dyof__figma-text-block" data-text-block="' + object.id + '" data-expanded="' + bodyExpanded + '">';
      if (hasMultipleBlocks) {
        html += '<div class="dyof__text-block-summary"><span>' + escapeHtml(dyofT("cover.text.block_number", "BLOCK {number}", { number: index + 1 })) + '</span>' + (index > 0 ? '<button type="button" class="dyof__text-block-remove" data-cover-remove data-cover-object="' + object.id + '" data-cover-side="' + side + '" aria-label="' + escapeHtml(dyofT("cover.text.delete_block", "Delete block {number}", { number: index + 1 })) + '"><span aria-hidden="true">×</span></button>' : '') + '<button type="button" class="dyof__text-block-toggle" data-cover-text-block-toggle="' + object.id + '" data-cover-side="' + side + '" aria-expanded="' + bodyExpanded + '" aria-controls="' + bodyId + '" aria-label="' + escapeHtml(bodyExpanded ? dyofT("cover.text.collapse_block", "Collapse block {number}", { number: index + 1 }) : dyofT("cover.text.expand_block", "Expand block {number}", { number: index + 1 })) + '"><i aria-hidden="true"></i></button></div>';
      } else {
        html += '<div class="dyof__text-block-heading"><strong>' + escapeHtml(dyofT("cover.text.block", "BLOCK")) + '</strong></div>';
      }
      html += '<div class="dyof__text-block-body" id="' + bodyId + '"' + (bodyExpanded ? "" : " hidden") + '>';
      object.lines.forEach(function(line, lineIndex) {
        var hasText = Boolean(String(line.text || "").trim());
        var hasDefaultEmptyTypography = !line.text && line.fontId === "minimal" && Number(line.fontSize) === 24;
        var fontOptions = FONT_OPTIONS.map(function(font) {
          return '<option value="' + font.id + '"' + (line.fontId === font.id ? " selected" : "") + '>' + escapeHtml(fontDisplayLabel(font.id).toUpperCase()) + '</option>';
        }).join("");
        var sizeOptions = [12, 14, 16, 18, 20, 24, 28, 32].map(function(size) {
          return '<option value="' + size + '"' + (Number(line.fontSize) === size ? " selected" : "") + '>' + size + '</option>';
        }).join("");
        if (hasDefaultEmptyTypography) {
          fontOptions = '<option value="" disabled selected>' + escapeHtml(dyofT("cover.text.font_placeholder", "FONT")) + '</option>' + fontOptions.replace(' selected', '');
          sizeOptions = '<option value="" disabled selected>' + escapeHtml(dyofT("cover.text.size_placeholder", "SIZE")) + '</option>' + sizeOptions.replace(' selected', '');
        }
        html += '<div class="dyof__figma-text-line"><label' + (lineIndex ? ' class="dyof__visually-hidden-label"' : "") + '>' + escapeHtml(dyofT("cover.text.label", "Text")) + '</label><div class="dyof__figma-text-fields"><input class="dyof__text-input" type="text" maxlength="48" value="' + escapeHtml(line.text) + '" placeholder="' + escapeHtml(dyofT("cover.text.placeholder", "Add your text here")) + '" data-cover-input data-cover-object="' + object.id + '" data-cover-side="' + side + '" data-cover-line="' + lineIndex + '" data-cover-line-prop="text"><select aria-label="' + escapeHtml(dyofT("cover.text.font", "Font")) + '" class="dyof__select" data-cover-input data-cover-object="' + object.id + '" data-cover-side="' + side + '" data-cover-line="' + lineIndex + '" data-cover-line-prop="fontId">' + fontOptions + '</select><select aria-label="' + escapeHtml(dyofT("cover.controls.size", "Size")) + '" class="dyof__select dyof__font-size-select" data-cover-input data-cover-object="' + object.id + '" data-cover-side="' + side + '" data-cover-line="' + lineIndex + '" data-cover-line-prop="fontSize">' + sizeOptions + '</select></div>';
        html += '<div class="dyof__figma-colour-row" aria-label="' + escapeHtml(dyofT("cover.text.colour", "Text colour")) + '">' + textColours.map(function(colour) {
          var whiteClass = colour === "#FFFFFF" ? ' dyof__colour-swatch--white' : '';
          return '<button type="button" class="dyof__colour-swatch' + whiteClass + '" data-cover-line-colour="' + colour + '" data-cover-line="' + lineIndex + '" data-cover-object="' + object.id + '" data-cover-side="' + side + '" aria-label="' + escapeHtml(dyofT("cover.controls.use_colour", "Use {colour}", { colour: colour })) + '" aria-pressed="' + (hasText && normalizeHexColour(line.colour) === colour) + '" style="--swatch:' + colour + '"></button>';
        }).join("") + this.customColourInputHtml("line", side, object.id, lineIndex, line.colour, textColours, hasText) + '</div></div>';
      }, this);
      html += '<div class="dyof__figma-shape-control"><p>' + escapeHtml(dyofT("cover.text.shape", "Shape")) + '</p><div class="dyof__shape-choices">' + SHAPE_OPTIONS.slice(1).map(function(shape) {
        var isSelected = object.shape === shape;
        return '<button type="button" data-shape-name="' + shape + '" data-cover-shape="' + shape + '" data-cover-object="' + object.id + '" data-cover-side="' + side + '" aria-label="' + escapeHtml(isSelected ? dyofT("cover.text.remove_shape", "Remove {shape} shape", { shape: shapeDisplayLabel(shape) }) : dyofT("cover.text.use_shape", "Use {shape} shape", { shape: shapeDisplayLabel(shape) })) + '" aria-pressed="' + isSelected + '">' + shapeIconHtml(shape) + (isSelected ? '<span class="dyof__shape-remove-mark" aria-hidden="true">×</span>' : '') + '</button>';
      }).join("") + '</div><div class="dyof__figma-colour-row dyof__figma-colour-row--shape" aria-label="' + escapeHtml(dyofT("cover.text.shape_colour", "Shape colour")) + '">' + shapeColours.map(function(colour) {
        var whiteClass = colour === "#FFFFFF" ? ' dyof__colour-swatch--white' : '';
        return '<button type="button" class="dyof__colour-swatch' + whiteClass + '" data-cover-shape-colour="' + colour + '" data-cover-object="' + object.id + '" data-cover-side="' + side + '" aria-label="' + escapeHtml(dyofT("cover.controls.use_colour", "Use {colour}", { colour: colour })) + '" aria-pressed="' + (normalizeHexColour(object.shapeColour) === colour) + '" style="--swatch:' + colour + '"></button>';
      }).join("") + this.customColourInputHtml("shape", side, object.id, null, object.shapeColour, shapeColours) + '</div>';
      html += '</div>';
      html += '<div class="dyof__figma-size-position"><label><span>' + escapeHtml(dyofT("cover.controls.size", "Size")) + '</span><span class="dyof__range-row"><b>−</b><input aria-label="' + escapeHtml(dyofT("cover.text.shape_size", "Shape size")) + '" type="range" min="0.2" max="3" step="0.05" value="' + object.shapeScale + '"' + rangeFillStyle(object.shapeScale, 0.2, 3) + ' data-cover-input data-cover-object="' + object.id + '" data-cover-side="' + side + '" data-cover-shape-scale><b>+</b></span></label><div><span>' + escapeHtml(dyofT("cover.controls.position", "Position")) + '</span>' + this.positionHelpHtml(dyofT("cover.text.position_help", "Drag and drop the block on the preview to position it on your cover. <strong>click to move it or incline it.</strong>")) + '</div></div>';
      html += '<div data-cover-overflow-warning-slot="' + object.id + '">' + this.textOverflowWarningHtml(side, object, object.overflowWarningFor) + '</div>';
      html += this.layerActionsHtml(side, object, count);
      return html + "</div></div>";
    }

    customColourInputHtml(kind, side, objectId, lineIndex, value, presets, selected) {
      var isLine = kind === "line";
      var label = isLine ? dyofT("cover.text.custom_colour", "Choose any text colour") : dyofT("cover.text.custom_shape_colour", "Choose any shape colour");
      var customSelected = selected !== false && (presets || []).indexOf(normalizeHexColour(value)) === -1;
      var attributes = isLine
        ? ' data-cover-custom-line-colour data-cover-line="' + lineIndex + '"'
        : ' data-cover-custom-shape-colour';
      return '<label class="dyof__rainbow-swatch' + (customSelected ? ' is-selected' : '') + '" data-cover-custom-colour-trigger title="' + escapeHtml(label) + '">'
        + '<input class="dyof__custom-colour-input" type="color" value="' + escapeHtml(value) + '" aria-label="' + escapeHtml(label) + '"'
        + attributes + ' data-cover-object="' + escapeHtml(objectId) + '" data-cover-side="' + side + '">'
        + '<span class="dyof__visually-hidden">' + escapeHtml(label) + '</span></label>';
    }

    textOverflowWarningHtml(side, object, warningFor) {
      if (!warningFor) { return ""; }
      var overflowAccepted = object.overflowAcceptedFor === warningFor;
      var html = '<div class="dyof__text-overflow-warning' + (overflowAccepted ? ' is-accepted' : '') + '" role="alert"><strong>' + escapeHtml(overflowAccepted ? dyofT("cover.text.print_confirmed", "Print as shown confirmed") : dyofT("cover.text.overflow", "Text extends beyond the shape")) + '</strong><p>' + escapeHtml(overflowAccepted ? dyofT("cover.text.print_confirmed_help", "We will print this cover exactly as it appears in the preview.") : dyofT("cover.text.overflow_help", "Part of your text sits outside the selected shape. The preview shows exactly how the cover will print.")) + '</p><div>';
      if (!overflowAccepted) {
        html += '<button type="button" data-cover-overflow-accept="' + warningFor + '" data-cover-object="' + object.id + '" data-cover-side="' + side + '">' + escapeHtml(dyofT("cover.text.keep_print", "Keep and print as shown")) + '</button>';
      }
      return html + '<button type="button" data-cover-overflow-revise data-cover-object="' + object.id + '" data-cover-side="' + side + '">' + escapeHtml(overflowAccepted ? dyofT("cover.text.change_choice", "Change this choice") : dyofT("cover.text.adjust", "Adjust text or shape")) + '</button></div></div>';
    }

    imageObjectHtml(side, object, index, count) {
      var imageName = String(objectDisplayName(object, this.assets) || "");
      if (!imageName.trim()) { imageName = dyofT("cover.image.uploaded_name", "Uploaded image"); }
      var html = '<div class="dyof__figma-image-slot dyof__figma-image-slot--populated" data-cover-drop="image" data-cover-side="' + side + '" data-cover-object="' + object.id + '"><div class="dyof__figma-upload-row"><span class="dyof__file-chip" title="' + escapeHtml(imageName) + '"><span aria-hidden="true">' + escapeHtml(uploadChipLabel(imageName)) + '</span><span class="dyof__visually-hidden">' + escapeHtml(dyofT("cover.image.uploaded_file", "Uploaded file: {name}", { name: imageName })) + '</span><button type="button" data-cover-remove data-cover-object="' + object.id + '" data-cover-side="' + side + '" aria-label="' + escapeHtml(dyofT("cover.controls.remove_named", "Remove {name}", { name: imageName })) + '">×</button></span><label><span>' + escapeHtml(dyofT("cover.controls.size", "Size")) + '</span><span class="dyof__range-row"><b>−</b><input aria-label="' + escapeHtml(dyofT("cover.image.size", "Image size")) + '" type="range" min="0.2" max="3" step="0.05" value="' + object.transform.scale + '"' + rangeFillStyle(object.transform.scale, 0.2, 3) + ' data-cover-input data-cover-object="' + object.id + '" data-cover-side="' + side + '" data-cover-transform="scale"><b>+</b></span></label><div class="dyof__figma-position-help"><span>' + escapeHtml(dyofT("cover.controls.position", "Position")) + '</span>' + this.positionHelpHtml(dyofT("cover.image.position_help", "Drag and drop the picture on the preview to position it on your cover. <strong>Click to move it or incline it.</strong>")) + '</div></div><small>' + escapeHtml(dyofT("cover.upload.formats", "JPG, PNG (max 10MB)")) + '</small>' + this.uploadRefusalHtml(this.uploadRefusalKey("image", side, object.id)) + '<p class="dyof__quality-warning" data-cover-resolution-slot="' + object.id + '" hidden></p><div class="dyof__figma-image-detail"><div class="dyof__asset-thumb dyof__crop-editor" data-cover-asset-thumb="' + object.assetId + '" data-cover-crop-editor="' + object.id + '" data-cover-side="' + side + '"></div><fieldset><legend>' + escapeHtml(dyofT("cover.image.choose_shape", "Choose image shape")) + '</legend><div class="dyof__mask-choices">';
      html += MASK_OPTIONS.map(function(mask) {
        return '<button type="button" data-cover-mask="' + mask + '" data-cover-object="' + object.id + '" data-cover-side="' + side + '" aria-pressed="' + (object.activeMask === mask) + '"><i data-mask-name="' + mask + '"></i><span>' + escapeHtml(shapeDisplayLabel(mask)) + '</span></button>';
      }).join("") + '</div></fieldset></div>';
      html += this.layerActionsHtml(side, object, count);
      return html + "</div>";
    }

    emptyImageSlotHtml(side, index) {
      var emptyScale = side === "back" ? 2.01 : 1;
      var uploadControl = this.privateStorageEnabled()
        ? '<label class="dyof__figma-upload-button">' + escapeHtml(dyofT("cover.upload.image", "Upload image")) + '<input type="file" accept="image/jpeg,image/png" data-cover-file="image" data-cover-side="' + side + '" data-cover-slot="' + index + '"></label>'
        : '<span class="dyof__figma-upload-button" aria-disabled="true">' + escapeHtml(dyofT("cover.upload.unavailable", "Upload unavailable")) + '</span>';
      var uploadHelp = this.privateStorageEnabled()
        ? dyofT("cover.upload.formats", "JPG, PNG (max 10MB)")
        : dyofT("cover.upload.storage_inactive", "Secure photo storage is not active yet.");
      return '<div class="dyof__figma-image-slot" data-cover-drop="image" data-cover-side="' + side + '" data-cover-slot="' + index + '"><div class="dyof__figma-upload-row">' + uploadControl + '<label><span>' + escapeHtml(dyofT("cover.controls.size", "Size")) + '</span><span class="dyof__range-row"><b>−</b><input aria-label="' + escapeHtml(dyofT("cover.image.slot_size", "Image slot {number} size", { number: index + 1 })) + '" type="range" min="0.2" max="3" step="0.05" value="' + emptyScale + '"' + rangeFillStyle(emptyScale, 0.2, 3) + ' disabled><b>+</b></span></label><div class="dyof__figma-position-help"><span>' + escapeHtml(dyofT("cover.controls.position", "Position")) + '</span>' + this.positionHelpHtml(dyofT("cover.image.empty_position_help", "Upload a picture, then drag it on the preview to position it on your cover. <strong>Click to move it or incline it.</strong>")) + '</div></div><small>' + escapeHtml(uploadHelp) + '</small>' + this.uploadRefusalHtml(this.uploadRefusalKey("image", side, null, index)) + '</div>';
    }

    positionHelpHtml(copy) {
      var icon = this.host.dataset.coverPositionIcon;
      return '<p class="dyof__position-copy">' + (icon ? '<img class="dyof__position-icon" src="' + escapeHtml(icon) + '" alt="" width="11" height="11">' : '') + '<span>' + copy + '</span></p>';
    }

    transformHtml(side, object) {
      return '<fieldset class="dyof__transform-fields"><legend>' + escapeHtml(dyofT("cover.controls.size_position", "Size and position")) + '</legend>' + [
        ["scale", dyofT("cover.controls.scale", "Scale"), 0.2, 3, 0.05],
        ["x", dyofT("cover.controls.horizontal", "Horizontal"), 0, 1, 0.01],
        ["y", dyofT("cover.controls.vertical", "Vertical"), 0, 1, 0.01],
        ["rotation", dyofT("cover.controls.rotate", "Rotate"), -180, 180, 1]
      ].map(function(setting) {
        return '<label>' + escapeHtml(setting[1]) + '<input type="range" min="' + setting[2] + '" max="' + setting[3] + '" step="' + setting[4] + '" value="' + object.transform[setting[0]] + '" data-cover-input data-cover-object="' + object.id + '" data-cover-side="' + side + '" data-cover-transform="' + setting[0] + '"></label>';
      }).join("") + "</fieldset>";
    }

    layerActionsHtml(side, object, count) {
      return '<div class="dyof__layer-actions"><button type="button" data-cover-move="backward" data-cover-object="' + object.id + '" data-cover-side="' + side + '"' + (object.order === 0 ? " disabled" : "") + '>' + escapeHtml(dyofT("cover.controls.move_back", "Move back")) + '</button><button type="button" data-cover-move="forward" data-cover-object="' + object.id + '" data-cover-side="' + side + '"' + (object.order === count - 1 ? " disabled" : "") + '>' + escapeHtml(dyofT("cover.controls.move_forward", "Move forward")) + '</button><button type="button" data-cover-remove data-cover-object="' + object.id + '" data-cover-side="' + side + '">' + escapeHtml(dyofT("cover.controls.remove", "Remove")) + '</button></div>';
    }

    async populateAssetThumbs(container) {
      var thumbs = container.querySelectorAll("[data-cover-asset-thumb]");
      for (var index = 0; index < thumbs.length; index += 1) {
        var record = await this.assets.get(thumbs[index].dataset.coverAssetThumb);
        if (record && record.previewUrl) {
          thumbs[index].innerHTML = '<img src="' + escapeHtml(record.previewUrl) + '" alt="' + escapeHtml(dyofT("cover.image.preview_alt", "Uploaded artwork preview")) + '">';
        }
      }
    }

    printResolutionAssets() {
      return this.assets ? this.assets.memory : null;
    }

    printResolutionContext(state, placements) {
      return {
        size: state.selection && state.selection.size,
        productType: state.journey && state.journey.productType,
        assets: this.printResolutionAssets(),
        placements: placements
      };
    }

    // Effective DPI at the real print size for one placement, optionally with an
    // uncommitted crop/size draft applied, so the message follows the gesture.
    photoResolutionEntry(state, placement, objectId, draft) {
      // A separately cached older contract asset must not break rendering; the
      // configuration save still enforces the stop cut-off.
      if (!this.contract || typeof this.contract.printResolution !== "function" || !this.assets) { return null; }
      var cover = state.cover;
      if (draft && placement === "background" && cover.background) {
        cover = Object.assign({}, cover, { background: Object.assign({}, cover.background, draft) });
      } else if (draft && placement !== "background") {
        cover = Object.assign({}, cover);
        cover[placement] = Object.assign({}, cover[placement], {
          objects: cover[placement].objects.map(function(object) {
            return object.id === objectId ? Object.assign({}, object, draft) : object;
          })
        });
      }
      return this.contract.printResolution(cover, this.printResolutionContext(state, [placement])).filter(function(entry) {
        return placement === "background" || entry.objectId === objectId;
      })[0] || null;
    }

    renderPhotoResolutionSlot(slot, entry) {
      if (!slot) { return; }
      var message = entry && entry.status !== "ok" ? entry.message : "";
      if (slot.textContent !== message) { slot.textContent = message; }
      slot.hidden = !message;
      slot.dataset.resolutionStatus = entry ? entry.status : "";
      slot.setAttribute("role", entry && entry.status === "stop" ? "alert" : "status");
      this.revealNewResolutionWarning(
        typeof slot.getAttribute === "function" ? slot.getAttribute("data-cover-resolution-slot") : "",
        entry,
        Boolean(message)
      );
    }

    // A refused upload (too small, too large, wrong type, upload failure) is
    // explained under the upload field that was used, never over the preview.
    uploadRefusalKey(purpose, side, replacementId, slotIndex) {
      if (purpose === "background") { return "background"; }
      return "image-" + (side || "front") + "-" + (replacementId ? replacementId : "slot" + (Number(slotIndex) || 0));
    }

    uploadRefusalHtml(key, extraClass) {
      var message = this.uploadRefusal && this.uploadRefusal.key === key ? this.uploadRefusal.message : "";
      return '<p class="dyof__quality-warning dyof__upload-refusal' + (extraClass ? " " + extraClass : "") + '" data-cover-upload-refusal="' + escapeHtml(key) + '" data-resolution-status="stop" role="alert"' + (message ? "" : " hidden") + '>' + escapeHtml(message) + '</p>';
    }

    syncUploadRefusalSlots() {
      if (!this.host || typeof this.host.querySelectorAll !== "function") { return; }
      Array.from(this.host.querySelectorAll("[data-cover-upload-refusal]")).forEach(function(slot) {
        var key = typeof slot.getAttribute === "function" ? slot.getAttribute("data-cover-upload-refusal") : "";
        var message = this.uploadRefusal && this.uploadRefusal.key === key ? this.uploadRefusal.message : "";
        if (slot.textContent !== message) { slot.textContent = message; }
        slot.hidden = !message;
      }, this);
    }

    clearUploadRefusal() {
      if (!this.uploadRefusal) { return; }
      this.uploadRefusal = null;
      this.syncUploadRefusalSlots();
    }

    showUploadRefusal(key, message) {
      this.uploadRefusal = { key: key, message: message };
      this.syncUploadRefusalSlots();
      // Clear the "Checking and uploading" progress shown over the preview.
      this.setStatus("", "idle");
      this.revealWhenPointerFree('[data-cover-upload-refusal="' + key + '"]');
    }

    // Scrolls an element into view once no drag is in progress. "nearest"
    // leaves an element that is already visible where it is.
    revealWhenPointerFree(selector) {
      var host = this.host;
      var reveal = function() {
        var element = host && host.querySelector && host.querySelector(selector);
        if (element && !element.hidden && typeof element.scrollIntoView === "function") {
          element.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      };
      if (this.pointerPressed) {
        this.pendingResolutionReveal = reveal;
        return;
      }
      if (typeof root.setTimeout === "function") {
        root.setTimeout(reveal, 0);
      } else {
        reveal();
      }
    }

    // Scrolls a low-resolution warning into view when it first appears for a
    // photo (not on every re-render, and only after a drag in progress ends).
    // "nearest" leaves a warning that is already visible where it is.
    revealNewResolutionWarning(slotKey, entry, shown) {
      if (!slotKey) { return; }
      var key = slotKey + "|" + (entry && entry.assetId || "");
      if (!this.shownResolutionWarnings) { this.shownResolutionWarnings = Object.create(null); }
      var wasShown = this.shownResolutionWarnings[key] === true;
      this.shownResolutionWarnings[key] = shown;
      if (!shown || wasShown) { return; }
      this.revealWhenPointerFree('[data-cover-resolution-slot="' + slotKey + '"]');
    }

    syncPhotoResolutionSlots(container, state, sideName) {
      if (!container || !state.cover || !state.cover[sideName]) { return; }
      state.cover[sideName].objects.forEach(function(object) {
        if (object.type !== "image") { return; }
        this.renderPhotoResolutionSlot(
          container.querySelector('[data-cover-resolution-slot="' + object.id + '"]'),
          this.photoResolutionEntry(state, sideName, object.id)
        );
      }, this);
    }

    syncBackgroundResolutionSlot(state, draft) {
      var container = this.elements.selection;
      var slot = container && container.querySelector && container.querySelector('[data-cover-resolution-slot="background"]');
      this.renderPhotoResolutionSlot(slot, this.photoResolutionEntry(state, "background", null, draft));
    }

    usesPhysicalUploadGeometry(state) {
      var background = state.cover && state.cover.background;
      return Boolean(background && background.source === "upload" && background.geometryVersion === 2);
    }

    backgroundCropAspect(state) {
      if (this.usesPhysicalUploadGeometry(state)) {
        var face = COVER_FACE_MM[resolvedCoverSize(state)];
        return 2 * face.width / face.height;
      }
      return resolvedCoverSize(state) === "personal"
        ? PERSONAL_FULL_WRAP_CROP_ASPECT
        : FULL_WRAP_CROP_ASPECT;
    }

    backgroundBaseCrop(record, aspectRatio) {
      var sourceAspect = record.width / record.height;
      if (sourceAspect >= aspectRatio) {
        return {
          width: record.height * aspectRatio,
          height: record.height
        };
      }
      return {
        width: record.width,
        height: record.width / aspectRatio
      };
    }

    backgroundCropData(record, crop, aspectRatio, geometryVersion) {
      var base = this.backgroundBaseCrop(record, aspectRatio);
      var zoom = clamp(crop && crop.zoom, 1, 8);
      var editorZoom = geometryVersion !== 2 && zoom === 1 ? 1.0072 : zoom;
      var width = base.width / editorZoom;
      var height = base.height / editorZoom;
      var centerX = clamp(crop && crop.x, width / record.width / 2, 1 - width / record.width / 2) * record.width;
      var centerY = clamp(crop && crop.y, height / record.height / 2, 1 - height / record.height / 2) * record.height;
      return {
        x: clamp(centerX - width / 2, 0, record.width - width),
        y: clamp(centerY - height / 2, 0, record.height - height),
        width: width,
        height: height
      };
    }

    backgroundCropFromDetail(detail, record, previous, aspectRatio) {
      var base = this.backgroundBaseCrop(record, aspectRatio);
      var width = clamp(Number(detail.width), 1, record.width);
      var height = clamp(Number(detail.height), 1, record.height);
      var zoom = clamp(Math.min(base.width / width, base.height / height), 1, 8);
      return {
        x: clamp((Number(detail.x) + width / 2) / record.width, 0, 1),
        y: clamp((Number(detail.y) + height / 2) / record.height, 0, 1),
        zoom: zoom,
        rotation: clamp(Number(previous && previous.rotation) || 0, -180, 180)
      };
    }

    backgroundCropWindow(record, crop, aspectRatio) {
      var base = this.backgroundBaseCrop(record, aspectRatio || FULL_WRAP_CROP_ASPECT);
      var zoom = clamp(crop && crop.zoom, 1, 8);
      var width = base.width / zoom;
      var height = base.height / zoom;
      var centerX = clamp(crop && crop.x, width / record.width / 2, 1 - width / record.width / 2) * record.width;
      var centerY = clamp(crop && crop.y, height / record.height / 2, 1 - height / record.height / 2) * record.height;
      return {
        x: clamp(centerX - width / 2, 0, record.width - width),
        y: clamp(centerY - height / 2, 0, record.height - height),
        width: width,
        height: height
      };
    }

    backgroundPreviewGeometry(record, crop, rect, sideName, aspectRatio) {
      var windowRect = this.backgroundCropWindow(record, crop, aspectRatio);
      var wrapWidth = rect.width * 2;
      var scaleX = wrapWidth / windowRect.width;
      var scaleY = rect.height / windowRect.height;
      var segmentStart = sideName === "front" ? rect.width : 0;
      return {
        x: rect.x - segmentStart - windowRect.x * scaleX,
        y: rect.y - windowRect.y * scaleY,
        width: record.width * scaleX,
        height: record.height * scaleY,
        rotationCenterX: rect.x - segmentStart + rect.width,
        rotationCenterY: rect.y + rect.height / 2,
        window: windowRect
      };
    }

    commitBackgroundCrop(entry) {
      if (!entry.ready || !entry.userEdited || !entry.detail || !entry.element.isConnected) { return; }
      var state = this.store.getState();
      var background = state.cover.background;
      if (!background || background.source !== "upload" || background.assetId !== entry.assetId) { return; }
      if (background.geometryVersion !== entry.geometryVersion || this.backgroundCropAspect(state) !== entry.aspectRatio) { return; }
      var next = this.backgroundCropFromDetail(entry.detail, entry.record, background.crop, entry.aspectRatio);
      entry.userEdited = false;
      if (JSON.stringify(next) === JSON.stringify(background.crop)) { return; }
      this.dispatch("COVER_UPDATE_BACKGROUND", { crop: next });
    }

    updateBackgroundCropProportions() {
      var background = this.store.getState().cover.background;
      if (!background || background.source !== "upload" || background.geometryVersion === 2) { return; }
      var crop = background.crop || {};
      this.dispatch("COVER_SET_BACKGROUND", Object.assign({}, background, {
        geometryVersion: 2,
        crop: {
          x: crop.x,
          y: crop.y,
          zoom: crop.zoom,
          rotation: crop.rotation
        }
      }));
      this.setStatus(dyofT("cover.upload.crop_updated", "Crop proportions updated. Check your image position before continuing."), "ready");
    }

    async initializeBackgroundCropEditor(container, state) {
      if (!root.Cropper || !container.isConnected) { return; }
      var background = state.cover.background;
      var element = container.querySelector("[data-cover-background-editor]");
      if (!background || background.source !== "upload" || !element) { return; }
      var record = await this.assets.get(background.assetId);
      if (!record || !record.previewUrl || !element.isConnected) { return; }
      var currentBackground = this.store.getState().cover.background;
      if (!currentBackground || currentBackground.assetId !== background.assetId) { return; }
      this.syncBackgroundResolutionSlot(this.store.getState());
      var image = document.createElement("img");
      image.src = record.previewUrl;
      image.alt = dyofT("cover.upload.full_artwork_alt", "Uploaded full cover artwork");
      element.appendChild(image);
      var aspectRatio = this.backgroundCropAspect(state);
      var entry = {
        element: element,
        assetId: background.assetId,
        record: record,
        aspectRatio: aspectRatio,
        geometryVersion: background.geometryVersion,
        cropper: null,
        detail: null,
        ready: false,
        userEdited: false,
        interacting: false,
        commitTimer: null,
        marginSwipe: null
      };
      // On phones a drag inside the frame moves the photo (cropMarginSwipeStart).
      var phoneLayout = isPhoneLayout();
      var cropper = new root.Cropper(image, {
        viewMode: 1,
        dragMode: "move",
        aspectRatio: aspectRatio,
        data: this.backgroundCropData(record, background.crop, aspectRatio, background.geometryVersion),
        responsive: true,
        restore: false,
        modal: true,
        guides: true,
        center: true,
        highlight: false,
        background: false,
        autoCrop: true,
        autoCropArea: 0.983,
        movable: true,
        rotatable: false,
        scalable: false,
        zoomable: true,
        zoomOnTouch: true,
        zoomOnWheel: true,
        wheelZoomRatio: 0.05,
        cropBoxMovable: !phoneLayout,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        minContainerWidth: 0,
        minContainerHeight: 0,
        ready: function() {
          entry.ready = true;
          element.classList.add("is-ready");
        },
        cropstart: function(event) {
          entry.marginSwipe = cropMarginSwipeStart(event);
          if (!entry.ready || entry.marginSwipe) { return; }
          entry.interacting = true;
          entry.userEdited = true;
          if (entry.commitTimer) {
            root.clearTimeout(entry.commitTimer);
            entry.commitTimer = null;
          }
        },
        cropmove: function(event) {
          if (!entry.marginSwipe) { return; }
          if (cropMarginSwipeHeld(entry.marginSwipe, event)) {
            event.preventDefault();
            return;
          }
          entry.marginSwipe = null;
          if (!entry.ready) { return; }
          entry.interacting = true;
          entry.userEdited = true;
          if (entry.commitTimer) {
            root.clearTimeout(entry.commitTimer);
            entry.commitTimer = null;
          }
        },
        crop: function(event) {
          entry.detail = event.detail;
          if (entry.ready && entry.userEdited) {
            var liveState = this.store.getState();
            var liveBackground = liveState.cover.background;
            if (liveBackground && liveBackground.assetId === entry.assetId) {
              this.syncBackgroundResolutionSlot(liveState, {
                crop: this.backgroundCropFromDetail(event.detail, record, liveBackground.crop, aspectRatio)
              });
            }
          }
          if (entry.ready && entry.userEdited && !entry.interacting) {
            if (entry.commitTimer) { root.clearTimeout(entry.commitTimer); }
            entry.commitTimer = root.setTimeout(function() {
              entry.commitTimer = null;
              this.commitBackgroundCrop(entry);
            }.bind(this), 180);
          }
        }.bind(this),
        zoom: function() {
          if (entry.ready) { entry.userEdited = true; }
        },
        cropend: function() {
          if (entry.marginSwipe) {
            entry.marginSwipe = null;
            return;
          }
          entry.interacting = false;
          this.commitBackgroundCrop(entry);
        }.bind(this)
      });
      entry.cropper = cropper;
      this.cropperEntries.push(entry);
    }

    destroyCroppers(container) {
      this.cropperEntries = this.cropperEntries.filter(function(entry) {
        var shouldDestroy = !container || container.contains(entry.element);
        if (!shouldDestroy) { return true; }
        if (entry.commitTimer) { root.clearTimeout(entry.commitTimer); }
        if (entry.frame) { root.cancelAnimationFrame(entry.frame); }
        if (entry.cropper) { entry.cropper.destroy(); }
        return false;
      });
    }

    cropFromDetail(detail, record, previous) {
      var width = clamp(Number(detail.width) / record.width, 0.01, 1);
      var height = clamp(Number(detail.height) / record.height, 0.01, 1);
      return {
        x: clamp((Number(detail.x) + Number(detail.width) / 2) / record.width, width / 2, 1 - width / 2),
        y: clamp((Number(detail.y) + Number(detail.height) / 2) / record.height, height / 2, 1 - height / 2),
        width: width,
        height: height,
        zoom: previous.zoom,
        rotation: previous.rotation
      };
    }

    applyCropDraft(entry) {
      entry.frame = null;
      if (!entry.draft) { return; }
      var state = this.store.getState();
      var object = state.cover[entry.side].objects.filter(function(item) { return item.id === entry.id; })[0];
      if (object && object.activeMask === entry.mask) {
        var draftCrops = Object.assign({}, object.crops);
        draftCrops[entry.mask] = entry.draft;
        this.renderPhotoResolutionSlot(
          this.host.querySelector('[data-cover-resolution-slot="' + entry.id + '"]'),
          this.photoResolutionEntry(state, entry.side, entry.id, { crops: draftCrops })
        );
      }
      var groups = this.host.querySelectorAll('[data-cover-preview-object="' + entry.id + '"]');
      if (!object || !groups.length) { return; }
      groups.forEach(function(group) {
        var image = group.querySelector("[data-cover-crop-image]");
        if (image) {
          this.applyImageCropGeometry(image, object, entry.record, entry.draft);
        }
      }, this);
    }

    imageCropKey(crop) {
      return JSON.stringify([crop.x, crop.y, crop.width, crop.height, crop.zoom, crop.rotation]);
    }

    commitCrop(entry) {
      if (!entry.draft || !entry.userEdited || !entry.element.isConnected) { return; }
      if (entry.commitTimer) {
        root.clearTimeout(entry.commitTimer);
        entry.commitTimer = null;
      }
      var state = this.store.getState();
      var object = state.cover[entry.side].objects.filter(function(item) { return item.id === entry.id; })[0];
      if (!object || object.assetId !== entry.record.id || object.activeMask !== entry.mask) { return; }
      var crops = Object.assign({}, object.crops);
      var current = crops[object.activeMask];
      var next = entry.draft;
      entry.userEdited = false;
      if (this.imageCropKey(current) === this.imageCropKey(next)) { return; }
      crops[object.activeMask] = next;
      entry.committedCrop = this.imageCropKey(next);
      this.dispatch("COVER_UPDATE_OBJECT", {
        side: entry.side,
        id: entry.id,
        patch: { crops: crops },
        clearSelection: true
      });
    }

    scheduleCropCommit(entry) {
      if (entry.interacting || !entry.ready) { return; }
      if (entry.commitTimer) { root.clearTimeout(entry.commitTimer); }
      entry.commitTimer = root.setTimeout(function() {
        this.commitCrop(entry);
      }.bind(this), 240);
    }

    async initializeCropEditors(container, state, sideName) {
      if (!root.Cropper || !container.isConnected) { return; }
      var editors = container.querySelectorAll("[data-cover-crop-editor]");
      for (let index = 0; index < editors.length; index += 1) {
        let element = editors[index];
        let id = element.dataset.coverCropEditor;
        let object = state.cover[sideName].objects.filter(function(item) { return item.id === id; })[0];
        let image = element.querySelector("img");
        let record = object && await this.assets.get(object.assetId);
        if (!object || !record || !image || !element.isConnected) { continue; }
        if (this.cropperEntries.some(function(candidate) { return candidate.element === element; })) { continue; }
        let crop = object.crops[object.activeMask];
        var data = crop.width && crop.height ? {
          x: (crop.x - crop.width / 2) * record.width,
          y: (crop.y - crop.height / 2) * record.height,
          width: crop.width * record.width,
          height: crop.height * record.height
        } : object.activeMask === "original" ? {
          x: 0,
          y: 0,
          width: record.width,
          height: record.height
        } : undefined;
        let entry = {
          element: element,
          side: sideName,
          id: id,
          record: record,
          mask: object.activeMask,
          committedCrop: this.imageCropKey(crop),
          userEdited: false,
          cropper: null,
          draft: null,
          ready: false,
          interacting: false,
          commitTimer: null,
          frame: null,
          marginSwipe: null
        };
        // On phones a drag inside the frame moves the photo (cropMarginSwipeStart).
        var phoneLayout = isPhoneLayout();
        var cropper = new root.Cropper(image, {
          viewMode: 1,
          dragMode: "move",
          aspectRatio: object.activeMask === "original" ? NaN : 1,
          data: data,
          responsive: true,
          restore: false,
          modal: true,
          guides: true,
          center: true,
          highlight: false,
          background: false,
          autoCrop: true,
          autoCropArea: object.activeMask === "original" ? 1 : 0.72,
          movable: true,
          rotatable: false,
          scalable: false,
          zoomable: true,
          zoomOnTouch: true,
          zoomOnWheel: true,
          wheelZoomRatio: 0.06,
          cropBoxMovable: !phoneLayout,
          cropBoxResizable: true,
          toggleDragModeOnDblclick: false,
          minContainerWidth: 0,
          minContainerHeight: 0,
          ready: function() {
            entry.ready = true;
            element.classList.toggle("dyof__crop-editor--circle", object.activeMask === "circle");
          },
          cropstart: function(event) {
            entry.marginSwipe = cropMarginSwipeStart(event);
            if (entry.marginSwipe) { return; }
            entry.interacting = true;
            entry.userEdited = true;
            if (entry.commitTimer) {
              root.clearTimeout(entry.commitTimer);
              entry.commitTimer = null;
            }
          },
          cropmove: function(event) {
            if (!entry.marginSwipe) { return; }
            if (cropMarginSwipeHeld(entry.marginSwipe, event)) {
              event.preventDefault();
              return;
            }
            entry.marginSwipe = null;
            entry.interacting = true;
            entry.userEdited = true;
            if (entry.commitTimer) {
              root.clearTimeout(entry.commitTimer);
              entry.commitTimer = null;
            }
          },
          crop: function(event) {
            entry.draft = this.cropFromDetail(event.detail, record, crop);
            if (!entry.frame) {
              entry.frame = root.requestAnimationFrame(function() { this.applyCropDraft(entry); }.bind(this));
            }
            this.scheduleCropCommit(entry);
          }.bind(this),
          zoom: function() {
            if (entry.ready) { entry.userEdited = true; }
          },
          cropend: function() {
            if (entry.marginSwipe) {
              entry.marginSwipe = null;
              return;
            }
            entry.interacting = false;
            this.commitCrop(entry);
          }.bind(this)
        });
        entry.cropper = cropper;
        this.cropperEntries.push(entry);
      }
    }

    onClick(event) {
      var clickedCustomColour = this.customColourInputFromTarget(event.target);
      if (this.customColourDraft && this.customColourDraft.target !== clickedCustomColour) {
        this.commitCustomColourDraft();
      }
      var tooltipTrigger = event.target.closest("[data-cover-tooltip-trigger]");
      if (tooltipTrigger && this.host.contains(tooltipTrigger)) {
        event.preventDefault();
        var tooltipName = tooltipTrigger.dataset.coverTooltipTrigger;
        var closingTooltip = this.coverTooltipDisclosure === tooltipName;
        this.coverTooltipDisclosure = closingTooltip ? null : tooltipName;
        this.coverTooltipHover = null;
        this.coverTooltipFocusSuppressed = closingTooltip ? tooltipName : null;
        this.syncCoverTooltips();
        return true;
      }
      if (this.sizeTooltipOpen || this.uploadTooltipOpen) {
        this.coverTooltipFocusSuppressed = this.coverTooltipFocus;
        this.coverTooltipDisclosure = null;
        this.coverTooltipHover = null;
        this.syncCoverTooltips();
      }
      var target = event.target.closest("[data-cover-menu-clear],[data-cover-menu-toggle],[data-cover-menu-option],[data-cover-size],[data-cover-source],[data-cover-design],[data-cover-text-block-toggle],[data-cover-add-text],[data-cover-elastic],[data-cover-line-colour],[data-cover-shape-colour],[data-cover-shape],[data-cover-mask],[data-cover-move],[data-cover-remove],[data-cover-remove-background],[data-cover-update-crop-proportions],[data-cover-overflow-accept],[data-cover-overflow-revise]");
      if (!target || !this.host.contains(target)) {
        if (this.openSelectionMenu) {
          this.openSelectionMenu = null;
          this.renderSelection(this.store.getState());
        }
        return false;
      }
      if (target.dataset.coverUpdateCropProportions !== undefined) {
        event.preventDefault();
        this.updateBackgroundCropProportions();
      } else if (target.dataset.coverMenuClear) {
        event.preventDefault();
        event.stopPropagation();
        var clearedFilter = target.dataset.coverMenuClear;
        var clearPatch = {};
        clearPatch[clearedFilter] = "all";
        this.openSelectionMenu = null;
        this.dispatch("COVER_SET_FILTERS", clearPatch);
      } else if (target.dataset.coverMenuToggle) {
        this.openSelectionMenu = this.openSelectionMenu === target.dataset.coverMenuToggle ? null : target.dataset.coverMenuToggle;
        this.renderSelection(this.store.getState());
      } else if (target.dataset.coverMenuOption) {
        var menuName = target.dataset.coverMenuOption;
        var menuValue = target.dataset.coverMenuValue;
        this.openSelectionMenu = null;
        if (menuName === "source") {
          this.didChooseSource = true;
          this.dispatch("COVER_SET_SOURCE", menuValue);
        } else {
          var filterState = this.store.getState().cover.filters[menuName];
          var filterPatch = {};
          filterPatch[menuName] = filterState === menuValue ? "all" : menuValue;
          this.dispatch("COVER_SET_FILTERS", filterPatch);
        }
      } else if (target.dataset.coverSize) {
        var currentSize = this.store.getState().selection.size;
        this.didChooseSize = true;
        this.dispatch("SET_COVER_SIZE", target.dataset.coverSize);
        if (currentSize !== target.dataset.coverSize) {
          this.dispatch("COVER_SET_FILTERS", { theme: "all", colour: "all" });
          this.applyDesignPaletteToPristineObjects(this.store.getState());
        }
      } else if (target.dataset.coverSource) {
        this.didChooseSource = true;
        this.dispatch("COVER_SET_SOURCE", target.dataset.coverSource);
      } else if (target.dataset.coverDesign) {
        var item = this.findCatalogue(target.dataset.coverDesign);
        if (item) {
          this.didChooseSource = true;
          this.didChooseDesign = true;
          var cards = target.parentNode && target.parentNode.querySelectorAll
            ? target.parentNode.querySelectorAll("[data-cover-design]")
            : [];
          Array.from(cards).forEach(function(card) {
            card.setAttribute("aria-pressed", String(card === target));
          });
          this.dispatch("COVER_SET_BACKGROUND", { source: "catalogue", designId: item.id, title: item.title });
          this.applyDesignPaletteToPristineObjects(this.store.getState());
        }
      } else if (target.dataset.coverTextBlockToggle) {
        var blockSide = target.dataset.coverSide;
        var blockId = target.dataset.coverTextBlockToggle;
        this.expandedTextBlocks[blockSide] = this.expandedTextBlocks[blockSide] === blockId ? "" : blockId;
        this.renderSide(this.store.getState(), blockSide);
        var nextBlockToggle = Array.from(this.elements[blockSide].querySelectorAll("[data-cover-text-block-toggle]")).filter(function(button) {
          return button.dataset.coverTextBlockToggle === blockId;
        })[0];
        if (nextBlockToggle && nextBlockToggle.focus) {
          nextBlockToggle.focus({ preventScroll: true });
        }
        return true;
      } else if (target.dataset.coverAddText) {
        var addTextSide = target.dataset.coverAddText;
        var existingTextIds = new Set(this.store.getState().cover[addTextSide].objects.map(function(object) {
          return object.id;
        }));
        this.pendingTextBlockExpansionSide = addTextSide;
        this.expandedTextBlocks[addTextSide] = "";
        this.dispatch("COVER_ADD_TEXT", {
          side: addTextSide,
          object: this.defaultTextObjectInput(this.store.getState())
        });
        var addedTextObject = this.store.getState().cover[addTextSide].objects.filter(function(object) {
          return object.type === "text" && !existingTextIds.has(object.id);
        })[0];
        this.rememberAutomaticPaletteObject(addTextSide, addedTextObject);
        if (addedTextObject) {
          var addedTextInput = Array.from(this.elements[addTextSide].querySelectorAll('input[data-cover-line-prop="text"]')).filter(function(input) {
            return input.dataset.coverObject === addedTextObject.id && input.dataset.coverLine === "0";
          })[0];
          if (addedTextInput && addedTextInput.focus) {
            addedTextInput.focus();
          }
        }
      } else if (target.dataset.coverElastic) {
        this.dispatch("COVER_SET_ELASTIC", { enabled: true, colourId: target.dataset.coverElastic });
      } else if (target.dataset.coverLineColour) {
        var colourState = this.store.getState();
        var colourObject = colourState.cover[target.dataset.coverSide].objects.filter(function(item) { return item.id === target.dataset.coverObject; })[0];
        if (colourObject) {
          var colourLines = colourObject.lines.map(function(line) { return Object.assign({}, line); });
          var colourLineIndex = Number(target.dataset.coverLine);
          this.forgetAutomaticPaletteColour(
            target.dataset.coverSide,
            target.dataset.coverObject,
            "line:" + colourLineIndex
          );
          colourLines[colourLineIndex].colour = target.dataset.coverLineColour;
          this.updateObject(target, { lines: colourLines });
        }
      } else if (target.dataset.coverShapeColour) {
        this.forgetAutomaticPaletteColour(
          target.dataset.coverSide,
          target.dataset.coverObject,
          "shape"
        );
        this.updateObject(target, { shapeColour: target.dataset.coverShapeColour });
      } else if (target.dataset.coverShape) {
        var shapeState = this.store.getState();
        var shapeSide = shapeState.cover[target.dataset.coverSide];
        var shapeObject = shapeSide && shapeSide.objects.filter(function(item) {
          return item.id === target.dataset.coverObject;
        })[0];
        var disableShape = Boolean(
          shapeObject && shapeObject.shape === target.dataset.coverShape
        );
        var shapePatch = {
          shape: disableShape ? "none" : target.dataset.coverShape
        };
        if (disableShape) {
          shapePatch.overflowWarningFor = null;
          shapePatch.overflowAcceptedFor = null;
        }
        this.updateObject(target, shapePatch);
      } else if (target.dataset.coverMask) {
        this.updateObject(target, { activeMask: target.dataset.coverMask });
      } else if (target.dataset.coverOverflowAccept) {
        this.updateObject(target, { overflowAcceptedFor: target.dataset.coverOverflowAccept });
      } else if (own(target.dataset, "coverOverflowRevise")) {
        this.updateObject(target, { overflowAcceptedFor: null });
        root.setTimeout(function() {
          var block = this.host.querySelector('[data-text-block="' + target.dataset.coverObject + '"]');
          var filledInputs = block && Array.from(block.querySelectorAll(".dyof__text-input")).filter(function(input) {
            return Boolean(input.value.trim());
          });
          var input = filledInputs && filledInputs[0] || block && block.querySelector(".dyof__text-input");
          if (block) {
            block.classList.add("is-adjusting");
            if (block.scrollIntoView) {
              block.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            root.setTimeout(function() { block.classList.remove("is-adjusting"); }, 1200);
          }
          if (input) {
            input.focus();
            input.select();
          }
        }.bind(this), 0);
      } else if (target.dataset.coverMove) {
        this.dispatch("COVER_MOVE_OBJECT", { side: target.dataset.coverSide, id: target.dataset.coverObject, direction: target.dataset.coverMove });
      } else if (own(target.dataset, "coverRemove")) {
        var removingSummaryBlock = target.classList && target.classList.contains("dyof__text-block-remove");
        this.dispatch("COVER_REMOVE_OBJECT", { side: target.dataset.coverSide, id: target.dataset.coverObject });
        if (removingSummaryBlock) {
          var removeContainer = this.elements[target.dataset.coverSide];
          var nextRemoveControl = removeContainer && (removeContainer.querySelector('.dyof__text-block-remove')
            || removeContainer.querySelector('[data-cover-add-text]'));
          if (nextRemoveControl && nextRemoveControl.focus) { nextRemoveControl.focus({ preventScroll: true }); }
        }
      } else if (own(target.dataset, "coverRemoveBackground")) {
        this.dispatch("COVER_SET_BACKGROUND", null);
      }
      var activeAfterAction = this.store.getState().cover.activeObject;
      if (activeAfterAction && activeAfterAction.id) {
        this.dispatch("COVER_SELECT_OBJECT", { side: activeAfterAction.side, id: null });
      }
      return true;
    }

    updateRangeFill(target) {
      var minimum = Number(target.min);
      var maximum = Number(target.max);
      var percent = clamp((Number(target.value) - minimum) / (maximum - minimum) * 100, 0, 100);
      target.style.setProperty("--dyof-range-fill", percent + "%");
    }

    rangePatch(target, object) {
      var patch = {};
      if (own(target.dataset, "coverShapeScale")) {
        patch.shapeScale = Number(target.value);
      } else if (target.dataset.coverTransform) {
        patch.transform = Object.assign({}, object.transform);
        patch.transform[target.dataset.coverTransform] = Number(target.value);
      } else if (target.dataset.coverCrop) {
        patch.crops = Object.assign({}, object.crops);
        patch.crops[object.activeMask] = Object.assign({}, patch.crops[object.activeMask]);
        patch.crops[object.activeMask][target.dataset.coverCrop] = Number(target.value);
      }
      return patch;
    }

    stageRangeDraft(target) {
      var state = this.store.getState();
      var side = target.dataset.coverSide;
      var object = state.cover[side] && state.cover[side].objects.filter(function(item) {
        return item.id === target.dataset.coverObject;
      })[0];
      if (!object) { return false; }
      this.rangeLastCommit = null;
      this.rangeDraft = {
        target: target,
        side: side,
        id: object.id,
        patch: this.rangePatch(target, object)
      };
      if (!this.rangePreviewFrame) {
        this.rangePreviewFrame = root.requestAnimationFrame(function() {
          this.rangePreviewFrame = 0;
          this.applyRangeDraftPreview();
        }.bind(this));
      }
      return true;
    }

    applyRangeDraftPreview() {
      var draft = this.rangeDraft;
      if (!draft) { return; }
      var state = this.store.getState();
      var object = state.cover[draft.side] && state.cover[draft.side].objects.filter(function(item) {
        return item.id === draft.id;
      })[0];
      if (!object) { return; }
      var previewObject = Object.assign({}, object, draft.patch);
      if (previewObject.type === "image") {
        this.renderPhotoResolutionSlot(
          this.host.querySelector('[data-cover-resolution-slot="' + draft.id + '"]'),
          this.photoResolutionEntry(state, draft.side, draft.id, draft.patch)
        );
      }
      var safe = this.safeRect(state, draft.side);
      var boundsWarpY = this.previewFaceWarpY(state, draft.side);
      var displayWarpY = this.previewObjectWarpY(state, draft.side);
      var groups = this.host.querySelectorAll('[data-cover-preview-object="' + draft.id + '"]');
      groups.forEach(function(group) {
        var x = safe.x + previewObject.transform.x * safe.width;
        var y = safe.y + previewObject.transform.y * safe.height;
        var transformScale = previewObject.type === "text" ? 1 : previewObject.transform.scale;
        group.setAttribute("transform", this.objectTransformValue(x, y, previewObject.transform.rotation, transformScale, displayWarpY));
        if (own(draft.patch, "shapeScale") && previewObject.type === "text") {
          while (group.firstChild) { group.removeChild(group.firstChild); }
          group.removeAttribute("data-cover-box-width");
          group.removeAttribute("data-cover-box-height");
          group.removeAttribute("data-cover-overflow-signature");
          this.appendTextObject(group, previewObject, safe, boundsWarpY);
        }
      }, this);
    }

    commitRangeDraft(target) {
      var draft = this.rangeDraft;
      if (!draft || (target && draft.target !== target)) { return false; }
      if (this.rangePreviewFrame) {
        root.cancelAnimationFrame(this.rangePreviewFrame);
        this.rangePreviewFrame = 0;
        this.applyRangeDraftPreview();
      }
      this.rangeLastCommit = { target: draft.target, value: String(draft.target.value) };
      try {
        this.updateObject(draft.target, draft.patch);
      } finally {
        if (this.rangeDraft === draft) {
          this.rangeDraft = null;
        }
        if (this.rangePointer && this.rangePointer.target === draft.target) {
          this.rangePointer = null;
        }
      }
      return true;
    }

    customColourInputFromTarget(target) {
      var selector = "[data-cover-custom-line-colour],[data-cover-custom-shape-colour]";
      if (!target) { return null; }
      if (target.matches && target.matches(selector)) { return target; }
      var trigger = target.closest && target.closest("[data-cover-custom-colour-trigger]");
      return trigger && trigger.querySelector ? trigger.querySelector(selector) : null;
    }

    stageCustomColourDraft(target) {
      var value = normalizeHexColour(target && target.value);
      if (!target || !value) { return false; }
      if (this.customColourDraft && this.customColourDraft.target !== target) {
        this.commitCustomColourDraft();
      }
      var state = this.store.getState();
      var side = target.dataset.coverSide;
      var object = state.cover[side] && state.cover[side].objects.filter(function(item) {
        return item.id === target.dataset.coverObject;
      })[0];
      if (!object || object.type !== "text") { return false; }
      var kind = target.matches("[data-cover-custom-line-colour]") ? "line" : "shape";
      var lineIndex = kind === "line" ? Number(target.dataset.coverLine) : null;
      if (kind === "line" && (!Number.isInteger(lineIndex) || !object.lines[lineIndex])) {
        return false;
      }
      if (!this.customColourDraft) {
        this.customColourDraft = {
          target: target,
          side: side,
          id: object.id,
          kind: kind,
          lineIndex: lineIndex,
          initialValue: normalizeHexColour(kind === "line" ? object.lines[lineIndex].colour : object.shapeColour),
          value: value
        };
      } else {
        this.customColourDraft.value = value;
      }
      this.applyCustomColourDraftPreview();
      return true;
    }

    applyCustomColourDraftPreview() {
      var draft = this.customColourDraft;
      if (!draft || !this.host || !this.host.querySelectorAll) { return; }
      var row = draft.target.closest && draft.target.closest(".dyof__figma-colour-row");
      var presetSelector = draft.kind === "line" ? "[data-cover-line-colour]" : "[data-cover-shape-colour]";
      var hasMatchingPreset = false;
      if (row && row.querySelectorAll) {
        row.querySelectorAll(presetSelector).forEach(function(button) {
          var preset = normalizeHexColour(draft.kind === "line"
            ? button.dataset.coverLineColour
            : button.dataset.coverShapeColour);
          var selected = preset === draft.value;
          hasMatchingPreset = hasMatchingPreset || selected;
          button.setAttribute("aria-pressed", String(selected));
        });
      }
      var trigger = draft.target.closest && draft.target.closest("[data-cover-custom-colour-trigger]");
      if (trigger && trigger.classList) {
        trigger.classList.toggle("is-selected", !hasMatchingPreset);
      }
      var groups = this.host.querySelectorAll('[data-cover-preview-object="' + draft.id + '"]');
      groups.forEach(function(group) {
        var selector = draft.kind === "line"
          ? '[data-cover-text-line="' + draft.lineIndex + '"]'
          : "[data-cover-text-shape]";
        var painted = group.querySelectorAll ? group.querySelectorAll(selector) : [];
        painted.forEach(function(element) {
          element.setAttribute("fill", draft.value);
        });
      });
    }

    commitCustomColourDraft(target) {
      var draft = this.customColourDraft;
      if (!draft || (target && draft.target !== target)) { return false; }
      this.customColourDraft = null;
      if (draft.value === draft.initialValue) { return true; }
      var state = this.store.getState();
      var object = state.cover[draft.side] && state.cover[draft.side].objects.filter(function(item) {
        return item.id === draft.id;
      })[0];
      if (!object || object.type !== "text") { return false; }
      var patch;
      if (draft.kind === "line") {
        var lines = object.lines.map(function(line) { return Object.assign({}, line); });
        if (!lines[draft.lineIndex]) { return false; }
        this.forgetAutomaticPaletteColour(draft.side, draft.id, "line:" + draft.lineIndex);
        lines[draft.lineIndex].colour = draft.value;
        patch = { lines: lines };
      } else {
        this.forgetAutomaticPaletteColour(draft.side, draft.id, "shape");
        patch = { shapeColour: draft.value };
      }
      this.customColourCommitSide = draft.side;
      try {
        this.updateObject(draft.target, patch);
      } finally {
        this.customColourCommitSide = null;
      }
      return true;
    }

    onInput(event) {
      var target = event.target;
      if (target.matches("[data-cover-custom-line-colour],[data-cover-custom-shape-colour]")) {
        this.stageCustomColourDraft(target);
        return true;
      }
      if (target.matches("[data-cover-background-crop]")) {
        var state = this.store.getState();
        var crop = Object.assign({}, state.cover.background && state.cover.background.crop || {});
        crop[target.dataset.coverBackgroundCrop] = Number(target.value);
        this.dispatch("COVER_UPDATE_BACKGROUND", { crop: crop });
        return true;
      }
      if (!target.matches("[data-cover-input]")) { return false; }
      if (target.type === "range") {
        this.updateRangeFill(target);
        this.stageRangeDraft(target);
        return true;
      }
      var state = this.store.getState();
      var side = target.dataset.coverSide;
      var object = state.cover[side].objects.filter(function(item) { return item.id === target.dataset.coverObject; })[0];
      if (!object) { return true; }
      var patch = {};
      if (target.dataset.coverLine != null) {
        var lineIndex = Number(target.dataset.coverLine);
        var lineProperty = target.dataset.coverLineProp;
        var lines = object.lines.map(function(line) { return Object.assign({}, line); });
        var value = lineProperty === "fontSize" ? Number(target.value) : target.value;
        if (lineProperty === "text") {
          value = this.printableLineText(event, lines[lineIndex]);
        }
        if (lineProperty === "fontId") {
          if (!this.acceptPrintableFont(target, lines[lineIndex], value)) {
            return true;
          }
          this.applyFontDraftPreview(target, value);
          if (object.lines[lineIndex] && object.lines[lineIndex].fontId === value) {
            return true;
          }
        }
        lines[lineIndex][lineProperty] = value;
        patch.lines = lines;
      } else if (target.dataset.coverProp) {
        patch[target.dataset.coverProp] = target.value;
      }
      this.updateObject(target, patch);
      return true;
    }

    onChange(event) {
      var target = event.target;
      if (target.matches('[data-cover-input][type="range"]')) {
        if (this.rangeLastCommit
          && this.rangeLastCommit.target === target
          && this.rangeLastCommit.value === String(target.value)) {
          this.rangeLastCommit = null;
          return true;
        }
        if (!this.rangeDraft) {
          this.stageRangeDraft(target);
        }
        this.commitRangeDraft(target);
        return true;
      }
      if (target.matches("[data-cover-custom-line-colour]")) {
        this.stageCustomColourDraft(target);
        return true;
      }
      if (target.matches("[data-cover-custom-shape-colour]")) {
        this.stageCustomColourDraft(target);
        return true;
      }
      if (target.matches("[data-cover-source-select]")) {
        this.didChooseSource = true;
        this.dispatch("COVER_SET_SOURCE", target.value);
        return true;
      }
      if (target.matches("[data-cover-filter]")) {
        var patch = {};
        patch[target.dataset.coverFilter] = target.value;
        this.dispatch("COVER_SET_FILTERS", patch);
        return true;
      }
      if (target.matches("[data-cover-elastic-disabled]")) {
        this.dispatch("COVER_SET_ELASTIC", target.checked
          ? { enabled: false, colourId: null }
          : { enabled: true, colourId: "navy" });
        return true;
      }
      if (target.matches("[data-cover-text-disabled]")) {
        this.dispatch("COVER_SET_TEXT_ENABLED", { side: target.dataset.coverTextDisabled, enabled: !target.checked });
        return true;
      }
      if (target.matches("[data-cover-elastic-enabled]")) {
        this.dispatch("COVER_SET_ELASTIC", target.checked
          ? { enabled: true, colourId: "navy" }
          : { enabled: false, colourId: null });
        return true;
      }
      if (target.matches("[data-cover-text-enabled]")) {
        this.dispatch("COVER_SET_TEXT_ENABLED", { side: target.dataset.coverTextEnabled, enabled: target.checked });
        return true;
      }
      if (target.matches("[data-cover-file]")) {
        var selectedFile = target.files && target.files[0] ? target.files[0] : null;
        /* Reset immediately so choosing the same file after a validation or
           network failure still emits a fresh change event. */
        target.value = "";
        if (selectedFile) {
          this.processFile(selectedFile, target.dataset.coverFile, target.dataset.coverSide || "front", null, target.dataset.coverSlot);
        }
        return true;
      }
      return target.matches("[data-cover-input]") ? this.onInput(event) : false;
    }

    updateObject(target, patch) {
      this.dispatch("COVER_UPDATE_OBJECT", {
        side: target.dataset.coverSide,
        id: target.dataset.coverObject,
        patch: patch,
        clearSelection: true
      });
    }

    printableLineText(event, line) {
      var printable = root && root.DYOFPrintableText;
      var target = event.target;
      if (!printable || !line || event.isComposing) { return target.value; }
      return printable.sanitizeInput(target, printable.coverLineFontIds(line.fontId, this.host && this.host.storeProfile));
    }

    // Refuse a font that cannot print the line; otherwise quietly convert
    // punctuation and spaces the new font lacks.
    acceptPrintableFont(target, line, fontId) {
      var printable = root && root.DYOFPrintableText;
      var fontIds = printable && line && line.fontId !== fontId ? printable.coverLineFontIds(fontId, this.host && this.host.storeProfile) : null;
      if (!fontIds) { return true; }
      var text = printable.normalizeText(line.text, fontIds);
      var unsupported = printable.unsupportedCharacters(text, fontIds);
      if (unsupported.length) {
        target.value = line.fontId;
        var option = FONT_OPTIONS.filter(function(font) { return font.id === fontId; })[0];
        printable.showNotice(target, printable.unsupportedMessage(unsupported, dyofT("cover.text.font_field", "the {font} font", { font: option ? fontDisplayLabel(option.id) : fontId })));
        return false;
      }
      printable.clearNotice(target);
      line.text = text;
      return true;
    }

    applyFontDraftPreview(target, fontId) {
      if (!target || !this.host || !this.host.querySelectorAll) { return; }
      var objectId = target.dataset.coverObject;
      var lineIndex = Number(target.dataset.coverLine);
      if (!objectId || !Number.isInteger(lineIndex)) { return; }
      var family = this.fontFamily(fontId);
      var groups = this.host.querySelectorAll('[data-cover-preview-object="' + objectId + '"]');
      groups.forEach(function(group) {
        var lines = group.querySelectorAll
          ? group.querySelectorAll('[data-cover-text-line="' + lineIndex + '"]')
          : [];
        lines.forEach(function(line) {
          line.setAttribute("font-family", family);
        });
      });
    }

    clearSelectionFromEvent(event) {
      if (event.target.closest && event.target.closest("[data-cover-preview-object],[data-cover-rotate],[data-cover-custom-colour-trigger],[data-cover-custom-line-colour],[data-cover-custom-shape-colour],[data-cover-input],[data-dyof-cover-side] input,[data-dyof-cover-side] select,[data-dyof-cover-side] textarea,[data-dyof-cover-side] label")) {
        return;
      }
      var state = this.store.getState();
      var active = state.cover.activeObject;
      if (active && active.id) {
        queueMicrotask(function() {
          this.dispatch("COVER_SELECT_OBJECT", { side: active.side, id: null });
        }.bind(this));
      }
    }

    onDragOver(event) {
      var zone = event.target.closest && event.target.closest("[data-cover-drop]");
      if (!zone || !this.host.contains(zone)) { return; }
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "copy";
      }
      zone.classList.add("is-dragover");
    }

    onDragLeave(event) {
      var zone = event.target.closest && event.target.closest("[data-cover-drop]");
      if (!zone || !this.host.contains(zone)) { return; }
      if (event.relatedTarget && zone.contains(event.relatedTarget)) { return; }
      zone.classList.remove("is-dragover");
    }

    onDrop(event) {
      var zone = event.target.closest && event.target.closest("[data-cover-drop]");
      if (!zone || !this.host.contains(zone)) { return; }
      event.preventDefault();
      zone.classList.remove("is-dragover");
      var file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (!file) {
        this.setStatus(dyofT("cover.upload.drop", "Drop a JPG or PNG file here."), "error");
        return;
      }
      this.processFile(file, zone.dataset.coverDrop, zone.dataset.coverSide || "front", zone.dataset.coverObject || null, zone.dataset.coverSlot);
    }

    renderedCoverRect(state, sideName) {
      return this.coverRect(state, sideName);
    }

    safeRect(state, sideName) {
      var rect = this.renderedCoverRect(state, sideName);
      var size = resolvedCoverSize(state);
      var face = COVER_FACE_MM[size];
      var outerX = SAFE_EDGE_MM / face.width;
      var bindingX = (BINDING_ZONE_MM[size] + SAFE_EDGE_MM) / face.width;
      var vertical = SAFE_EDGE_MM / face.height;
      var planner = Boolean(
        state.journey && state.journey.productType === "planner"
      );
      var left = planner ? outerX : sideName === "back" ? outerX : bindingX;
      var right = planner ? outerX : sideName === "back" ? bindingX : outerX;
      return {
        x: rect.x + rect.width * left,
        y: rect.y + rect.height * vertical,
        width: rect.width * (1 - left - right),
        height: rect.height * (1 - vertical * 2)
      };
    }

    previewFaceWarpY(state, sideName) {
      var rect = this.renderedCoverRect(state, sideName || "front");
      var size = resolvedCoverSize(state);
      var face = COVER_FACE_MM[size];
      return rect.height * face.width / Math.max(1, rect.width * face.height);
    }

    previewObjectWarpY(state, sideName) {
      if (this.usesPhysicalUploadGeometry(state)) {
        return this.previewFaceWarpY(state, sideName);
      }
      // The lifestyle shell remains a photographed, non-uniform projection.
      // Customer objects are drawn orthographically on every product, so a
      // circle or square looks like the true circle or square written to the
      // PDF (stretched with the Clipbook photo, circles read as ovals).
      // previewFaceWarpY is still used for physical bounds and clamping.
      return 1;
    }

    productProjection(state, sideName) {
      var rect = this.renderedCoverRect(state, sideName);
      var scaleX = this.usesPhysicalUploadGeometry(state) ? this.previewFaceWarpY(state, sideName) : 1;
      return { scaleX: scaleX, translateX: (rect.x + rect.width / 2) * (1 - scaleX) };
    }

    productProjectionTransform(state, sideName) {
      var projection = this.productProjection(state, sideName);
      return "matrix(" + projection.scaleX + " 0 0 1 " + projection.translateX + " 0)";
    }

    appendProjectedProduct(svg, child, state, sideName) {
      if (!this.usesPhysicalUploadGeometry(state)) {
        svg.appendChild(child);
        return;
      }
      var group = svgElement("g", {
        "data-cover-product-projection": "2",
        transform: this.productProjectionTransform(state, sideName)
      });
      group.appendChild(child);
      svg.appendChild(group);
    }

    backgroundRotationTransform(state, sideName, geometry, rotation) {
      if (!this.usesPhysicalUploadGeometry(state)) {
        return "rotate(" + rotation + " " + geometry.rotationCenterX + " " + geometry.rotationCenterY + ")";
      }
      var factor = this.productProjection(state, sideName).scaleX;
      var radians = Number(rotation) * Math.PI / 180;
      var cosine = Math.cos(radians);
      var b = Math.sin(radians) * factor;
      var c = -Math.sin(radians) / factor;
      var x = geometry.rotationCenterX;
      var y = geometry.rotationCenterY;
      return "matrix(" + cosine + " " + b + " " + c + " " + cosine + " " + (x - cosine * x - c * y) + " " + (y - b * x - cosine * y) + ")";
    }

    objectTransformValue(x, y, rotation, scale, warpY) {
      var normalizedWarp = Number(warpY) || 1;
      if (Math.abs(normalizedWarp - 1) < 0.000001) {
        return "translate(" + x + " " + y + ") rotate(" + rotation + ") scale(" + scale + ")";
      }
      var radians = Number(rotation) * Math.PI / 180;
      var cosine = Math.cos(radians) * scale;
      var sine = Math.sin(radians) * scale;
      return "matrix(" + cosine + " " + (sine * normalizedWarp) + " " + (-sine) + " " + (cosine * normalizedWarp) + " " + x + " " + y + ")";
    }

    objectCssTransformValue(rotation, warpY) {
      var normalizedWarp = Number(warpY) || 1;
      if (Math.abs(normalizedWarp - 1) < 0.000001) {
        return "rotate(" + rotation + "deg)";
      }
      var radians = Number(rotation) * Math.PI / 180;
      var cosine = Math.cos(radians);
      var sine = Math.sin(radians);
      return "matrix(" + cosine + "," + (sine * normalizedWarp) + "," + (-sine) + "," + (cosine * normalizedWarp) + ",0,0)";
    }

    pointerAngleForWarp(deltaX, deltaY, warpY) {
      return Math.atan2(deltaY / (Number(warpY) || 1), deltaX);
    }

    destroyTransformOverlay() {
      if (this.moveableTarget && this.moveableTarget.parentNode) {
        this.moveableTarget.parentNode.removeChild(this.moveableTarget);
      }
      this.moveableTarget = null;
      if (!this.pointerDrag) {
        this.interaction = null;
      }
    }

    normalizeRotation(value) {
      var rotation = Number(value) || 0;
      while (rotation > 180) { rotation -= 360; }
      while (rotation < -180) { rotation += 360; }
      return rotation;
    }

    normalizeCoverDisplayText(text) {
      // SVG collapses ordinary whitespace; PDF and canvas must measure the same
      // displayed string. Keep saved text and intentional Unicode spaces intact.
      return String(text || "").replace(/[\t\n\r\f ]+/g, " ").replace(/^ | $/g, "");
    }

    // Every render lays out every line of the side, and a keystroke changes
    // one line. A line's metrics depend only on its font, size and text while
    // the page's fonts stay as they are, so they are kept until any font face
    // changes its load status or the zoom changes (textMetricsFontState).
    textLineMetrics(line) {
      var fontState = this.textMetricsFontState();
      if (!fontState) {
        return this.measureTextLineMetrics(line);
      }
      if (fontState !== this.textMetricsState) {
        this.clearTextMetrics();
        this.textMetricsState = fontState;
      }
      var displayText = this.normalizeCoverDisplayText(line.text);
      var size = Number(line.fontSize) * 2;
      var key = String(line.fontId) + "\u0000" + size + "px " + this.fontFamily(line.fontId) + "\u0000" + displayText;
      var cache = this.textMetricsCache || (this.textMetricsCache = new Map());
      if (cache.has(key)) {
        return cache.get(key);
      }
      var metrics = this.measureTextLineMetrics(line);
      // A measurement that started a font load used a fallback font.
      if (this.textMetricsFontState() === fontState) {
        if (cache.size >= TEXT_METRICS_CACHE_LIMIT) {
          cache.delete(cache.keys().next().value);
        }
        cache.set(key, metrics);
      }
      return metrics;
    }

    // What kept metrics were measured with: the load status of every font
    // face on the page and the zoom (device pixel ratio). A face changes its
    // status the moment it finishes loading, before any font event, so a
    // loaded font is never measured from a fallback kept earlier. Null, and
    // nothing is kept, while any face is loading or without the FontFaceSet
    // API.
    textMetricsFontState() {
      var fontSet = root && root.document && root.document.fonts;
      if (this.destroyed || !fontSet || typeof fontSet.forEach !== "function") { return null; }
      var loaded = 0;
      var failed = 0;
      var unloaded = 0;
      var loading = false;
      try {
        fontSet.forEach(function(face) {
          var status = face && face.status;
          if (status === "loaded") {
            loaded += 1;
          } else if (status === "error") {
            failed += 1;
          } else if (status === "unloaded") {
            unloaded += 1;
          } else {
            loading = true;
          }
        });
      } catch (error) {
        return null;
      }
      if (loading) { return null; }
      return [loaded, failed, unloaded, Number(root.devicePixelRatio) || 1].join(":");
    }

    clearTextMetrics() {
      if (this.textMetricsCache) { this.textMetricsCache.clear(); }
    }

    // Measures the shown side's cover text before a render changes the page,
    // so a measurement that needs layout (the hidden unkerned probe) costs
    // little and the preview then reuses it.
    prepareTextMetrics(state) {
      var sideName = state && state.screen === "back-cover" ? "back" : "front";
      var side = state && state.cover && state.cover[sideName];
      if (!side || !side.textEnabled || !Array.isArray(side.objects)) { return; }
      side.objects.forEach(function(object) {
        if (object.type !== "text" || !Array.isArray(object.lines)) { return; }
        object.lines.forEach(function(line) {
          var displayText = this.normalizeCoverDisplayText(line.text);
          if (!displayText.trim()) { return; }
          this.textLineMetrics(Object.assign({}, line, { text: displayText }));
        }, this);
      }, this);
    }

    measureTextLineMetrics(line) {
      var displayText = this.normalizeCoverDisplayText(line.text);
      var size = Number(line.fontSize) * 2;
      var factors = { minimal: 0.58, condensed: 0.48, casual: 0.56, lettering: 0.52, happy: 0.55, "instrument-serif": 0.47, "instrument-serif-italic": 0.49 };
      var estimatedWidth = displayText.length * size * (factors[line.fontId] || 0.58);
      var measuredWidth = 0;
      var measuredInk = null;
      if (root && root.document) {
        if (!this.textMeasureContext) {
          this.textMeasureContext = root.document.createElement("canvas").getContext("2d");
        }
        if (this.textMeasureContext) {
          this.textMeasureContext.font = size + "px " + this.fontFamily(line.fontId);
          var canvasKerningOff = "fontKerning" in this.textMeasureContext;
          if (canvasKerningOff) { this.textMeasureContext.fontKerning = "none"; }
          var measurement = this.textMeasureContext.measureText(displayText);
          measuredWidth = measurement.width;
          if (Number.isFinite(measurement.actualBoundingBoxLeft)) {
            measuredInk = this.textInkMetrics(displayText, size, line.fontId);
          }
          if (!measuredInk && !canvasKerningOff) {
            // This canvas always kerns; the hidden unkerned span measures the run.
            measuredWidth = this.textUnkernedAdvance(displayText, size, line.fontId) || measuredWidth;
          }
        }
      }
      // The unkerned advance the preview paints and the PDF sizes its shape from.
      var width = measuredInk ? measuredInk.advance : measuredWidth || estimatedWidth;
      if (!measuredInk) {
        // Without canvas ink metrics keep the estimated centred line box.
        measuredInk = {
          advance: width,
          ink: { left: -width / 2, right: width / 2, top: -size * 0.42, bottom: size * 0.42 }
        };
      }
      return {
        width: width,
        height: size * TEXT_LINE_HEIGHT_MULTIPLIER,
        advance: measuredInk.advance,
        ink: measuredInk.ink,
        inkRows: measuredInk.inkRows || null,
        fontSize: size
      };
    }

    textInkMetrics(text, size, fontId) {
      // Measure the ink the PDF paints: pdf-lib draws the shaped glyph run on
      // its advance widths without kerning, centred on that advance and on the
      // calibrated baseline.
      if (!this.textInkContext) {
        this.textInkContext = root.document.createElement("canvas").getContext("2d", { willReadFrequently: true });
      }
      var context = this.textInkContext;
      if (!context) { return null; }
      context.font = size + "px " + this.fontFamily(fontId);
      context.textAlign = "left";
      context.textBaseline = "alphabetic";
      var canvasKerningOff = "fontKerning" in context;
      if (canvasKerningOff) { context.fontKerning = "none"; }
      var measurement = context.measureText(text);
      var box = [
        measurement.width,
        measurement.actualBoundingBoxLeft,
        measurement.actualBoundingBoxRight,
        measurement.actualBoundingBoxAscent,
        measurement.actualBoundingBoxDescent
      ];
      if (!box.every(Number.isFinite)) { return null; }
      var advance = box[0];
      var kerning = 0;
      if (!canvasKerningOff) {
        // This canvas always kerns. A hidden span with CSS font-kerning:none
        // supplies the unkerned advance, and the kerned ink is widened to
        // cover any shift between none and all of the run's kerning.
        advance = this.textUnkernedAdvance(text, size, fontId);
        if (!advance) { return null; }
        kerning = box[0] - advance;
      }
      if (!(box[1] + box[2] > 0 && box[3] + box[4] > 0)) { return { advance: advance, ink: null }; }
      var baseline = size * this.textBaselineMiddleEm(fontId);
      return {
        advance: advance,
        ink: {
          left: -advance / 2 - box[1] - Math.max(0, kerning),
          right: -advance / 2 + box[2] - Math.min(0, kerning),
          top: baseline - box[3],
          bottom: baseline + box[4]
        },
        // A canvas that always kerns cannot paint the printed glyph positions.
        inkRows: canvasKerningOff ? this.textInkRows(context, text, size, fontId, box, advance, baseline) : null
      };
    }

    textInkRows(context, text, size, fontId, box, advance, baseline) {
      // Paint the unkerned run and keep each pixel row's leftmost and rightmost
      // ink, so the shape test follows the painted glyph outlines like the PDF
      // check instead of pairing one glyph's height with another's width.
      // Every inked pixel is widened by one raster pixel on each side to cover
      // anti-aliasing and hinting. Returns null when the rows are unavailable.
      var canvas = context.canvas;
      if (!canvas || typeof context.fillText !== "function" || typeof context.getImageData !== "function") { return null; }
      var rasterScale = Math.min(2, 2048 / Math.max(1, box[1] + box[2], box[3] + box[4]));
      var padding = 4;
      var originX = padding + Math.ceil(box[1] * rasterScale);
      var originY = padding + Math.ceil(box[3] * rasterScale);
      var width = originX + Math.ceil(box[2] * rasterScale) + padding;
      var height = originY + Math.ceil(box[4] * rasterScale) + padding;
      // Resizing the canvas clears it and resets the drawing state.
      canvas.width = width;
      canvas.height = height;
      context.font = size * rasterScale + "px " + this.fontFamily(fontId);
      context.fontKerning = "none";
      context.textAlign = "left";
      context.textBaseline = "alphabetic";
      context.fillStyle = "#000";
      context.fillText(text, originX, originY);
      var pixels;
      try {
        pixels = context.getImageData(0, 0, width, height).data;
      } catch (error) {
        return null;
      }
      var rows = [];
      for (var row = 0; row < height; row += 1) {
        var offset = row * width * 4 + 3;
        var first = 0;
        while (first < width && !pixels[offset + first * 4]) { first += 1; }
        if (first === width) { continue; }
        var last = width - 1;
        while (!pixels[offset + last * 4]) { last -= 1; }
        // Ink on the padding edge may continue past the canvas.
        if (row === 0 || row === height - 1 || first === 0 || last === width - 1) { return null; }
        rows.push({
          left: (first - 1 - originX) / rasterScale - advance / 2,
          right: (last + 2 - originX) / rasterScale - advance / 2,
          top: baseline + (row - 1 - originY) / rasterScale,
          bottom: baseline + (row + 2 - originY) / rasterScale
        });
      }
      return rows.length ? rows : null;
    }

    textUnkernedAdvance(text, size, fontId) {
      var body = root.document.body;
      if (!body) { return null; }
      var probe = root.document.createElement("span");
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText = "position:absolute;left:-10000px;top:0;visibility:hidden;white-space:pre;"
        + "font:" + size + "px " + this.fontFamily(fontId) + ";" + UNKERNED_TEXT_STYLE + ";"
        + "letter-spacing:0;word-spacing:0;text-transform:none;text-rendering:optimizeLegibility";
      probe.textContent = text;
      body.appendChild(probe);
      var width = probe.getBoundingClientRect().width;
      body.removeChild(probe);
      return width > 0 ? width : null;
    }

    textLineLeavesShape(item, shapeWidth, shapeHeight, shape) {
      // The backend's coverTextLineLeavesShape rule: the line's ink box bounds
      // its painted rows, so the rows are only tested when the box leaves.
      if (!item.ink || !this.textInkBoxLeavesShape(item.y, item.ink, shapeWidth, shapeHeight, shape)) { return false; }
      if (!item.inkRows) { return true; }
      return item.inkRows.some(function(row) {
        return this.textInkBoxLeavesShape(item.y, row, shapeWidth, shapeHeight, shape);
      }, this);
    }

    textInkBoxLeavesShape(y, box, shapeWidth, shapeHeight, shape) {
      // An ink box (Y down) is tested at its farthest corner, inflated by
      // TEXT_INK_MARGIN so the editor is never more lenient than the PDF.
      return this.textInkLeavesShape(
        Math.max(-box.left, box.right) + TEXT_INK_MARGIN,
        Math.max(Math.abs(y + box.top), Math.abs(y + box.bottom)) + TEXT_INK_MARGIN,
        shapeWidth,
        shapeHeight,
        shape
      );
    }

    textInkLeavesShape(paintedHalfWidth, verticalExtent, shapeWidth, shapeHeight, shape) {
      // The backend's coverTextInkLeavesShape geometry.
      var tolerance = 0.5;
      var halfWidth = shapeWidth / 2;
      var halfHeight = shapeHeight / 2;
      if (verticalExtent > halfHeight + tolerance) { return true; }
      if (shape === "circle") {
        var circleY = Math.min(halfHeight, verticalExtent);
        var circleHalfWidth = Math.sqrt(Math.max(0, halfWidth * halfWidth - circleY * circleY));
        return paintedHalfWidth > circleHalfWidth + tolerance;
      }
      if (shape === "oval") {
        var ellipseY = Math.min(halfHeight, verticalExtent);
        var ellipseHalfWidth = halfWidth * Math.sqrt(Math.max(0, 1 - Math.pow(ellipseY / Math.max(1, halfHeight), 2)));
        return paintedHalfWidth > ellipseHalfWidth + tolerance;
      }
      if (shape === "ribbon") {
        var ribbonProgress = Math.min(1, verticalExtent / Math.max(1, halfHeight));
        var ribbonHalfWidth = halfWidth * (0.86 - 0.14 * ribbonProgress);
        return paintedHalfWidth > ribbonHalfWidth + tolerance;
      }
      if (shape === "pill") {
        var radius = Math.min(halfHeight, halfWidth);
        var straightHalfWidth = Math.max(0, halfWidth - radius);
        var curveY = Math.min(radius, verticalExtent);
        var capsuleHalfWidth = straightHalfWidth + Math.sqrt(Math.max(0, radius * radius - curveY * curveY));
        return paintedHalfWidth > capsuleHalfWidth + tolerance;
      }
      return paintedHalfWidth > halfWidth + tolerance;
    }

    textShapeSize(object, contentWidth, contentHeight, safe, warpY) {
      var baseDimensions = shapeDimensions(object.shape);
      var minimumWidth = baseDimensions.width;
      var minimumHeight = baseDimensions.height;
      var naturalGrowth = object.shape === "circle"
        ? Math.max(1, Math.hypot(contentWidth + 40, contentHeight + 30) / minimumWidth)
        : Math.max(1, (contentWidth + 48) / minimumWidth, (contentHeight + 34) / minimumHeight);
      var growth = naturalGrowth * Math.max(0.2, Number(object.shapeScale) || 1);
      var desiredWidth = minimumWidth * growth;
      var desiredHeight = minimumHeight * growth;
      var radians = Number(object.transform.rotation) * Math.PI / 180;
      var cosine = Math.abs(Math.cos(radians));
      var sine = Math.abs(Math.sin(radians));
      var rotatedWidth = desiredWidth * cosine + desiredHeight * sine;
      var rotatedHeight = (desiredWidth * sine + desiredHeight * cosine) * (Number(warpY) || 1);
      var cap = Math.min(1, safe.width / Math.max(1, rotatedWidth), safe.height / Math.max(1, rotatedHeight));
      return { width: desiredWidth * cap, height: desiredHeight * cap };
    }

    textLayout(object, safe, warpY) {
      var normalizedWhitespace = false;
      var active = object.lines.map(function(line, index) {
        var displayText = this.normalizeCoverDisplayText(line.text);
        if (displayText !== String(line.text || "")) { normalizedWhitespace = true; }
        // Preserve the existing empty-row rule without trimming visible Unicode spacing.
        if (!displayText.trim()) { return null; }
        var displayLine = Object.assign({}, line, { text: displayText });
        return { line: displayLine, index: index, metrics: this.textLineMetrics(displayLine) };
      }, this).filter(Boolean);
      var gap = TEXT_LINE_GAP;
      var contentWidth = active.reduce(function(maximum, item) {
        return Math.max(maximum, item.metrics.width);
      }, 0);
      var contentHeight = active.reduce(function(total, item) {
        return total + item.metrics.height;
      }, 0) + Math.max(0, active.length - 1) * gap;
      var cursor = -contentHeight / 2;
      var lines = active.map(function(item) {
        var y = cursor + item.metrics.height / 2;
        cursor += item.metrics.height + gap;
        return {
          line: item.line,
          index: item.index,
          y: y,
          width: item.metrics.width,
          height: item.metrics.height,
          advance: item.metrics.advance,
          ink: item.metrics.ink,
          inkRows: item.metrics.inkRows,
          fontSize: item.metrics.fontSize
        };
      });
      if (object.shape === "none") {
        return {
          lines: lines,
          contentWidth: contentWidth,
          contentHeight: contentHeight,
          shapeWidth: 0,
          shapeHeight: 0,
          boxWidth: Math.max(18, contentWidth),
          boxHeight: Math.max(18, contentHeight),
          overflowSignature: null
        };
      }

      // Line widths are the unkerned advances the PDF sizes its shape from, so
      // the shape drawn here is the printed shape the decision uses.
      var shapeSize = this.textShapeSize(object, contentWidth, contentHeight, safe, warpY);
      var shapeWidth = shapeSize.width;
      var shapeHeight = shapeSize.height;
      var overflow = lines.some(function(item) {
        return this.textLineLeavesShape(item, shapeWidth, shapeHeight, object.shape);
      }, this);
      var signature = overflow ? shortHash([
        object.shape,
        object.transform.x,
        object.transform.y,
        object.shapeScale,
        object.transform.rotation,
        normalizedWhitespace ? TEXT_LAYOUT_VERSION + "-ascii-whitespace-v2" : TEXT_LAYOUT_VERSION,
        safe.width.toFixed(2),
        safe.height.toFixed(2),
        object.lines.map(function(line) { return [line.text, line.fontId, line.fontSize].join("/"); }).join("|")
      ].join("::")) : null;
      return {
        lines: lines,
        contentWidth: contentWidth,
        contentHeight: contentHeight,
        shapeWidth: shapeWidth,
        shapeHeight: shapeHeight,
        boxWidth: Math.max(18, shapeWidth, contentWidth),
        boxHeight: Math.max(18, shapeHeight, contentHeight),
        overflowSignature: signature
      };
    }

    objectLocalBounds(group) {
      var width = Number(group.dataset.coverBoxWidth);
      var height = Number(group.dataset.coverBoxHeight);
      if (width > 0 && height > 0) {
        return { width: width, height: height };
      }
      try {
        var box = group.getBBox();
        if (box.width > 0 && box.height > 0) {
          return { width: box.width, height: box.height };
        }
      } catch (error) {
        // A newly inserted SVG group can briefly have no measurable box.
      }
      return { width: 18, height: 18 };
    }

    constrainInteraction(interaction) {
      var draft = interaction.draft;
      var radians = draft.rotation * Math.PI / 180;
      var cosine = Math.abs(Math.cos(radians));
      var sine = Math.abs(Math.sin(radians));
      var unscaledWidth = interaction.localBounds.width * cosine + interaction.localBounds.height * sine;
      var unscaledHeight = (interaction.localBounds.width * sine + interaction.localBounds.height * cosine) * (interaction.warpY || 1);
      var maxScale = Math.min(
        interaction.safe.width / Math.max(1, unscaledWidth),
        interaction.safe.height / Math.max(1, unscaledHeight)
      );
      var preservesTextSize = interaction.object && interaction.object.type === "text";
      if (!preservesTextSize) {
        draft.scale = Math.min(draft.scale, maxScale);
      }
      var halfWidth = unscaledWidth * draft.scale / 2;
      var halfHeight = unscaledHeight * draft.scale / 2;
      var minimumX = Math.min(0.5, halfWidth / interaction.safe.width);
      var minimumY = Math.min(0.5, halfHeight / interaction.safe.height);
      draft.x = clamp(draft.x, minimumX, 1 - minimumX);
      draft.y = clamp(draft.y, minimumY, 1 - minimumY);
    }

    prepareInteraction() {
      if (!this.moveableTarget) { return null; }
      var state = this.store.getState();
      var side = this.moveableTarget.dataset.coverSide;
      var id = this.moveableTarget.dataset.coverObject;
      var object = state.cover[side].objects.filter(function(item) { return item.id === id; })[0];
      var group = this.moveableTarget._dyofGroup;
      var svg = group && group.closest("svg");
      if (!object || !group || !svg) { return null; }
      var stage = this.moveableTarget.parentNode;
      var groups = stage && stage.querySelectorAll
        ? Array.from(stage.querySelectorAll('[data-cover-preview-object="' + id + '"]'))
        : [group];
      this.interaction = {
        side: side,
        id: id,
        object: object,
        group: group,
        groups: groups.length ? groups : [group],
        svg: svg,
        safe: this.safeRect(state, side),
        warpY: this.previewFaceWarpY(state, side),
        displayWarpY: this.previewObjectWarpY(state, side),
        projectionX: this.productProjection(state, side).scaleX,
        localBounds: this.objectLocalBounds(group),
        start: Object.assign({}, object.transform),
        draft: Object.assign({}, object.transform),
        changed: false
      };
      return this.interaction;
    }

    applyLiveTransform(interaction) {
      if (!interaction || !interaction.group || !interaction.group.isConnected) { return; }
      var x = interaction.safe.x + interaction.draft.x * interaction.safe.width;
      var y = interaction.safe.y + interaction.draft.y * interaction.safe.height;
      var transformScale = interaction.object && interaction.object.type === "text" ? 1 : interaction.draft.scale;
      interaction.groups.forEach(function(group) {
        if (group.isConnected) {
          group.setAttribute(
            "transform",
            this.objectTransformValue(
              x,
              y,
              interaction.draft.rotation,
              transformScale,
              interaction.displayWarpY || interaction.warpY
            )
          );
        }
      }, this);
    }

    commitInteraction() {
      var interaction = this.interaction;
      if (!interaction) { return; }
      this.interaction = null;
      queueMicrotask(function() {
        if (interaction.changed) {
          this.dispatch("COVER_UPDATE_OBJECT", {
            side: interaction.side,
            id: interaction.id,
            patch: { transform: interaction.draft }
          });
        } else {
          this.dispatch("COVER_SELECT_OBJECT", { side: interaction.side, id: interaction.id });
        }
      }.bind(this));
    }

    attachTransformTarget(state, side, group, stage) {
      if (!group || !stage) { return; }
      this.destroyTransformOverlay();
      var stageBounds = stage.getBoundingClientRect();
      var svg = group.closest("svg");
      if (!stageBounds.width || !svg) { return; }
      var svgBounds = svg.getBoundingClientRect();
      var safe = this.safeRect(state, side);
      var id = group.dataset.coverPreviewObject;
      var object = state.cover[side].objects.filter(function(item) { return item.id === id; })[0];
      if (!object || !svgBounds.width || !svgBounds.height) { return; }
      var localBounds = this.objectLocalBounds(group);
      var centerX = safe.x + object.transform.x * safe.width;
      var centerY = safe.y + object.transform.y * safe.height;
      var projection = this.productProjection(state, side);
      centerX = centerX * projection.scaleX + projection.translateX;
      var warpY = this.previewObjectWarpY(state, side) / projection.scaleX;
      var transformScale = object.type === "text" ? 1 : object.transform.scale;
      var width = Math.max(18, localBounds.width * transformScale * projection.scaleX * svgBounds.width / 1600);
      var height = Math.max(18, localBounds.height * transformScale * projection.scaleX * svgBounds.height / 1600);
      var target = document.createElement("div");
      target.className = "dyof__transform-target";
      target.dataset.coverObject = id;
      target.dataset.coverSide = side;
      target.style.left = (svgBounds.left - stageBounds.left + centerX / 1600 * svgBounds.width - width / 2) + "px";
      target.style.top = (svgBounds.top - stageBounds.top + centerY / 1600 * svgBounds.height - height / 2) + "px";
      target.style.width = width + "px";
      target.style.height = height + "px";
      target.style.transform = this.objectCssTransformValue(object.transform.rotation, warpY);
      target._dyofGroup = group;
      var handle = document.createElement("button");
      handle.type = "button";
      handle.className = "dyof__rotation-handle";
      handle.dataset.coverRotate = "";
      handle.setAttribute("aria-label", dyofT("cover.controls.rotate_named", "Rotate {name}", { name: objectDisplayName(object, this.assets) }));
      target.appendChild(handle);
      stage.appendChild(target);
      this.moveableTarget = target;
    }

    syncTransformTarget(state, side, stage) {
      var active = state.cover.activeObject;
      if (!active || active.side !== side || !active.id || (state.screen !== "front-cover" && state.screen !== "back-cover")) {
        this.destroyTransformOverlay();
        return;
      }
      var groups = stage.querySelectorAll('[data-cover-preview-object="' + active.id + '"]');
      var group = groups.length ? groups[groups.length - 1] : null;
      if (group) {
        this.attachTransformTarget(state, side, group, stage);
      }
    }

    onPointerDown(event) {
      var customColourInput = this.customColourInputFromTarget(event.target);
      if (this.customColourDraft && this.customColourDraft.target !== customColourInput) {
        this.commitCustomColourDraft();
      }
      if (customColourInput && this.host.contains(customColourInput)) {
        this.stageCustomColourDraft(customColourInput);
        return;
      }
      var range = event.target.closest && event.target.closest('[data-cover-input][type="range"]');
      if (range && this.host.contains(range)) {
        this.rangePointer = { target: range, pointerId: event.pointerId };
        this.destroyTransformOverlay();
        return;
      }
      var rotateHandle = event.target.closest && event.target.closest("[data-cover-rotate]");
      if (rotateHandle && this.moveableTarget && !this.pointerDrag) {
        event.preventDefault();
        event.stopPropagation();
        var rotationInteraction = this.prepareInteraction();
        if (!rotationInteraction) { return; }
        var targetBounds = this.moveableTarget.getBoundingClientRect();
        var centerX = targetBounds.left + targetBounds.width / 2;
        var centerY = targetBounds.top + targetBounds.height / 2;
        this.pointerDrag = {
          mode: "rotate",
          pointerId: event.pointerId,
          target: rotateHandle,
          centerX: centerX,
          centerY: centerY,
          startAngle: this.pointerAngleForWarp(
            event.clientX - centerX,
            event.clientY - centerY,
            (rotationInteraction.displayWarpY || rotationInteraction.warpY) / (rotationInteraction.projectionX || 1)
          )
        };
        if (rotateHandle.setPointerCapture) {
          rotateHandle.setPointerCapture(event.pointerId);
        }
        return;
      }
      var target = event.target.closest && event.target.closest("[data-cover-preview-object]");
      if (!target || !this.host.contains(target) || (event.pointerType === "mouse" && event.button !== 0) || this.pointerDrag) { return; }
      var state = this.store.getState();
      var side = state.screen === "back-cover" ? "back" : "front";
      var object = state.cover[side].objects.filter(function(item) {
        return item.id === target.dataset.coverPreviewObject;
      })[0];
      var stage = target.closest("[data-dyof-preview-pages]");
      if (!object || !stage) { return; }
      event.preventDefault();
      if (!this.moveableTarget || this.moveableTarget.dataset.coverObject !== object.id) {
        this.attachTransformTarget(state, side, target, stage);
      }
      var interaction = this.prepareInteraction();
      if (!interaction) { return; }
      this.pointerDrag = {
        mode: "drag",
        pointerId: event.pointerId,
        target: target,
        startClientX: event.clientX,
        startClientY: event.clientY
      };
      this.setSafeGuideVisible(true);
      if (target.setPointerCapture) {
        target.setPointerCapture(event.pointerId);
      }
    }

    onPointerMove(event) {
      var drag = this.pointerDrag;
      var interaction = this.interaction;
      if (!drag || !interaction || drag.pointerId !== event.pointerId) { return; }
      event.preventDefault();
      var svgBounds = interaction.svg.getBoundingClientRect();
      if (!svgBounds.width || !svgBounds.height) { return; }
      if (drag.mode === "rotate") {
        var currentAngle = this.pointerAngleForWarp(
          event.clientX - drag.centerX,
          event.clientY - drag.centerY,
          (interaction.displayWarpY || interaction.warpY) / (interaction.projectionX || 1)
        );
        var deltaDegrees = (currentAngle - drag.startAngle) * 180 / Math.PI;
        interaction.draft.rotation = snapRotation(
          this.normalizeRotation(interaction.start.rotation + deltaDegrees),
          ROTATION_SNAP_THRESHOLD
        );
        interaction.changed = interaction.changed || Math.abs(deltaDegrees) > 0.25;
        this.constrainInteraction(interaction);
        this.applyLiveTransform(interaction);
        if (this.moveableTarget) {
          this.moveableTarget.style.transform = this.objectCssTransformValue(
            interaction.draft.rotation,
            (interaction.displayWarpY || interaction.warpY) / (interaction.projectionX || 1)
          );
        }
        return;
      }
      var deltaX = event.clientX - drag.startClientX;
      var deltaY = event.clientY - drag.startClientY;
      interaction.draft.x = clamp(interaction.start.x + deltaX * 1600 / svgBounds.width / (interaction.projectionX || 1) / interaction.safe.width, 0, 1);
      interaction.draft.y = clamp(interaction.start.y + deltaY * 1600 / svgBounds.height / interaction.safe.height, 0, 1);
      this.constrainInteraction(interaction);
      interaction.changed = interaction.changed || Math.abs(deltaX) > 0.5 || Math.abs(deltaY) > 0.5;
      this.applyLiveTransform(interaction);
      if (this.moveableTarget) {
        var appliedX = (interaction.draft.x - interaction.start.x) * interaction.safe.width * (interaction.projectionX || 1) / 1600 * svgBounds.width;
        var appliedY = (interaction.draft.y - interaction.start.y) * interaction.safe.height / 1600 * svgBounds.height;
        this.moveableTarget.style.transform = "translate(" + appliedX + "px," + appliedY + "px) " + this.objectCssTransformValue(
          interaction.start.rotation,
          (interaction.displayWarpY || interaction.warpY) / (interaction.projectionX || 1)
        );
      }
    }

    onPointerEnd(event) {
      var range = this.rangePointer;
      if (range && range.pointerId === event.pointerId) {
        this.rangePointer = null;
        this.commitRangeDraft(range.target);
        return;
      }
      var drag = this.pointerDrag;
      if (!drag || drag.pointerId !== event.pointerId) { return; }
      this.pointerDrag = null;
      this.setSafeGuideVisible(false);
      if (drag.target.releasePointerCapture && drag.target.hasPointerCapture && drag.target.hasPointerCapture(event.pointerId)) {
        drag.target.releasePointerCapture(event.pointerId);
      }
      this.commitInteraction();
    }

    onBlur(event) {
      var target = event.target;
      if (target && target.matches && target.matches("[data-cover-size-help]")) {
        this.dismissSizeTooltip();
        return;
      }
      if (target && target.matches && target.matches('[data-cover-tooltip-trigger="upload"]')) {
        this.dismissCoverTooltips();
        return;
      }
      if (target && target.matches && target.matches("[data-cover-custom-line-colour],[data-cover-custom-shape-colour]")) {
        this.commitCustomColourDraft(target);
        return;
      }
      if (target && target.matches && target.matches('[data-cover-input][type="range"]')) {
        this.commitRangeDraft(target);
      }
    }

    async renderPdfPreview(buffer) {
      if (!root.pdfjsLib) {
        throw dyofError(dyofT("cover.upload.pdf_loading", "PDF preview support is still loading. Try the file again in a moment."));
      }
      var documentTask = root.pdfjsLib.getDocument({ data: buffer });
      var pdf = await documentTask.promise;
      var page = await pdf.getPage(1);
      var initial = page.getViewport({ scale: 1 });
      var scale = Math.min(2.5, 1600 / Math.max(initial.width, initial.height));
      var viewport = page.getViewport({ scale: scale });
      var canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvasContext: canvas.getContext("2d"), viewport: viewport }).promise;
      return { previewUrl: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height };
    }

    isLocalDevelopmentHost() {
      var hostname = root && root.location ? root.location.hostname : "";
      return hostname === "127.0.0.1" || hostname === "localhost";
    }

    privateStorageEnabled() {
      var dataset = this.host && this.host.dataset ? this.host.dataset : {};
      return this.isLocalDevelopmentHost()
        ? dataset.devPrivateStorageEnabled === "true"
        : dataset.privateStorageEnabled === "true";
    }

    privateUploadScopeEndpoint() {
      var dataset = this.host && this.host.dataset ? this.host.dataset : {};
      var endpoint = this.isLocalDevelopmentHost()
        ? dataset.devUploadScopesEndpoint
        : dataset.uploadScopesEndpoint;
      if (!endpoint) {
        throw dyofError(dyofT("cover.upload.endpoint_missing", "Private artwork storage is enabled but its upload endpoint is not configured."));
      }
      var base = root && root.location && root.location.href
        ? root.location.href
        : "https://invalid.example/";
      var parsed = new URL(endpoint, base);
      var localHttp = this.isLocalDevelopmentHost()
        && parsed.protocol === "http:"
        && (parsed.hostname === "127.0.0.1" || parsed.hostname === "localhost");
      if ((parsed.protocol !== "https:" && !localHttp) || parsed.username || parsed.password || parsed.search || parsed.hash) {
        throw dyofError(dyofT("cover.upload.endpoint_unsafe", "The private artwork upload endpoint is unsafe."));
      }
      return parsed.toString().replace(/\/$/, "");
    }

    async privateUploadResponse(response, operation) {
      var payload = null;
      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }
      if (!response.ok || !payload || typeof payload !== "object" || Array.isArray(payload)) {
        var code = payload && payload.error && typeof payload.error === "object"
          ? payload.error.code
          : payload && typeof payload.error === "string"
            ? payload.error
            : "";
        // Older responses put English sentences in error instead of a code.
        // Keep them verbatim in the UK; only identifiers belong in Czech copy.
        if (dyofLanguageActive() && code && !/^[A-Z][A-Z0-9_]*$/.test(String(code))) {
          code = "";
        }
        throw dyofError(code ? dyofT("cover.upload.operation_failed_code", "{operation} failed ({code}).", { operation: operation, code: code }) : dyofT("cover.upload.operation_failed", "{operation} failed.", { operation: operation }));
      }
      return payload;
    }

    async privateSha256(buffer) {
      if (!root || !root.crypto || !root.crypto.subtle || typeof root.crypto.subtle.digest !== "function") {
        throw dyofError(dyofT("cover.upload.hashing_unavailable", "Secure browser hashing is unavailable for this artwork upload."));
      }
      var digest = await root.crypto.subtle.digest("SHA-256", buffer);
      return Array.from(new Uint8Array(digest)).map(function(value) {
        return value.toString(16).padStart(2, "0");
      }).join("");
    }

    exactPrivateUploadUrl(endpoint, uploadScopeId, assetId, uploadPath) {
      var base = new URL(endpoint);
      var candidate = new URL(uploadPath, base.origin);
      var expectedPath = base.pathname.replace(/\/$/, "")
        + "/" + uploadScopeId + "/assets/" + assetId;
      if (
        candidate.origin !== base.origin
        || candidate.pathname !== expectedPath
        || candidate.search
        || candidate.hash
      ) {
        throw dyofError(dyofT("cover.upload.target_invalid", "The private artwork upload target is invalid."));
      }
      return candidate.toString();
    }

    canonicalPrivateAsset(asset, expected) {
      var extension = expected.mime === "image/jpeg" ? "jpg" : "png";
      var expectedKey = new RegExp(
        "^dyof/originals/[a-z0-9][a-z0-9_-]{7,79}/" + expected.sha256 + "\\." + extension + "$"
      );
      if (
        !asset
        || typeof asset !== "object"
        || asset.id !== expected.assetId
        || asset.name !== expected.name
        || asset.mime !== expected.mime
        || asset.width !== expected.width
        || asset.height !== expected.height
        || asset.sha256 !== expected.sha256
        || asset.sizeBytes !== expected.byteSize
        || typeof asset.storageKey !== "string"
        || !expectedKey.test(asset.storageKey)
        || (asset.versionId !== undefined && (typeof asset.versionId !== "string" || !asset.versionId))
      ) {
        throw dyofError(dyofT("cover.upload.asset_invalid", "The private artwork service returned an invalid canonical asset."));
      }
      var canonical = {
        id: asset.id,
        name: asset.name,
        mime: asset.mime,
        width: asset.width,
        height: asset.height,
        storageKey: asset.storageKey,
        sha256: asset.sha256,
        sizeBytes: asset.sizeBytes
      };
      if (asset.versionId) { canonical.versionId = asset.versionId; }
      return Object.freeze(canonical);
    }

    async uploadPrivateOriginal(file, buffer, metadata) {
      var endpoint = this.privateUploadScopeEndpoint();
      var sha256 = await this.privateSha256(buffer);
      var byteSize = buffer.byteLength;
      var createResponse = await fetch(endpoint, {
        method: "POST",
        headers: { "Accept": "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          assets: [{
            name: file.name,
            mime: metadata.mime,
            byteSize: byteSize,
            sha256: sha256,
            width: metadata.width,
            height: metadata.height
          }]
        })
      });
      var created = await this.privateUploadResponse(createResponse, dyofT("cover.upload.session_creation", "Private artwork session creation"));
      var target = Array.isArray(created.assets) && created.assets.length === 1 ? created.assets[0] : null;
      if (
        !validUploadClaim(created)
        || !target
        || !UPLOAD_ASSET_ID_PATTERN.test(String(target.assetId || ""))
        || target.name !== file.name
        || target.mime !== metadata.mime
        || target.byteSize !== byteSize
        || target.sha256 !== sha256
        || target.width !== metadata.width
        || target.height !== metadata.height
      ) {
        throw dyofError(dyofT("cover.upload.session_invalid", "The private artwork service returned an invalid upload session."));
      }

      var credentialHeaders = {
        "Accept": "application/json",
        "x-dyof-upload-receipt": created.receipt,
        "x-dyof-upload-owner": created.ownerProof
      };
      var uploadUrl = this.exactPrivateUploadUrl(
        endpoint,
        created.uploadScopeId,
        target.assetId,
        target.uploadPath
      );
      var putResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: Object.assign({ "Content-Type": metadata.mime }, credentialHeaders),
        body: buffer
      });
      var put = await this.privateUploadResponse(putResponse, dyofT("cover.upload.operation", "Private artwork upload"));
      if (put.assetId !== target.assetId || put.status !== "stored") {
        throw dyofError(dyofT("cover.upload.asset_unconfirmed", "The private artwork service did not confirm the uploaded asset."));
      }

      var finalizeUrl = endpoint + "/" + created.uploadScopeId + "/finalize";
      var finalizeResponse = await fetch(finalizeUrl, {
        method: "POST",
        headers: credentialHeaders
      });
      var finalized = await this.privateUploadResponse(finalizeResponse, dyofT("cover.upload.finalization", "Private artwork finalization"));
      var finalizedAsset = Array.isArray(finalized.assets)
        ? finalized.assets.filter(function(asset) { return asset && asset.id === target.assetId; })[0]
        : null;
      if (finalized.uploadScopeId !== created.uploadScopeId || finalized.status !== "ready") {
        throw dyofError(dyofT("cover.upload.session_unconfirmed", "The private artwork service did not finalize the upload session."));
      }
      var canonical = this.canonicalPrivateAsset(finalizedAsset, {
        assetId: target.assetId,
        name: file.name,
        mime: metadata.mime,
        byteSize: byteSize,
        sha256: sha256,
        width: metadata.width,
        height: metadata.height
      });
      // Stored before the record is kept and the photo reaches the design, so
      // no reload checkpoint can hold the photo without its claim.
      this.rememberUploadClaim(canonical.id, {
        uploadScopeId: created.uploadScopeId,
        receipt: created.receipt,
        ownerProof: created.ownerProof
      }, finalized.expiresAt);
      return canonical;
    }

    rememberUploadClaim(id, claim, expiresAtInput) {
      var times = {
        expiresAt: uploadClaimExpiry(expiresAtInput) || null,
        // On this device's clock, so a wrong clock alone never drops the
        // claim (see uploadClaimExpired).
        rememberedAt: Date.now()
      };
      if (!this.privateUploadClaims) { this.privateUploadClaims = new Map(); }
      if (!this.privateUploadClaimTimes) { this.privateUploadClaimTimes = new Map(); }
      this.privateUploadClaims.set(id, Object.freeze({
        uploadScopeId: claim.uploadScopeId,
        receipt: claim.receipt,
        ownerProof: claim.ownerProof
      }));
      this.privateUploadClaimTimes.set(id, times);
      var stored = readStoredUploadClaims();
      stored.set(id, {
        uploadScopeId: claim.uploadScopeId,
        receipt: claim.receipt,
        ownerProof: claim.ownerProof,
        expiresAt: times.expiresAt,
        rememberedAt: times.rememberedAt
      });
      writeStoredUploadClaims(stored);
    }

    // This page's claims merged with the ones this tab stored before a reload.
    // Returns server asset ID ->
    // { uploadScopeId, receipt, ownerProof, expiresAt, rememberedAt }.
    uploadClaimEntries() {
      var entries = readStoredUploadClaims();
      if (this.privateUploadClaims) {
        this.privateUploadClaims.forEach(function(claim, id) {
          var stored = entries.get(id);
          var times = this.privateUploadClaimTimes && this.privateUploadClaimTimes.get(id)
            || (sameUploadClaim(stored, claim) ? stored : {});
          entries.set(id, {
            uploadScopeId: claim.uploadScopeId,
            receipt: claim.receipt,
            ownerProof: claim.ownerProof,
            expiresAt: times.expiresAt || null,
            rememberedAt: typeof times.rememberedAt === "number" ? times.rememberedAt : null
          });
        }, this);
      }
      return entries;
    }

    // Removes every claim match(claim, assetId) selects, from this page and
    // from the tab's store.
    forgetUploadClaims(match) {
      var stored = readStoredUploadClaims();
      var storedChanged = false;
      stored.forEach(function(claim, id) {
        if (match(claim, id)) {
          stored.delete(id);
          storedChanged = true;
        }
      });
      if (storedChanged) { writeStoredUploadClaims(stored); }
      if (!this.privateUploadClaims) { return; }
      this.privateUploadClaims.forEach(function(claim, id) {
        if (match(claim, id)) {
          this.privateUploadClaims.delete(id);
          if (this.privateUploadClaimTimes) { this.privateUploadClaimTimes.delete(id); }
        }
      }, this);
    }

    async uploadOriginal(file, id, metadata) {
      this.uploading.add(id);
      try {
        if (!this.privateStorageEnabled()) {
          throw dyofError(dyofT("cover.upload.disabled", "Private artwork uploads are not enabled yet."));
        }
        if (
          !metadata
          || (metadata.mime !== "image/jpeg" && metadata.mime !== "image/png")
        ) {
          throw dyofError(dyofT("cover.upload.jpg_png_only", "Private artwork uploads currently accept JPG and PNG files only."));
        }
        return {
          mode: "private",
          asset: await this.uploadPrivateOriginal(file, metadata.buffer, metadata)
        };
      } finally {
        this.uploading.delete(id);
      }
    }

    async processFile(file, purpose, side, replacementId, slotIndex) {
      if (this.fileProcessing) {
        this.setStatus(dyofT("cover.upload.busy", "Another photo is already uploading. Please wait for it to finish."), "loading");
        return;
      }
      var refusalKey = this.uploadRefusalKey(purpose, side, replacementId, slotIndex);
      this.clearUploadRefusal();
      if (file.size > MAX_FILE_BYTES) {
        this.showUploadRefusal(refusalKey, dyofT("cover.upload.too_large", "That file is larger than 10 MB."));
        return;
      }
      if (!this.privateStorageEnabled()) {
        this.showUploadRefusal(refusalKey, dyofT("cover.upload.disabled", "Private artwork uploads are not enabled yet."));
        return;
      }
      this.fileProcessing = true;
      if (this.host && this.host.setAttribute) {
        this.host.setAttribute("data-cover-uploading", "true");
        this.host.setAttribute("aria-busy", "true");
      }
      this.setStatus(dyofT("cover.upload.checking", "Checking and uploading {name}…", { name: file.name }), "loading");
      try {
        var buffer = await file.arrayBuffer();
        var bytes = new Uint8Array(buffer.slice(0, 8));
        var kind = fileKind(file, bytes);
        if (!kind) {
          throw dyofError(dyofT("cover.upload.genuine_file", "Use a genuine JPG or PNG file."));
        }
        if (kind === "pdf") {
          throw dyofError(dyofT("cover.upload.jpg_png_only", "Private artwork uploads currently accept JPG and PNG files only."));
        }
        var preview;
        var previewUrl = await dataUrlForFile(file);
        // naturalWidth/naturalHeight are the displayed, EXIF-oriented pixels
        // (the server validates the same oriented size before storing).
        var dimensions = await imageDimensions(previewUrl);
        var printCheck = this.contract && typeof this.contract.uploadPrintCheck === "function"
          ? this.contract.uploadPrintCheck(
              purpose === "background" ? "background" : "image",
              dimensions,
              this.printResolutionContext(this.store.getState())
            )
          : { ok: true };
        if (!printCheck.ok) {
          throw dyofError(printCheck.message);
        }
        preview = { previewUrl: previewUrl, width: dimensions.width, height: dimensions.height };
        var id = assetId();
        var mime = kind === "jpeg" ? "image/jpeg" : "image/png";
        var uploadResult = await this.uploadOriginal(file, id, {
          buffer: buffer,
          mime: mime,
          width: preview.width,
          height: preview.height
        });
        if (!uploadResult || uploadResult.mode !== "private" || !uploadResult.asset) {
          throw dyofError(dyofT("cover.upload.storage_failed", "The artwork did not reach private production storage."));
        }
        id = uploadResult.asset.id;
        var record = {
          id: id,
          name: file.name,
          mime: mime,
          size: file.size,
          previewUrl: preview.previewUrl,
          width: preview.width,
          height: preview.height,
          remoteUrl: null,
          createdAt: new Date().toISOString()
        };
        record.storageKey = uploadResult.asset.storageKey;
        record.sha256 = uploadResult.asset.sha256;
        record.sizeBytes = uploadResult.asset.sizeBytes;
        if (uploadResult.asset.versionId) { record.versionId = uploadResult.asset.versionId; }
        await this.assets.put(record);
        if (purpose === "background") {
          this.didChooseSource = true;
          this.didChooseDesign = true;
          this.dispatch("COVER_SET_BACKGROUND", { source: "upload", assetId: id, title: file.name, geometryVersion: 2 });
        } else if (replacementId) {
          var replacementState = this.store.getState();
          var existing = replacementState.cover[side].objects.filter(function(object) { return object.id === replacementId && object.type === "image"; })[0];
          if (!existing) {
            throw dyofError(dyofT("cover.upload.replacement_missing", "That image is no longer available to replace."));
          }
          var replacement = this.contract.createImageObject(id, {
            id: existing.id,
            name: file.name,
            transform: existing.transform,
            qualityWarning: null
          });
          this.dispatch("COVER_UPDATE_OBJECT", {
            side: side,
            id: existing.id,
            patch: {
              name: replacement.name,
              assetId: replacement.assetId,
              activeMask: replacement.activeMask,
              crops: replacement.crops,
              transform: replacement.transform,
              qualityWarning: replacement.qualityWarning
            },
            clearSelection: true
          });
        } else {
          this.dispatch("COVER_ADD_IMAGE", { side: side, assetId: id, object: { name: file.name, qualityWarning: null } });
          this.dispatch("COVER_SELECT_OBJECT", { side: side, id: null });
        }
        // An accepted photo that is still below the print resolution shows its
        // warning under the upload field (renderPhotoResolutionSlot), not
        // over the preview.
        this.setStatus(dyofT("cover.upload.success", "Artwork added and securely uploaded."), "ready");
      } catch (error) {
        this.showUploadRefusal(refusalKey,
          error && error.message && (!dyofLanguageActive() || error.dyofTranslated)
            ? error.message
            : dyofT("cover.upload.process_failed", "The artwork could not be processed.")
        );
      } finally {
        this.fileProcessing = false;
        if (this.host && this.host.removeAttribute) {
          this.host.removeAttribute("data-cover-uploading");
          this.host.removeAttribute("aria-busy");
        }
      }
    }

    assetIds(state) {
      var ids = [];
      if (state.cover.background && state.cover.background.assetId) {
        ids.push(state.cover.background.assetId);
      }
      ["front", "back"].forEach(function(side) {
        state.cover[side].objects.forEach(function(object) {
          if (object.type === "image" && object.assetId) { ids.push(object.assetId); }
        });
      });
      return Array.from(new Set(ids));
    }

    getAssetManifest(state) {
      return this.assets.manifest(this.assetIds(state));
    }

    pendingUploadClaims(assetIds) {
      var entries = this.uploadClaimEntries();
      var now = Date.now();
      var expired = new Set();
      entries.forEach(function(entry, id) {
        if (uploadClaimExpired(entry, now)) { expired.add(id); }
      });
      if (expired.size) {
        // A dead claim can never be used again, and the QA download lane
        // refuses any claim it cannot authorize.
        this.forgetUploadClaims(function(_claim, id) { return expired.has(id); });
      }
      var scopes = new Set();
      return (assetIds || []).map(function(id) {
        var entry = expired.has(id) ? null : entries.get(id);
        // The service refuses a request that names one upload session twice.
        if (!entry || scopes.has(entry.uploadScopeId)) { return null; }
        scopes.add(entry.uploadScopeId);
        // Exactly the three fields the service's strict claim schema accepts.
        return {
          uploadScopeId: entry.uploadScopeId,
          receipt: entry.receipt,
          ownerProof: entry.ownerProof
        };
      }).filter(Boolean);
    }

    clearProductionUploadClaims(claims) {
      var cleared = (claims || []).filter(Boolean);
      if (!cleared.length) { return; }
      this.forgetUploadClaims(function(current) {
        return cleared.some(function(claim) { return sameUploadClaim(current, claim); });
      });
    }

    async getProductionReferences(state) {
      var manifest = await this.getAssetManifest(state);
      var catalogue = null;
      if (state.cover.background && state.cover.background.source === "catalogue") {
        var item = this.findCatalogue(state.cover.background.designId);
        var printAsset = this.cataloguePrintAsset(item, state);
        var strictPrintAssetReady = !Boolean(
          this.printPreviewManifest && this.printPreviewManifest.strict
        ) || Boolean(
          printAsset
          && printAsset.previewDerivedFromProduction
          && /^pa1_[a-f0-9]{64}$/.test(printAsset.id || "")
        );
        var productionRef = printAsset && printAsset.productionRef
          || item && item.production && item.production[this.catalogueKey(state)]
          || "";
        if (!strictPrintAssetReady) { productionRef = ""; }
        productionRef = normalizeProductionUrl(productionRef);
        if (productionRef) {
          catalogue = {
            designId: state.cover.background.designId,
            productionRef: productionRef
          };
          if (printAsset && /^pa1_[a-f0-9]{64}$/.test(printAsset.id || "")) {
            catalogue.printAssetId = printAsset.id;
          }
        }
      }
      var assets = manifest.map(function(asset) {
        if (
          asset.storageKey
          && asset.sha256
          && Number.isSafeInteger(asset.sizeBytes)
          && asset.sizeBytes > 0
        ) {
          var privateAsset = {
            id: asset.id,
            name: asset.name,
            mime: asset.mime,
            width: asset.width,
            height: asset.height,
            storageKey: asset.storageKey,
            sha256: asset.sha256,
            sizeBytes: asset.sizeBytes
          };
          if (asset.versionId) { privateAsset.versionId = asset.versionId; }
          return privateAsset;
        }
        if (!asset.remoteUrl) { return null; }
        return {
          id: asset.id,
          name: asset.name,
          mime: asset.mime,
          width: asset.width,
          height: asset.height,
          remoteUrl: asset.remoteUrl
        };
      }).filter(Boolean);
      // Claims go only with the private assets this save sends. The service
      // matches every claim to one of those, so a claim for a photo whose
      // record has no storage fields (restored or lost) fails the whole save.
      var privateAssetIds = assets.filter(function(asset) {
        return Boolean(asset.storageKey);
      }).map(function(asset) {
        return asset.id;
      });
      var references = {
        catalogue: catalogue,
        assets: assets,
        uploadClaims: this.pendingUploadClaims(privateAssetIds)
      };
      // Saved as intent.cover.backLogo: the colour the customer saw.
      var backLogo = await this.productionBackLogo(state);
      if (backLogo) { references.backLogo = backLogo; }
      return references;
    }

    // A restore merges into this browser's record for the same photo. A local
    // restore passes manifest entries (no preview) and a server restore has no
    // storage fields; neither may erase what the record already holds, which
    // another tab may be using too.
    //
    // A server restore hands over object URLs, which die with this page (or
    // sooner, when the caller releases them after a failed restore). Memory
    // uses them. Only a photo this browser uploaded (its record has storage
    // fields) also gets a data: copy in IndexedDB: another customer's private
    // original opened from a shared link must not stay on this device, so
    // after a reload it is fetched again instead (restoreMissingPreviews).
    async restoreAssets(entries) {
      var assets = this.assets;
      var list = (entries || []).filter(Boolean);
      list.forEach(function(entry) {
        if (isObjectUrl(entry.previewUrl)) { assets.objectUrls.add(entry.previewUrl); }
      });
      for (var index = 0; index < list.length; index += 1) {
        var entry = list[index];
        var existing = await assets.stored(entry.id);
        var existingPreviewUrl = existing && assets.usablePreview(existing.previewUrl)
          ? existing.previewUrl
          : undefined;
        var previewUrl = entry.previewUrl;
        var durablePreviewUrl = "";
        var width = entry.width;
        var height = entry.height;
        if (entry.mime === "application/pdf" && previewUrl) {
          var response = await fetch(previewUrl, { headers: { "Accept": "application/pdf" } });
          if (!response.ok) { continue; }
          var rendered = await this.renderPdfPreview(await response.arrayBuffer());
          previewUrl = rendered.previewUrl;
          width = rendered.width;
          height = rendered.height;
        } else if (/^data:/i.test(String(existingPreviewUrl || "")) && (!previewUrl || isObjectUrl(previewUrl))) {
          // The same photo is already kept in full; reuse it.
          previewUrl = existingPreviewUrl;
        } else if (isObjectUrl(previewUrl)) {
          if (hasStorageFields(existing)) {
            // This browser's own upload lost its stored preview (an earlier
            // version's restore); keep it again so a reload still shows it.
            durablePreviewUrl = await this.durablePreviewUrl(previewUrl);
          }
        } else if (!previewUrl) {
          // Never a dead object URL: those are not usable.
          previewUrl = existingPreviewUrl;
        }
        if (isObjectUrl(previewUrl) && !assets.objectUrls.has(previewUrl)) {
          // Released while this restore was still running.
          previewUrl = durablePreviewUrl
            || (assets.usablePreview(existingPreviewUrl) ? existingPreviewUrl : undefined);
        }
        var restoredRecord = {
          id: entry.id,
          name: entry.name,
          mime: entry.mime,
          size: existing && Number.isFinite(existing.size) ? existing.size : 0,
          previewUrl: previewUrl,
          width: width || (existing ? existing.width : width),
          height: height || (existing ? existing.height : height),
          remoteUrl: existing && existing.remoteUrl || null,
          createdAt: existing && existing.createdAt || new Date().toISOString()
        };
        var stored = hasStorageFields(entry) ? entry : existing;
        if (hasStorageFields(stored)) {
          restoredRecord.storageKey = stored.storageKey;
          restoredRecord.sha256 = stored.sha256;
          restoredRecord.sizeBytes = stored.sizeBytes;
          if (stored.versionId) { restoredRecord.versionId = stored.versionId; }
        }
        await assets.put(restoredRecord, { durablePreviewUrl: durablePreviewUrl });
      }
    }

    async durablePreviewUrl(objectUrl) {
      try {
        var response = await fetch(objectUrl);
        if (!response || !response.ok) { return ""; }
        return await bytesToDataUrl(await response.blob());
      } catch (error) {
        // IndexedDB then keeps the preview it had, if any.
        return "";
      }
    }

    // The caller revoked these object URLs (see AssetStore.release).
    releaseObjectUrls(urls) {
      if (this.assets && typeof this.assets.release === "function") {
        this.assets.release(urls);
      }
    }

    // The photos the design shows that this page cannot draw or capture for
    // the cart: no record, or no preview it can still load. A restore in
    // progress is waited for.
    async assetIdsWithoutPreview(state) {
      var ids = this.assetIds(state);
      var missing = [];
      for (var index = 0; index < ids.length; index += 1) {
        var record = await this.assets.get(ids[index]);
        if (!record || !this.assets.usablePreview(record.previewUrl)) {
          missing.push(ids[index]);
        }
      }
      return missing;
    }

    // Brings back, through load(missingIds) -> restore entries (object URL
    // previews), the photos of ids this page has no preview for; after a
    // reload that is every photo another browser uploaded (restoreAssets).
    // Until it settles, get() answers for such a photo only once it is back,
    // so renders, saves and the cart thumbnail include it. Never rejects.
    restoreMissingPreviews(ids, load) {
      var assets = this.assets;
      var controller = this;
      if (!assets || typeof assets.stored !== "function") { return Promise.resolve(0); }
      var restore = (async function() {
        var missing = [];
        for (var index = 0; index < (ids || []).length; index += 1) {
          var record = await assets.stored(ids[index]);
          if (!record || !assets.usablePreview(record.previewUrl)) { missing.push(ids[index]); }
        }
        if (!missing.length) { return 0; }
        var entries = (await load(missing) || []).filter(function(entry) {
          return entry && missing.indexOf(entry.id) !== -1;
        });
        if (entries.length) { await controller.restoreAssets(entries); }
        return entries.length;
      })().catch(function() {
        // Those photos stay without a preview; Add to cart names them.
        return 0;
      });
      var pending = restore.then(function(count) {
        if (assets.previewRestore === pending) { assets.previewRestore = null; }
        return count;
      });
      assets.previewRestore = pending;
      return pending;
    }

    async backgroundAsset(state, sideName) {
      var background = state.cover.background;
      if (!background) { return null; }
      if (background.source === "upload") {
        var record = await this.assets.get(background.assetId);
        return record ? {
          url: record.previewUrl,
          width: record.width,
          height: record.height
        } : null;
      }
      var item = this.findCatalogue(background.designId);
      var url = this.cataloguePreviewUrl(item, state, sideName || "front");
      var printAsset = this.cataloguePrintAsset(item, state);
      return url ? { url: url, printAssetId: printAsset && printAsset.id || "" } : null;
    }

    backgroundAssetImmediate(state, sideName) {
      var background = state && state.cover && state.cover.background;
      if (!background) { return null; }
      if (background.source === "upload") {
        var record = this.assets.peek(background.assetId);
        return record && record.previewUrl ? {
          url: record.previewUrl,
          width: record.width,
          height: record.height
        } : null;
      }
      var item = this.findCatalogue(background.designId);
      var url = this.cataloguePreviewUrl(item, state, sideName || "front");
      var printAsset = this.cataloguePrintAsset(item, state);
      return url ? { url: url, printAssetId: printAsset && printAsset.id || "" } : null;
    }

    // The Shopify crop a catalogue design's faces are previewed with (for
    // example _825x1065_crop_right): the printed face is the full wrap height
    // and that crop's proportion wide (server geometry.ts coverFace). Null
    // falls back to the product's default crop (contract PRINT_FACES
    // previewCrop). Read only by the back-logo geometry.
    catalogueFaceCrop(state) {
      var background = state && state.cover && state.cover.background;
      if (!background || background.source !== "catalogue") { return null; }
      var item = Array.isArray(this.catalogue) ? this.findCatalogue(background.designId) : null;
      var url = this.cataloguePreviewUrl(item, state, "front");
      var match = /_(\d+)x(\d+)_crop_(?:left|right)(?=\.[a-z0-9]+(?:\?|$))/i.exec(String(url || ""));
      return match && Number(match[1]) > 0 && Number(match[2]) > 0
        ? { width: Number(match[1]), height: Number(match[2]) }
        : null;
    }

    backLogoContext(state) {
      return {
        size: resolvedCoverSize(state),
        productType: state && state.journey && state.journey.productType,
        catalogueCrop: this.catalogueFaceCrop(state)
      };
    }

    // What decides this cover's back logo, or null when it prints none (the A5
    // Planner, no background yet, or catalogue artwork that already carries
    // the logo): the configuration service's own colour where it refused this
    // theme's (adoptServiceBackLogo), a catalogue design's reviewed colour
    // from the print-preview manifest, else a sampling request keyed by
    // everything that changes what prints behind the logo box (photo, crop,
    // size).
    backLogoRequest(state) {
      var contract = this.contract;
      var background = state && state.cover && state.cover.background;
      if (!background || !contract || typeof contract.backLogoBox !== "function") { return null; }
      var context = this.backLogoContext(state);
      if (!contract.backLogoBox(context)) { return null; }
      if (background.source === "catalogue") {
        var item = this.findCatalogue(background.designId);
        var printAsset = this.cataloguePrintAsset(item, state);
        var serviceKey = printAsset && (printAsset.id || printAsset.productionRef);
        if (serviceKey && this.serviceBackLogos && this.serviceBackLogos.has(serviceKey)) {
          var serviceColour = this.serviceBackLogos.get(serviceKey);
          return serviceColour ? { colour: serviceColour } : null;
        }
        if (printAsset && typeof printAsset.backLogo === "string") {
          return printAsset.backLogo ? { colour: printAsset.backLogo } : null;
        }
        var url = this.cataloguePreviewUrl(item, state, "back");
        return url ? {
          key: ["catalogue", context.size, url].join("|"),
          kind: "catalogue",
          url: url,
          palette: designColourTable(item, context.size, this.colourSettings),
          context: context
        } : null;
      }
      return {
        key: ["upload", context.size, context.productType, background.assetId, background.geometryVersion || 1, JSON.stringify(background.crop || {})].join("|"),
        kind: "upload",
        background: background,
        palette: [],
        context: context
      };
    }

    // The colour to draw now: the reviewed one, or a finished sample ("" while
    // a sample is pending or when nothing prints).
    backLogoColourImmediate(state) {
      var request = this.backLogoRequest(state);
      if (!request) { return ""; }
      if (request.colour) { return request.colour; }
      var cached = this.backLogoColours && this.backLogoColours.get(request.key);
      return typeof cached === "string" ? cached : "";
    }

    // True while this cover's back logo still waits on a sample of its photo
    // (or unreviewed design): what is drawn now would lack the logo the save
    // will print.
    backLogoPending(state) {
      var request = this.backLogoRequest(state);
      return Boolean(request && !request.colour && !(this.backLogoColours && this.backLogoColours.has(request.key)));
    }

    // The configuration service refused this theme's back-logo colour for the
    // selected design (its reviewed table is another release's) and named its
    // own: backLogo { version, colour }, or null for none. That print asset
    // shows and saves the service's choice from now on. False when the cover
    // is not a catalogue design.
    adoptServiceBackLogo(state, backLogo) {
      var background = state && state.cover && state.cover.background;
      if (!background || background.source !== "catalogue") { return false; }
      var printAsset = this.cataloguePrintAsset(this.findCatalogue(background.designId), state);
      var serviceKey = printAsset && (printAsset.id || printAsset.productionRef);
      if (!serviceKey) { return false; }
      var normalized = backLogo && this.contract && typeof this.contract.normalizeBackLogo === "function"
        ? this.contract.normalizeBackLogo(backLogo)
        : null;
      this.serviceBackLogos = this.serviceBackLogos || new Map();
      this.serviceBackLogos.set(serviceKey, normalized ? normalized.colour : "");
      return true;
    }

    async resolveBackLogo(state) {
      var request = this.backLogoRequest(state);
      if (!request) { return ""; }
      if (request.colour) { return request.colour; }
      this.backLogoColours = this.backLogoColours || new Map();
      this.backLogoSamples = this.backLogoSamples || new Map();
      if (this.backLogoColours.has(request.key)) { return this.backLogoColours.get(request.key); }
      if (!this.backLogoSamples.has(request.key)) {
        this.backLogoSamples.set(request.key, this.sampleBackLogo(request).catch(function() {
          return "";
        }).then(function(colour) {
          var colours = this.backLogoColours;
          this.backLogoSamples.delete(request.key);
          // null: the photo is not available yet (a restore still loading); ask again later.
          if (colour === null) { return ""; }
          colours.set(request.key, colour || BACK_LOGO_FALLBACK_COLOUR);
          while (colours.size > BACK_LOGO_SAMPLE_CACHE_LIMIT) { colours.delete(colours.keys().next().value); }
          return colours.get(request.key);
        }.bind(this)));
      }
      return this.backLogoSamples.get(request.key);
    }

    // The saved form of the back logo (intent.cover.backLogo), or null.
    async productionBackLogo(state) {
      var colour = await this.resolveBackLogo(state);
      return colour && this.contract && typeof this.contract.normalizeBackLogo === "function"
        ? this.contract.normalizeBackLogo(colour)
        : null;
    }

    // Draws what prints behind the logo and picks its colour with the shared
    // rule. An uploaded photo is drawn on the wrap exactly as print places it
    // (contract uploadBackgroundPlacement, rotation included); a catalogue
    // design's back preview is its back face.
    async sampleBackLogo(request) {
      var contract = this.contract;
      var box = contract.backLogoBox(request.context);
      var image;
      var width;
      var height;
      var regions;
      var draw;
      if (request.kind === "upload") {
        var record = await this.assets.get(request.background.assetId);
        if (!record || !record.previewUrl) { return null; }
        image = await loadSampleImage(record.previewUrl);
        var placed = contract.uploadBackgroundPlacement(request.background, {
          width: Number(record.width) || image.naturalWidth,
          height: Number(record.height) || image.naturalHeight
        }, request.context);
        width = Math.round(box.wrapWidthPt * BACK_LOGO_SAMPLE_PX_PER_PT);
        height = Math.round(box.wrapHeightPt * BACK_LOGO_SAMPLE_PX_PER_PT);
        draw = function(context) {
          // Page points with y down: the PDF placement flipped about the wrap.
          var centerX = placed.rotationCenter.x;
          var centerY = box.wrapHeightPt - placed.rotationCenter.y;
          context.scale(width / box.wrapWidthPt, height / box.wrapHeightPt);
          context.translate(centerX, centerY);
          context.rotate(placed.rotation * Math.PI / 180);
          context.drawImage(
            image,
            placed.placement.x - centerX,
            box.wrapHeightPt - placed.placement.y - placed.placement.height - centerY,
            placed.placement.width,
            placed.placement.height
          );
        };
        regions = {
          box: { x: box.x, y: box.wrapHeightPt - box.y - box.height, width: box.width, height: box.height },
          panel: { x: 0, y: 0, width: box.backPanelWidthPt, height: box.wrapHeightPt },
          scaleX: width / box.wrapWidthPt,
          scaleY: height / box.wrapHeightPt
        };
      } else {
        image = await loadSampleImage(request.url);
        var shares = contract.backLogoFaceBox(request.context, { source: "catalogue" });
        width = image.naturalWidth;
        height = image.naturalHeight;
        draw = function(context) {
          context.drawImage(image, 0, 0, width, height);
        };
        regions = {
          box: { x: shares.x, y: shares.y, width: shares.width, height: shares.height },
          panel: { x: 0, y: 0, width: Math.min(1, box.backPanelWidthPt * shares.width / box.width), height: 1 },
          scaleX: width,
          scaleY: height
        };
      }
      var canvas = cartThumbnailCanvas(width, height);
      var context2d = canvas.getContext("2d");
      context2d.fillStyle = "#FFFFFF";
      context2d.fillRect(0, 0, width, height);
      context2d.save();
      draw(context2d);
      context2d.restore();
      var pixels = context2d.getImageData(0, 0, width, height);
      var scaled = function(rect) {
        return { x: rect.x * regions.scaleX, y: rect.y * regions.scaleY, width: rect.width * regions.scaleX, height: rect.height * regions.scaleY };
      };
      var sample = contract.backLogoSample(
        { data: pixels.data, width: width, height: height, channels: 4 },
        scaled(regions.box),
        scaled(regions.panel)
      );
      var choice = sample && contract.chooseBackLogoColour({
        background: sample.background,
        regions: sample.regions,
        palette: request.palette.concat(sample.palette)
      });
      return choice ? choice.colour : "";
    }

    async backgroundUrl(state, sideName) {
      var asset = await this.backgroundAsset(state, sideName || "front");
      return asset ? asset.url : "";
    }

    getReviewAssetUrls(state) {
      var urls = [];
      var seen = new Set();
      var add = function(url) {
        var normalized = String(url || "");
        if (!normalized || seen.has(normalized)) { return; }
        seen.add(normalized);
        urls.push(normalized);
      };
      ["front", "back"].forEach(function(side) {
        var shell = this.shellForState(state, side);
        if (shell) {
          add(shell.middle);
          add(shell.top);
        }
        var background = this.backgroundAssetImmediate(state, side);
        add(background && background.url);
      }, this);
      var cover = state && state.cover;
      ["front", "back"].forEach(function(side) {
        var objects = cover && cover[side] && Array.isArray(cover[side].objects)
          ? cover[side].objects
          : [];
        objects.forEach(function(object) {
          if (object.type !== "image" || !object.assetId) { return; }
          var record = this.assets.peek(object.assetId);
          add(record && record.previewUrl);
        }, this);
      }, this);
      return urls;
    }

    shellForState(state, sideName) {
      var previewTemplate = this.resolvedCoverPreviewTemplate(state);
      if (resolvedCoverSize(state) === "personal") {
        if (state.journey.productType !== "clipbook") { return null; }
        var personalFamily = this.shells.personalClipbook || {};
        var personalShell = personalFamily[previewTemplate] || personalFamily.classic;
        return personalShell && (personalShell[sideName] || personalShell);
      }
      var product = state.journey.productType === "planner" ? "notebook" : "clipbook";
      var shellFamily = this.shells[product] && this.shells[product][previewTemplate];
      return shellFamily && (shellFamily[sideName] || shellFamily);
    }

    backShellTranslateX(state) {
      return resolvedCoverSize(state) === "personal" ? 0 : A5_BACK_SHELL_TRANSLATE_X;
    }

    coverRect(state, sideName) {
      if (resolvedCoverSize(state) === "personal") {
        return {
          x: PERSONAL_CLIPBOOK_COVER_RECT.x,
          y: PERSONAL_CLIPBOOK_COVER_RECT.y,
          width: PERSONAL_CLIPBOOK_COVER_RECT.width,
          height: PERSONAL_CLIPBOOK_COVER_RECT.height
        };
      }
      var product = state.journey.productType === "planner" ? "notebook" : "clipbook";
      var source = A5_COVER_RECTS[product];
      var rect = { x: source.x, y: source.y, width: source.width, height: source.height };
      if (sideName === "back") {
        rect.x = PREVIEW_CANVAS_SIZE - rect.x - rect.width + this.backShellTranslateX(state);
      }
      return rect;
    }

    constrainedObjectTransform(object, group, safe, warpY) {
      var bounds = this.objectLocalBounds(group);
      var radians = Number(object.transform.rotation) * Math.PI / 180;
      var cosine = Math.abs(Math.cos(radians));
      var sine = Math.abs(Math.sin(radians));
      var rotatedWidth = bounds.width * cosine + bounds.height * sine;
      var rotatedHeight = (bounds.width * sine + bounds.height * cosine) * (Number(warpY) || 1);
      var maximumScale = Math.min(
        3,
        safe.width / Math.max(1, rotatedWidth),
        safe.height / Math.max(1, rotatedHeight)
      );
      var requestedScale = object.type === "text" ? 1 : Math.max(0.2, Number(object.transform.scale) || 1);
      var scale = object.type === "text" ? 1 : Math.min(requestedScale, maximumScale);
      var minimumX = Math.min(0.5, rotatedWidth * scale / 2 / safe.width);
      var minimumY = Math.min(0.5, rotatedHeight * scale / 2 / safe.height);
      return {
        x: clamp(object.transform.x, minimumX, 1 - minimumX),
        y: clamp(object.transform.y, minimumY, 1 - minimumY),
        scale: scale,
        rotation: object.transform.rotation
      };
    }

    constrainedTextPosition(object, group, safe, warpY) {
      var transform = this.constrainedObjectTransform(object, group, safe, warpY);
      return { x: transform.x, y: transform.y };
    }

    syncObjectBoundsState(state, sideName, stage) {
      var safe = this.safeRect(state, sideName);
      var warpY = this.previewFaceWarpY(state, sideName);
      var objects = state.cover[sideName].objects;
      for (var index = 0; index < objects.length; index += 1) {
        var object = objects[index];
        var groups = stage.querySelectorAll('[data-cover-preview-object="' + object.id + '"]');
        var group = groups.length ? groups[groups.length - 1] : null;
        if (!group) { continue; }
        var transform = this.constrainedObjectTransform(object, group, safe, warpY);
        if (
          Math.abs(transform.x - object.transform.x) > 0.00001
          || Math.abs(transform.y - object.transform.y) > 0.00001
          || Math.abs(transform.scale - object.transform.scale) > 0.00001
        ) {
          if (object.type === "image") {
            var slider = this.elements[sideName] && this.elements[sideName].querySelector(
              '[data-cover-object="' + object.id + '"][data-cover-transform="scale"]'
            );
            if (slider) {
              slider.value = String(transform.scale);
              slider.style.setProperty("--dyof-range-fill", clamp((transform.scale - 0.2) / 2.8 * 100, 0, 100) + "%");
            }
          }
          var patch = { transform: transform };
          var acceptedFor = object.type === "text" && group.dataset.coverOverflowSignature;
          if (acceptedFor && object.overflowAcceptedFor === acceptedFor) {
            // Keeping the block inside the guides only moves it, and position
            // does not change the overflow. (A design an earlier editor placed
            // at the edge with its narrower kerned box moves in to where the
            // PDF prints it.) The acceptance of this exact layout moves with it.
            var movedSignature = this.textLayout(
              Object.assign({}, object, { transform: transform }),
              safe,
              warpY
            ).overflowSignature;
            if (movedSignature) {
              patch.overflowWarningFor = movedSignature;
              patch.overflowAcceptedFor = movedSignature;
            }
          }
          this.dispatch("COVER_UPDATE_OBJECT", {
            side: sideName,
            id: object.id,
            patch: patch
          });
          return true;
        }
      }
      return false;
    }

    syncTextOverflowState(state, sideName, stage) {
      if (state.screen !== "front-cover" && state.screen !== "back-cover") { return; }
      var objects = state.cover[sideName].objects.filter(function(object) { return object.type === "text"; });
      for (var index = 0; index < objects.length; index += 1) {
        var object = objects[index];
        var group = stage.querySelector('[data-cover-preview-object="' + object.id + '"]');
        var warningFor = group && group.dataset.coverOverflowSignature || null;
        var acceptedFor = warningFor && object.overflowAcceptedFor === warningFor ? warningFor : null;
        var warningSlot = this.elements[sideName] && this.elements[sideName].querySelector('[data-cover-overflow-warning-slot="' + object.id + '"]');
        if (warningSlot) {
          warningSlot.innerHTML = this.textOverflowWarningHtml(sideName, object, warningFor);
        }
        if (object.overflowWarningFor !== warningFor || object.overflowAcceptedFor !== acceptedFor) {
          this.dispatch("COVER_UPDATE_OBJECT", {
            side: sideName,
            id: object.id,
            patch: {
              overflowWarningFor: warningFor,
              overflowAcceptedFor: acceptedFor
            }
          });
          return;
        }
      }
    }

    renderPreviewShell(state, elements) {
      var sideName = state.screen === "back-cover" ? "back" : "front";
      var stage = elements.previewPages;
      var showDefaultClipbookPreview = state.screen === "cover-selection"
        && state.journey.productType === "clipbook"
        && !this.didChooseSize
        && Boolean(this.host.dataset.coverSizeClipbookDefault);
      var showDefaultPlannerPreview = state.screen === "cover-selection"
        && state.journey.productType === "planner"
        && !this.didChooseDesign
        && Boolean(this.host.dataset.coverSizePlannerDefault);
      stage.innerHTML = "";
      stage.dataset.mode = "closed-cover";
      if (state.screen === "cover-selection" && (showDefaultClipbookPreview || !this.didChooseDesign)) {
        var sizeImage = root.document.createElement("img");
        var isA5Preview = resolvedCoverSize(state) !== "personal";
        sizeImage.className = "dyof__cover-size-preview " + (isA5Preview
          ? "dyof__cover-size-preview--figma"
          : "dyof__cover-size-preview--personal");
        sizeImage.src = showDefaultPlannerPreview
          ? this.host.dataset.coverSizePlannerDefault
          : showDefaultClipbookPreview
            ? this.host.dataset.coverSizeClipbookDefault
            : isA5Preview
              ? this.host.dataset.coverSizeClipbookA5
              : this.host.dataset.coverSizeClipbookPersonal;
        sizeImage.alt = showDefaultPlannerPreview
          ? dyofT("cover.preview.planner_alt", "Navy A5 Planner on a desk")
          : showDefaultClipbookPreview
          ? dyofT("cover.preview.clipbooks_alt", "Blue A5 and Personal Clipbooks on a desk")
          : isA5Preview ? dyofT("cover.preview.a5_alt", "Blue A5 Clipbook on a desk") : dyofT("cover.preview.personal_alt", "Blue Personal Clipbook on a desk");
        stage.appendChild(sizeImage);
        elements.previewTitle.textContent = showDefaultPlannerPreview
          ? dyofT("cover.preview.planner_title", "A5 Planner preview")
          : dyofT("cover.preview.size_title", "Size preview");
        this.setStatus(
          showDefaultPlannerPreview
            ? dyofT("cover.preview.choose_design", "Choose a cover design to continue.")
            : dyofT("cover.preview.choose_size", "Choose the organiser size that suits you."),
          "ready"
        );
        if (typeof this.host.recordWp2PreviewCommit === "function") {
          this.host.recordWp2PreviewCommit("cover-shell", "size-preview", state);
        }
        return;
      }

      var previewState = state;
      if (state.screen === "cover-selection") {
        previewState = Object.assign({}, state, {
          cover: Object.assign({}, state.cover, {
            elastic: Object.assign({}, state.cover.elastic, { enabled: false }),
            front: Object.assign({}, state.cover.front, { objects: [] })
          })
        });
      }
      var background = this.backgroundAssetImmediate(previewState, sideName);
      var imageAssets = {};
      previewState.cover[sideName].objects.filter(function(object) {
        return object.type === "image";
      }).forEach(function(object) {
        var record = this.assets.peek(object.assetId);
        if (record) {
          imageAssets[object.assetId] = {
            url: record.previewUrl,
            width: record.width,
            height: record.height
          };
        }
      }, this);
      stage.appendChild(this.buildScene(previewState, sideName, background, imageAssets));
      elements.previewTitle.textContent = sideName === "back"
        ? dyofT("cover.preview.back_title", "Back cover preview")
        : dyofT("cover.preview.front_title", "Front cover preview");
      this.setStatus(
        background ? dyofT("cover.preview.matches", "Preview matches your current cover design.") : dyofT("cover.preview.preparing", "Preparing the selected cover…"),
        background ? "ready" : "loading"
      );
      if (typeof this.host.recordWp2PreviewCommit === "function") {
        this.host.recordWp2PreviewCommit(
          "cover-shell",
          this.previewRequestKey(previewState, sideName),
          state
        );
      }
    }

    async renderPreview(state, elements) {
      var token = ++this.previewToken;
      this.destroyTransformOverlay();
      var sideName = state.screen === "back-cover" ? "back" : "front";
      var requestKey = this.previewRequestKey(state, sideName);
      var background = await this.backgroundAsset(state, sideName);
      if (sideName === "back") { await this.resolveBackLogo(state); }
      var imageAssets = {};
      var imageObjects = state.cover[sideName].objects.filter(function(object) { return object.type === "image"; });
      await Promise.all(imageObjects.map(async function(object) {
        var record = await this.assets.get(object.assetId);
        if (record) {
          imageAssets[object.assetId] = {
            url: record.previewUrl,
            width: record.width,
            height: record.height
          };
        }
      }, this));
      if (!this.isCurrentPreviewRequest(token, state, elements, requestKey)) { return; }
      var stage = elements.previewPages;
      stage.innerHTML = "";
      stage.dataset.mode = "closed-cover";
      var showDefaultClipbookPreview = state.screen === "cover-selection"
        && state.journey.productType === "clipbook"
        && !this.didChooseSize
        && Boolean(this.host.dataset.coverSizeClipbookDefault);
      var showDefaultPlannerPreview = state.screen === "cover-selection"
        && state.journey.productType === "planner"
        && !this.didChooseDesign
        && Boolean(this.host.dataset.coverSizePlannerDefault);
      if (state.screen === "cover-selection" && (showDefaultClipbookPreview || !background || !this.didChooseDesign)) {
        var sizeImage = document.createElement("img");
        var isA5Preview = resolvedCoverSize(state) !== "personal";
        sizeImage.className = "dyof__cover-size-preview " + (isA5Preview
          ? "dyof__cover-size-preview--figma"
          : "dyof__cover-size-preview--personal");
        sizeImage.src = showDefaultPlannerPreview
          ? this.host.dataset.coverSizePlannerDefault
          : showDefaultClipbookPreview
            ? this.host.dataset.coverSizeClipbookDefault
            : isA5Preview
              ? this.host.dataset.coverSizeClipbookA5
              : this.host.dataset.coverSizeClipbookPersonal;
        sizeImage.alt = showDefaultPlannerPreview
          ? dyofT("cover.preview.planner_alt", "Navy A5 Planner on a desk")
          : showDefaultClipbookPreview
          ? dyofT("cover.preview.clipbooks_alt", "Blue A5 and Personal Clipbooks on a desk")
          : isA5Preview ? dyofT("cover.preview.a5_alt", "Blue A5 Clipbook on a desk") : dyofT("cover.preview.personal_alt", "Blue Personal Clipbook on a desk");
        stage.appendChild(sizeImage);
        elements.previewTitle.textContent = showDefaultPlannerPreview
          ? dyofT("cover.preview.planner_title", "A5 Planner preview")
          : dyofT("cover.preview.size_title", "Size preview");
        this.setStatus(
          showDefaultPlannerPreview
            ? dyofT("cover.preview.choose_design", "Choose a cover design to continue.")
            : dyofT("cover.preview.choose_size", "Choose the organiser size that suits you."),
          "ready"
        );
        if (typeof this.host.recordWp2PreviewCommit === "function") {
          this.host.recordWp2PreviewCommit("cover-assets", requestKey, state);
        }
        return;
      }
      var previewState = state;
      if (state.screen === "cover-selection") {
        previewState = Object.assign({}, state, {
          cover: Object.assign({}, state.cover, {
            elastic: Object.assign({}, state.cover.elastic, { enabled: false }),
            front: Object.assign({}, state.cover.front, { objects: [] })
          })
        });
      }
      var scene = this.buildScene(previewState, sideName, background, imageAssets);
      stage.appendChild(scene);
      this.scheduleFontPreviewRefresh(previewState, sideName, elements);
      if (this.syncObjectBoundsState(previewState, sideName, stage)) { return; }
      this.syncTransformTarget(previewState, sideName, stage);
      this.syncTextOverflowState(previewState, sideName, stage);
      elements.previewTitle.textContent = sideName === "back" ? dyofT("cover.preview.back_title", "Back cover preview") : dyofT("cover.preview.front_title", "Front cover preview");
      this.setStatus(background ? dyofT("cover.preview.matches", "Preview matches your current cover design.") : dyofT("cover.preview.choose_upload", "Choose or upload a cover design to continue."), background ? "ready" : "idle");
      if (typeof this.host.recordWp2PreviewCommit === "function") {
        this.host.recordWp2PreviewCommit("cover-assets", requestKey, state);
      }
    }

    buildBackTopOverlay(state, shell, options) {
      var settings = options || {};
      var includeReviewMask = settings.reviewMask !== false;
      var rect = this.renderedCoverRect(state, "back");
      var translateX = this.backShellTranslateX(state);
      var padding = 90;
      var blur = 42;
      this.shellSequence = (this.shellSequence || 0) + 1;
      var maskId = "dyof-back-shell-mask-" + this.shellSequence;
      var reviewMaskId = "dyof-back-review-mask-" + this.shellSequence;
      var blurId = "dyof-back-shell-blur-" + this.shellSequence;
      var svg = svgElement("svg", {
        class: settings.className || "dyof__cover-shell dyof__cover-shell--top dyof__cover-shell--back-top-composite",
        viewBox: "0 0 " + PREVIEW_CANVAS_SIZE + " " + PREVIEW_CANVAS_SIZE,
        "aria-hidden": "true"
      });
      var defs = svgElement("defs");
      var filter = svgElement("filter", {
        id: blurId,
        x: "-30%",
        y: "-30%",
        width: "160%",
        height: "160%",
        colorInterpolationFilters: "sRGB"
      });
      filter.appendChild(svgElement("feGaussianBlur", { stdDeviation: blur }));
      defs.appendChild(filter);
      var mask = svgElement("mask", {
        id: maskId,
        x: 0,
        y: 0,
        width: PREVIEW_CANVAS_SIZE,
        height: PREVIEW_CANVAS_SIZE,
        maskUnits: "userSpaceOnUse",
        maskContentUnits: "userSpaceOnUse"
      });
      mask.appendChild(svgElement("rect", {
        x: rect.x - padding,
        y: rect.y - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        rx: 105,
        fill: "#fff",
        filter: "url(#" + blurId + ")"
      }));
      defs.appendChild(mask);
      if (shell.middle && includeReviewMask) {
        // Danny's middle alpha is the product silhouette. The painted image
        // below already mirrors its own coordinates, so this source mask must
        // stay unmirrored too. Review CSS opts in; editing keeps the soft mask.
        var reviewMask = svgElement("mask", {
          id: reviewMaskId,
          x: 0,
          y: 0,
          width: PREVIEW_CANVAS_SIZE,
          height: PREVIEW_CANVAS_SIZE,
          maskUnits: "userSpaceOnUse",
          maskContentUnits: "userSpaceOnUse",
          style: "mask-type:alpha",
          "data-cover-review-product-mask": ""
        });
        reviewMask.appendChild(svgElement("image", {
          href: shell.middle,
          x: 0,
          y: 0,
          width: PREVIEW_CANVAS_SIZE,
          height: PREVIEW_CANVAS_SIZE,
          preserveAspectRatio: "none"
        }));
        defs.appendChild(reviewMask);
      }
      svg.appendChild(defs);
      var paintedImage = svgElement("image", {
        href: shell.top,
        x: 0,
        y: 0,
        width: PREVIEW_CANVAS_SIZE,
        height: PREVIEW_CANVAS_SIZE,
        preserveAspectRatio: "none",
        transform: "matrix(-1 0 0 1 " + (PREVIEW_CANVAS_SIZE + translateX) + " 0)",
        mask: "url(#" + maskId + ")"
      });
      if (shell.middle && includeReviewMask) {
        paintedImage.setAttribute("data-cover-review-mask", "");
        paintedImage.setAttribute("style", "--dyof-cover-review-mask:url(#" + reviewMaskId + ")");
      }
      svg.appendChild(paintedImage);
      return svg;
    }

    buildProjectedShellLayer(state, sideName, shell, layer) {
      var svg = svgElement("svg", {
        class: "dyof__cover-shell dyof__cover-shell--" + layer,
        viewBox: "0 0 " + PREVIEW_CANVAS_SIZE + " " + PREVIEW_CANVAS_SIZE,
        "aria-hidden": "true",
        "data-cover-physical-shell": "2"
      });
      var attributes = {
        href: shell[layer], x: 0, y: 0,
        width: PREVIEW_CANVAS_SIZE, height: PREVIEW_CANVAS_SIZE,
        preserveAspectRatio: "none"
      };
      if (sideName === "back") {
        attributes.transform = "matrix(-1 0 0 1 " + (PREVIEW_CANVAS_SIZE + this.backShellTranslateX(state)) + " 0)";
      }
      if (layer === "top" && shell.middle) {
        this.shellSequence = (this.shellSequence || 0) + 1;
        var maskId = "dyof-physical-shell-mask-" + this.shellSequence;
        var defs = svgElement("defs");
        var mask = svgElement("mask", {
          id: maskId, x: 0, y: 0,
          width: PREVIEW_CANVAS_SIZE, height: PREVIEW_CANVAS_SIZE,
          maskUnits: "userSpaceOnUse", maskContentUnits: "userSpaceOnUse",
          style: "mask-type:alpha"
        });
        mask.appendChild(svgElement("image", {
          href: shell.middle, x: 0, y: 0,
          width: PREVIEW_CANVAS_SIZE, height: PREVIEW_CANVAS_SIZE,
          preserveAspectRatio: "none"
        }));
        defs.appendChild(mask);
        svg.appendChild(defs);
        attributes.mask = "url(#" + maskId + ")";
      }
      this.appendProjectedProduct(svg, svgElement("image", attributes), state, sideName);
      return svg;
    }

    buildCoverObjectOverlay(state, sideName, assetUrls) {
      var rect = this.renderedCoverRect(state, sideName);
      var safe = this.safeRect(state, sideName);
      var boundsWarpY = this.previewFaceWarpY(state, sideName);
      var displayWarpY = this.previewObjectWarpY(state, sideName);
      this.svgSequence = (this.svgSequence || 0) + 1;
      var clipId = "dyof-cover-object-clip-" + sideName + "-" + this.svgSequence;
      var svg = svgElement("svg", {
        class: "dyof__cover-object-overlay",
        viewBox: "0 0 " + PREVIEW_CANVAS_SIZE + " " + PREVIEW_CANVAS_SIZE
      });
      var defs = svgElement("defs");
      var clip = svgElement("clipPath", { id: clipId });
      clip.appendChild(svgElement("rect", {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height
      }));
      defs.appendChild(clip);
      svg.appendChild(defs);

      var objects = svgElement("g", { "clip-path": "url(#" + clipId + ")" });
      state.cover[sideName].objects.forEach(function(object, index) {
        if (object.type === "text" && !state.cover[sideName].textEnabled) { return; }
        var x = safe.x + object.transform.x * safe.width;
        var y = safe.y + object.transform.y * safe.height;
        var transformScale = object.type === "text" ? 1 : object.transform.scale;
        var group = svgElement("g", {
          transform: this.objectTransformValue(x, y, object.transform.rotation, transformScale, displayWarpY),
          "data-cover-preview-object": object.id,
          "data-cover-preview-warp-y": displayWarpY,
          tabindex: "0",
          role: "button",
          "aria-label": dyofT("cover.preview.draggable_layer", "{name}, draggable layer {number}", { name: objectDisplayName(object, this.assets), number: index + 1 })
        });
        if (object.type === "text") {
          this.appendTextObject(group, object, safe, boundsWarpY);
        } else {
          this.appendImageObject(group, object, assetUrls[object.assetId], defs);
        }
        objects.appendChild(group);
      }, this);
      this.appendProjectedProduct(svg, objects, state, sideName);
      if (state.screen === "front-cover" || state.screen === "back-cover") {
        this.appendProjectedProduct(svg, svgElement("rect", {
          x: safe.x,
          y: safe.y,
          width: safe.width,
          height: safe.height,
          fill: "none",
          class: "dyof__cover-safe-guide",
          stroke: "#00C3E5",
          "stroke-width": 2,
          "stroke-dasharray": "9 9",
          "pointer-events": "none"
        }), state, sideName);
      }
      return svg;
    }

    buildScene(state, sideName, background, imageAssets, options) {
      var scene = document.createElement("div");
      scene.className = "dyof__cover-scene";
      scene.dataset.side = sideName;
      scene.dataset.coverSize = resolvedCoverSize(state);
      scene.dataset.previewTemplate = this.resolvedCoverPreviewTemplate(state);
      var physicalUpload = this.usesPhysicalUploadGeometry(state);
      if (physicalUpload) { scene.dataset.coverGeometryVersion = "2"; }
      var shell = this.shellForState(state, sideName);
      var cartCapture = options && options.cartThumbnail;
      if (shell && cartCapture) {
        shell = Object.assign({}, shell, {
          middle: cartThumbnailMockupUrl(shell.middle || "") || shell.middle,
          top: cartThumbnailMockupUrl(shell.top || "") || shell.top
        });
      }
      if (shell && (sideName === "back" || physicalUpload)) {
        var backgroundImage = document.createElement("img");
        backgroundImage.setAttribute("class", "dyof__cover-shell dyof__cover-shell--back-background");
        if (cartCapture) { backgroundImage.setAttribute("data-cart-thumbnail-src", shell.top); }
        else { backgroundImage.src = shell.top; }
        backgroundImage.alt = "";
        backgroundImage.setAttribute("aria-hidden", "true");
        scene.appendChild(backgroundImage);
        if (physicalUpload && sideName === "back" && shell.top) {
          // The fixed desk above is cut out in the FRONT cover's shape. On a
          // v2 upload back the product is mirrored, so the front's spine-side
          // wrap and curled corners would show through as bare stage colour at
          // the fore-edge. A mirrored desk patch (the same soft composite the
          // catalogue back paints on top) sits under the artwork and fills that
          // gap. It keeps the back-background class so Review hides it, and it
          // stays outside the product projection and physical-shell layers.
          scene.appendChild(this.buildBackTopOverlay(state, shell, {
            className: "dyof__cover-shell dyof__cover-shell--back-background dyof__cover-shell--back-desk",
            reviewMask: false
          }));
        }
      }
      var compositedBack = Boolean(shell && sideName === "back");
      var coverArtwork = this.buildCoverSvg(
        state,
        sideName,
        background,
        imageAssets,
        shell,
        compositedBack
      );
      scene.appendChild(coverArtwork);
      if (shell) {
        ["middle", "top"].forEach(function(layer) {
          if (physicalUpload) {
            if (shell[layer]) {
              scene.appendChild(this.buildProjectedShellLayer(state, sideName, shell, layer));
            }
            return;
          }
          if (sideName === "back" && layer === "top") {
            scene.appendChild(this.buildBackTopOverlay(state, shell));
            return;
          }
          var shellSource = shell[layer];
          if (!shellSource) { return; }
          var image = document.createElement("img");
          var shellClassName = "dyof__cover-shell dyof__cover-shell--" + layer;
          if (sideName === "back") {
            shellClassName += " dyof__cover-shell--back-product";
          }
          image.setAttribute("class", shellClassName);
          // Detached <img src> still downloads. Cart scenes defer loading to
          // the shared fetch/decode cache instead of starting another request.
          if (cartCapture) { image.setAttribute("data-cart-thumbnail-src", shellSource); }
          else { image.src = shellSource; }
          image.alt = "";
          image.setAttribute("aria-hidden", "true");
          if (layer === "top" && shell.middle) {
            image.setAttribute("data-cover-review-mask", "");
            image.setAttribute("style", "--dyof-cover-review-mask:url(" + JSON.stringify(shell.middle) + ")");
          }
          scene.appendChild(image);
        }, this);
      }
      if (compositedBack) {
        scene.appendChild(this.buildCoverObjectOverlay(state, sideName, imageAssets));
      }
      return scene;
    }

    async buildReviewScenes(state, options) {
      var wrapper = document.createElement("div");
      var backgrounds = {};
      var resolvedBackgrounds = await Promise.all([
        this.backgroundAsset(state, "front"),
        this.backgroundAsset(state, "back"),
        this.resolveBackLogo(state)
      ]);
      backgrounds.front = resolvedBackgrounds[0];
      backgrounds.back = resolvedBackgrounds[1];
      var assetUrls = {};
      var objects = state.cover.front.objects.concat(state.cover.back.objects).filter(function(object) {
        return object.type === "image";
      });
      await Promise.all(objects.map(async function(object) {
        var record = await this.assets.get(object.assetId);
        if (record) {
          assetUrls[object.assetId] = {
            url: record.previewUrl,
            width: record.width,
            height: record.height
          };
        }
      }, this));
      wrapper.className = "dyof__review-cover-scenes";
      ["front", "back"].forEach(function(side) {
        var figure = document.createElement("figure");
        var caption = document.createElement("figcaption");
        caption.textContent = side === "back" ? dyofT("cover.preview.back_caption", "Back cover") : dyofT("cover.preview.front_caption", "Front cover");
        figure.appendChild(this.buildScene(state, side, backgrounds[side], assetUrls, options));
        figure.appendChild(caption);
        wrapper.appendChild(figure);
      }, this);
      return wrapper;
    }

    selectedCartThumbnailFonts(state, sideName) {
      var selected = new Map();
      var side = state && state.cover && state.cover[sideName];
      (side && Array.isArray(side.objects) ? side.objects : []).forEach(function(object) {
        if (object.type !== "text" || side.textEnabled === false) { return; }
        (Array.isArray(object.lines) ? object.lines : []).forEach(function(line) {
          var text = String(line && line.text || "");
          if (!text.trim() || selected.has(line.fontId)) { return; }
          selected.set(line.fontId, text);
        });
      });
      return selected;
    }

    async cartThumbnailResourceDataUrl(sourceInput, cacheInput, signal) {
      var source = String(sourceInput || "");
      var mockupSource = cartThumbnailMockupUrl(source);
      if (mockupSource) { source = mockupSource; }
      var cache = mockupSource ? cartThumbnailMockupDataUrls : cacheInput || new Map();
      if (!source) {
        throw dyofError(dyofT("cover.capture.resource_missing", "The exact cart thumbnail is missing an image resource."));
      }
      if (/^data:/i.test(source)) { return source; }
      if (cache.has(source)) { return awaitCartThumbnail(cache.get(source), signal); }
      var fetcher = root && typeof root.fetch === "function" ? root.fetch.bind(root) : null;
      if (!fetcher) {
        throw dyofError(dyofT("cover.capture.resources_unavailable", "Cart thumbnail resources cannot be loaded in this browser."));
      }
      var pending = boundedCartThumbnailResource(source, async function(resourceSignal, setPhase) {
        var response = await fetcher(source, { credentials: "omit", mode: "cors", signal: resourceSignal });
        if (!response.ok) {
          var httpError = dyofError(dyofT("cover.capture.http_error", "An exact cart thumbnail resource returned HTTP {status}.", { status: response.status }));
          httpError.dyofCartThumbnailHttpStatus = Number(response.status);
          throw httpError;
        }
        var advertisedLength = Number(response.headers && response.headers.get
          ? response.headers.get("content-length") || 0
          : 0);
        if (advertisedLength > CART_THUMBNAIL_RESOURCE_MAX_BYTES) {
          throw dyofError(dyofT("cover.capture.resource_size_limit", "An exact cart thumbnail resource exceeds the safe capture limit."));
        }
        setPhase("body");
        var blob = await response.blob();
        if (blob.size > CART_THUMBNAIL_RESOURCE_MAX_BYTES) {
          throw dyofError(dyofT("cover.capture.resource_size_limit", "An exact cart thumbnail resource exceeds the safe capture limit."));
        }
        setPhase("data-url");
        return bytesToDataUrl(blob);
        // Shared immutable work survives an obsolete design's cancellation;
        // each consumer still cancels its own wait immediately.
      }, mockupSource ? null : signal);
      cache.set(source, pending);
      pending.catch(function() { if (cache.get(source) === pending) { cache.delete(source); } });
      return awaitCartThumbnail(pending, signal);
    }

    async cartThumbnailResourceImage(sourceInput, resourceCache, signal) {
      var source = String(sourceInput || "");
      var mockupSource = cartThumbnailMockupUrl(source);
      if (!mockupSource) {
        var dataUrl = await this.cartThumbnailResourceDataUrl(source, resourceCache, signal);
        return this.loadCartThumbnailImage(dataUrl, signal);
      }
      var pending = cartThumbnailMockupImages.get(mockupSource);
      if (!pending) {
        pending = (async function() {
          var dataUrl = await this.cartThumbnailResourceDataUrl(mockupSource, resourceCache);
          return boundedCartThumbnailResource(mockupSource, function(resourceSignal, setPhase) {
            setPhase("decode");
            return this.loadCartThumbnailImage(dataUrl, resourceSignal);
          }.bind(this));
        }.bind(this))();
        cartThumbnailMockupImages.set(mockupSource, pending);
        pending.catch(function() {
          if (cartThumbnailMockupImages.get(mockupSource) === pending) { cartThumbnailMockupImages.delete(mockupSource); }
        });
      }
      return awaitCartThumbnail(pending, signal);
    }

    async cartThumbnailFontCss(state, sideName, resourceCache, signal) {
      var selected = this.selectedCartThumbnailFonts(state, sideName);
      if (!selected.size) { return ""; }
      var pendingLoads = [];
      selected.forEach(function(sample, fontId) {
        var pending = this.ensureFontLoaded(fontId, sample);
        if (pending) { pendingLoads.push(pending); }
      }, this);
      await Promise.all(pendingLoads);

      var rules = [];
      for (var entry of selected.entries()) {
        var fontId = entry[0];
        var configured = this.configuredFont(fontId);
        var family = this.configuredFontFamily(fontId);
        var webUrl = String(configured && configured.webUrl || "");
        if (!family || !webUrl) {
          throw dyofError(dyofT("cover.capture.font_unavailable", "The selected cover font is unavailable for exact cart capture."));
        }
        var dataUrl = await this.cartThumbnailResourceDataUrl(webUrl, resourceCache, signal);
        rules.push(
          "@font-face{font-family:" + JSON.stringify(family) + ";src:url(" + JSON.stringify(dataUrl) + ");font-style:normal;font-weight:400;}"
        );
      }
      return rules.join("\n");
    }

    loadCartThumbnailImage(source, signal) {
      var documentRef = this.document || (root && root.document);
      return new Promise(function(resolve, reject) {
        if (!documentRef || typeof documentRef.createElement !== "function") {
          reject(dyofError(dyofT("cover.capture.decoding_unavailable", "Cart thumbnail image decoding is unavailable.")));
          return;
        }
        var image = documentRef.createElement("img");
        var settled = false;
        var finish = function(error) {
          if (settled) { return; }
          settled = true;
          image.onload = null;
          image.onerror = null;
          if (signal) { signal.removeEventListener("abort", onAbort); }
          if (error) { reject(error); } else { resolve(image); }
        };
        var onAbort = function() {
          var error = dyofError(dyofT("cover.capture.cancelled", "The exact cart thumbnail capture was cancelled."));
          error.name = "AbortError";
          try { image.src = ""; } catch (ignored) { /* best-effort decode cancellation */ }
          finish(error);
        };
        if (signal) {
          if (signal.aborted) {
            onAbort();
            return;
          }
          signal.addEventListener("abort", onAbort, { once: true });
        }
        image.onload = function() {
          if (typeof image.decode !== "function") {
            finish();
            return;
          }
          Promise.resolve().then(function() {
            return image.decode();
          }).then(function() {
            finish();
          }, function(error) {
            if (image.complete && Number(image.naturalWidth) > 0) {
              finish();
            } else {
              finish(error || dyofError(dyofT("cover.capture.decode_failed", "An exact cart thumbnail image could not be decoded.")));
            }
          });
        };
        image.onerror = function() {
          finish(dyofError(dyofT("cover.capture.decode_failed", "An exact cart thumbnail image could not be decoded.")));
        };
        image.src = source;
        if (image.complete && Number(image.naturalWidth) > 0) {
          image.onload();
        }
      });
    }

    async inlineCartThumbnailSvg(svgInput, fontCss, resourceCache, signal, diagnostics) {
      var svg = svgInput.cloneNode(true);
      svg.setAttribute("width", PREVIEW_CANVAS_SIZE);
      svg.setAttribute("height", PREVIEW_CANVAS_SIZE);
      Array.from(svg.querySelectorAll(".dyof__cover-safe-guide")).forEach(function(guide) {
        if (guide.parentNode) { guide.parentNode.removeChild(guide); }
      });
      var imageNodes = Array.from(svg.querySelectorAll("image"));
      await Promise.all(imageNodes.map(async function(imageNode) {
        var source = imageNode.getAttribute("href")
          || imageNode.getAttributeNS && imageNode.getAttributeNS("http://www.w3.org/1999/xlink", "href")
          || "";
        var load = function() { return this.cartThumbnailResourceDataUrl(source, resourceCache, signal); }.bind(this);
        var dataUrl = diagnostics
          ? await diagnostics.run("layer svg image (" + cartThumbnailSourceHint(source) + ")", load, signal)
          : await load();
        imageNode.setAttribute("href", dataUrl);
        if (imageNode.setAttributeNS) {
          imageNode.setAttributeNS("http://www.w3.org/1999/xlink", "href", dataUrl);
        }
      }, this));

      var documentRef = this.document || (root && root.document);
      var defs = svg.querySelector("defs");
      if (!defs) {
        defs = documentRef.createElementNS(SVG_NS, "defs");
        svg.insertBefore(defs, svg.firstChild || null);
      }
      var style = documentRef.createElementNS(SVG_NS, "style");
      style.textContent = [
        fontCss || "",
        ".dyof__cover-safe-guide{display:none!important;}"
      ].join("\n");
      defs.insertBefore(style, defs.firstChild || null);
      return svg;
    }

    async drawCartThumbnailSvg(context, svg, fontCss, resourceCache, signal, diagnostics) {
      var inlined = await this.inlineCartThumbnailSvg(svg, fontCss, resourceCache, signal, diagnostics);
      var documentRef = this.document || (root && root.document);
      var view = documentRef && documentRef.defaultView || root;
      var Serializer = view && view.XMLSerializer || (typeof XMLSerializer !== "undefined" ? XMLSerializer : null);
      if (!Serializer) {
        throw dyofError(dyofT("cover.capture.serialization_unavailable", "Exact cart thumbnail SVG serialization is unavailable."));
      }
      var source = new Serializer().serializeToString(inlined);
      var objectUrl = URL.createObjectURL(new Blob([source], { type: "image/svg+xml" }));
      try {
        var image = await this.loadCartThumbnailImage(objectUrl, signal);
        context.drawImage(image, 0, 0, PREVIEW_CANVAS_SIZE, PREVIEW_CANVAS_SIZE);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    }

    async flattenCartCoverScene(scene, state, fontCss, resourceCache, options) {
      var canvas = cartThumbnailCanvas(PREVIEW_CANVAS_SIZE, PREVIEW_CANVAS_SIZE, options);
      var context = canvas.getContext && canvas.getContext("2d");
      if (!context) {
        throw dyofError(dyofT("cover.capture.scene_context_failed", "The exact cover scene could not create a drawing context."));
      }
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";

      var children = Array.from(scene && scene.children || []);
      if (!children.length) {
        throw dyofError(dyofT("cover.capture.scene_unavailable", "The exact front-cover scene is unavailable."));
      }
      for (var index = 0; index < children.length; index += 1) {
        var child = children[index];
        var tagName = String(child.tagName || "").toLowerCase();
        context.globalCompositeOperation = child.classList
          && child.classList.contains("dyof__cover-shell--middle")
          ? "multiply"
          : "source-over";
        var source = tagName === "img" ? child.getAttribute("data-cart-thumbnail-src") || child.currentSrc || child.getAttribute("src") || child.src : "";
        var diagnostics = options && options.diagnostics;
        var drawLayer = async function() {
          if (tagName === "svg") {
            await this.drawCartThumbnailSvg(context, child, fontCss, resourceCache, options && options.signal, diagnostics);
          } else if (tagName === "img") {
            var image = await this.cartThumbnailResourceImage(source, resourceCache, options && options.signal);
            context.drawImage(image, 0, 0, PREVIEW_CANVAS_SIZE, PREVIEW_CANVAS_SIZE);
          } else {
            throw dyofError(dyofT("cover.capture.layer_unsupported", "The exact front-cover scene contains an unsupported layer."));
          }
        }.bind(this);
        if (diagnostics) {
          var layerStage = "layer " + (tagName === "svg" ? "svg " + (index + 1) + " (scene artwork)"
            : tagName === "img" ? "img " + (index + 1) + " (" + cartThumbnailSourceHint(source) + ")" : "unsupported");
          await diagnostics.run(layerStage, drawLayer, options.signal);
        } else {
          await drawLayer();
        }
      }
      context.globalCompositeOperation = "source-over";
      return canvas;
    }

    encodeCartThumbnail(canvas, kind, options) {
      return encodeCartThumbnail(canvas, kind, options);
    }

    async captureFrontCartThumbnail(state, optionsInput) {
      var diagnostics = createCartThumbnailDiagnostics("cover");
      if (
        !state
        || !state.journey
        || (state.journey.productType !== "clipbook" && state.journey.productType !== "planner")
      ) {
        var unsupported = dyofError(dyofT("cover.capture.product_unsupported", "A front-cover cart thumbnail is only available for Clipbook or Planner."));
        diagnostics.finish(unsupported);
        throw unsupported;
      }
      var options = optionsInput || {};
      var controller = new AbortController();
      var externalSignal = options.signal;
      var abortFromExternal = function() { controller.abort(cartThumbnailAbortError(externalSignal)); };
      if (externalSignal) {
        if (externalSignal.aborted) {
          abortFromExternal();
        } else {
          externalSignal.addEventListener("abort", abortFromExternal, { once: true });
        }
      }
      var timeoutMs = Math.max(1000, Math.min(30000, Number(options.timeoutMs) || 15000));
      var timeout = setTimeout(function() { controller.abort(cartThumbnailTimeoutError("capture")); }, timeoutMs);
      var captureOptions = Object.assign({}, options, { signal: controller.signal, diagnostics: diagnostics });
      var captureError = null;
      try {
        // The compositor omits missing image records for the editor. A cart
        // derivative must instead be complete or be left out of the purchase.
        await diagnostics.run("scene assets", async function() {
          var background = await this.backgroundAsset(state, "front");
          if (!background || !background.url) { throw new Error("The exact front-cover background is unavailable."); }
          var imageIds = (state.cover.front.objects || []).filter(function(object) {
            return object.type === "image";
          }).map(function(object) { return object.assetId; });
          if (state.cover.background && state.cover.background.assetId) { imageIds.push(state.cover.background.assetId); }
          for (var index = 0; index < imageIds.length; index += 1) {
            var record = await this.assets.get(imageIds[index]);
            if (!record || !this.assets.usablePreview(record.previewUrl)) {
              throw new Error("An exact front-cover photo preview is unavailable.");
            }
          }
        }.bind(this), controller.signal);
        var resourceCache = new Map();
        var fontCss = await diagnostics.run("fonts", function() {
          return this.cartThumbnailFontCss(state, "front", resourceCache, controller.signal);
        }.bind(this), controller.signal);
        var scene = await diagnostics.run("scene", async function() {
          var wrapper = await this.buildReviewScenes(state, { cartThumbnail: true });
          var front = wrapper && wrapper.querySelector ? wrapper.querySelector("figure .dyof__cover-scene") : null;
          if (!front) {
            throw dyofError(dyofT("cover.capture.scene_unavailable", "The exact front-cover scene is unavailable."));
          }
          return front;
        }.bind(this), controller.signal);
        var flattened = await diagnostics.run("scene flatten", function() {
          return this.flattenCartCoverScene(scene, state, fontCss, resourceCache, captureOptions);
        }.bind(this), controller.signal);
        return await diagnostics.run("encode", function() {
          return this.encodeCartThumbnail(flattened, "cover", captureOptions);
        }.bind(this), controller.signal);
      } catch (error) {
        captureError = error;
        diagnostics.finish(error, controller.signal);
        if (error && error.name === "AbortError") {
          throw markCartThumbnailDiagnosed(dyofError(dyofT("cover.capture.timed_out", "The exact front-cover cart thumbnail timed out or was cancelled.")));
        }
        if (dyofLanguageActive() && !(error && error.dyofTranslated)) {
          throw markCartThumbnailDiagnosed(dyofError(dyofT("cover.capture.failed", "The cover preview could not be prepared for cart. Please try again.")));
        }
        throw error;
      } finally {
        if (!captureError) { diagnostics.finish(null, controller.signal); }
        clearTimeout(timeout);
        controller.abort();
        if (externalSignal) {
          externalSignal.removeEventListener("abort", abortFromExternal);
        }
      }
    }

    buildReviewScenesImmediate(state) {
      var wrapper = document.createElement("div");
      var backgrounds = {
        front: this.backgroundAssetImmediate(state, "front"),
        back: this.backgroundAssetImmediate(state, "back")
      };
      var assetUrls = {};
      var missingAssetIds = [];
      var seenMissing = new Set();
      var cover = state && state.cover;
      var background = cover && cover.background;
      var rememberMissing = function(id) {
        if (!id || seenMissing.has(id)) { return; }
        seenMissing.add(id);
        missingAssetIds.push(id);
      };
      if (background && background.source === "upload" && background.assetId) {
        var backgroundRecord = this.assets.peek(background.assetId);
        if (!backgroundRecord || !backgroundRecord.previewUrl) {
          rememberMissing(background.assetId);
        }
      }
      ["front", "back"].forEach(function(side) {
        var objects = cover && cover[side] && Array.isArray(cover[side].objects)
          ? cover[side].objects
          : [];
        objects.forEach(function(object) {
          if (object.type !== "image" || !object.assetId) { return; }
          var record = this.assets.peek(object.assetId);
          if (!record || !record.previewUrl) {
            rememberMissing(object.assetId);
            return;
          }
          assetUrls[object.assetId] = {
            url: record.previewUrl,
            width: record.width,
            height: record.height
          };
        }, this);
      }, this);
      // A back logo still waiting on its photo sample is drawn by the async
      // build (buildReviewScenes resolves it), so Review shows what prints.
      var backLogoPending = this.backLogoPending(state);
      var hydrationRequired = missingAssetIds.length > 0 || backLogoPending;
      var sceneState = state;
      if (missingAssetIds.length > 0) {
        sceneState = Object.assign({}, state, {
          cover: Object.assign({}, cover, {
            front: Object.assign({}, cover.front, { objects: [] }),
            back: Object.assign({}, cover.back, { objects: [] })
          })
        });
      }
      wrapper.className = "dyof__review-cover-scenes";
      wrapper.dataset.hydrationRequired = String(hydrationRequired);
      wrapper.dataset.reviewCoverMode = missingAssetIds.length > 0 ? "shell" : backLogoPending ? "back-logo-pending" : "exact";
      if (missingAssetIds.length > 0) {
        wrapper.dataset.missingAssetIds = missingAssetIds.join(",");
      }
      ["front", "back"].forEach(function(side) {
        var figure = document.createElement("figure");
        var caption = document.createElement("figcaption");
        figure.dataset.coverSide = side;
        caption.textContent = side === "back" ? dyofT("cover.preview.back_caption", "Back cover") : dyofT("cover.preview.front_caption", "Front cover");
        figure.appendChild(this.buildScene(sceneState, side, backgrounds[side], assetUrls));
        figure.appendChild(caption);
        wrapper.appendChild(figure);
      }, this);
      return wrapper;
    }

    buildCoverSvg(state, sideName, background, assetUrls, shell, backgroundOnly) {
      var rect = this.renderedCoverRect(state, sideName);
      var safe = this.safeRect(state, sideName);
      var boundsWarpY = this.previewFaceWarpY(state, sideName);
      var displayWarpY = this.previewObjectWarpY(state, sideName);
      var backgroundUrl = typeof background === "string" ? background : background && background.url;
      var svg = svgElement("svg", { viewBox: "0 0 1600 1600", role: "img", "aria-label": sideName === "back" ? dyofT("cover.preview.back_design", "Back cover design preview") : dyofT("cover.preview.front_design", "Front cover design preview") });
      var defs = svgElement("defs");
      this.svgSequence = (this.svgSequence || 0) + 1;
      var coverClipId = "dyof-cover-clip-" + sideName + "-" + this.svgSequence;
      var coverMaskId = "dyof-cover-mask-" + sideName + "-" + this.svgSequence;
      var coverToneId = "dyof-cover-tone-" + sideName + "-" + this.svgSequence;
      defs.appendChild(coverArtworkToneFilter(coverToneId));
      var coverClip = svgElement("clipPath", { id: coverClipId });
      coverClip.appendChild(svgElement("rect", { x: rect.x, y: rect.y, width: rect.width, height: rect.height }));
      defs.appendChild(coverClip);
      if (shell && shell.middle) {
        var coverMask = svgElement("mask", {
          id: coverMaskId,
          x: 0,
          y: 0,
          width: PREVIEW_CANVAS_SIZE,
          height: PREVIEW_CANVAS_SIZE,
          maskUnits: "userSpaceOnUse",
          maskContentUnits: "userSpaceOnUse",
          style: "mask-type:alpha"
        });
        var maskAttributes = {
          class: "dyof-cover-mask-source",
          href: shell.middle,
          x: 0,
          y: 0,
          width: PREVIEW_CANVAS_SIZE,
          height: PREVIEW_CANVAS_SIZE,
          preserveAspectRatio: "none"
        };
        if (sideName === "back") {
          maskAttributes.transform = "matrix(-1 0 0 1 " + (PREVIEW_CANVAS_SIZE + this.backShellTranslateX(state)) + " 0)";
        }
        coverMask.appendChild(svgElement("image", maskAttributes));
        defs.appendChild(coverMask);
      }
      svg.appendChild(defs);
      var artworkAttributes = { "clip-path": "url(#" + coverClipId + ")" };
      if (shell && shell.middle) {
        artworkAttributes.mask = "url(#" + coverMaskId + ")";
      }
      var artwork = svgElement("g", artworkAttributes);
      artwork.appendChild(svgElement("rect", { x: rect.x, y: rect.y, width: rect.width, height: rect.height, fill: "#F4F0E9" }));
      if (backgroundUrl) {
        if (state.cover.background && state.cover.background.source === "upload") {
          var backgroundCrop = state.cover.background.crop;
          var backgroundRecord = {
            width: Number(background && background.width) || 1,
            height: Number(background && background.height) || 1
          };
          var backgroundGeometry = this.backgroundPreviewGeometry(
            backgroundRecord,
            backgroundCrop,
            rect,
            sideName,
            this.backgroundCropAspect(state)
          );
          artwork.appendChild(svgElement("image", {
            class: "dyof-cover-background-artwork",
            href: backgroundUrl,
            filter: "url(#" + coverToneId + ")",
            x: backgroundGeometry.x,
            y: backgroundGeometry.y,
            width: backgroundGeometry.width,
            height: backgroundGeometry.height,
            preserveAspectRatio: "none",
            transform: this.backgroundRotationTransform(state, sideName, backgroundGeometry, backgroundCrop.rotation)
          }));
        } else {
          artwork.appendChild(svgElement("image", {
            class: "dyof-cover-background-artwork",
            href: backgroundUrl,
            filter: "url(#" + coverToneId + ")",
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height,
            preserveAspectRatio: "none",
            "data-print-asset-id": background && background.printAssetId || ""
          }));
        }
      }
      // The Filofax logo prints on the back over the background and under
      // every customer object, so it belongs to the artwork layer, also when
      // a composited back draws its objects separately.
      var backLogo = sideName === "back" && backgroundUrl ? this.backLogoColourImmediate(state) : "";
      if (backLogo) {
        this.appendBackLogo(artwork, state, rect, backLogo, coverToneId);
      }
      if (!backgroundOnly) {
        state.cover[sideName].objects.forEach(function(object, index) {
          if (object.type === "text" && !state.cover[sideName].textEnabled) { return; }
          var x = safe.x + object.transform.x * safe.width;
          var y = safe.y + object.transform.y * safe.height;
          var transformScale = object.type === "text" ? 1 : object.transform.scale;
          var group = svgElement("g", {
            transform: this.objectTransformValue(x, y, object.transform.rotation, transformScale, displayWarpY),
            "data-cover-preview-object": object.id,
            "data-cover-preview-warp-y": displayWarpY,
            tabindex: "0",
            role: "button",
            "aria-label": dyofT("cover.preview.draggable_layer", "{name}, draggable layer {number}", { name: objectDisplayName(object, this.assets), number: index + 1 })
          });
          if (object.type === "text") {
            this.appendTextObject(group, object, safe, boundsWarpY);
          } else {
            this.appendImageObject(group, object, assetUrls[object.assetId], defs);
          }
          artwork.appendChild(group);
        }, this);
      }
      if (!backgroundOnly && sideName === "front" && this.elasticClosureOffered() && state.cover.elastic.enabled) {
        this.appendElasticPreview(artwork, defs, state, rect);
      }
      this.appendProjectedProduct(svg, artwork, state, sideName);
      if (!backgroundOnly && (state.screen === "front-cover" || state.screen === "back-cover")) {
        this.appendProjectedProduct(svg, svgElement("rect", { class: "dyof__cover-safe-guide", x: safe.x, y: safe.y, width: safe.width, height: safe.height, fill: "none", stroke: "#00C3E5", "stroke-width": 2, "stroke-dasharray": "9 9", "pointer-events": "none" }), state, sideName);
      }
      if (resolvedCoverSize(state) === "personal" && !shell) {
        var ringX = sideName === "back" ? rect.x + rect.width + 18 : rect.x - 18;
        for (var ring = 0; ring < 6; ring += 1) {
          svg.appendChild(svgElement("circle", { cx: ringX, cy: rect.y + 100 + ring * 145, r: 16, fill: "none", stroke: "#B7B7B7", "stroke-width": 8 }));
        }
      }
      return svg;
    }

    // Filofax's wordmark at its print box, mapped into this back face exactly
    // as the background is (contract backLogoFaceBox), with the artwork's
    // preview tone.
    appendBackLogo(artwork, state, rect, colour, toneId) {
      var shares = this.contract.backLogoFaceBox(this.backLogoContext(state), state.cover.background);
      if (!shares) { return; }
      var wordmark = this.contract.BACK_LOGO.wordmark;
      artwork.appendChild(svgElement("path", {
        class: "dyof-cover-back-logo",
        d: wordmark.path,
        fill: colour,
        filter: "url(#" + toneId + ")",
        transform: "matrix(" + [
          shares.width * rect.width / wordmark.width,
          0,
          0,
          shares.height * rect.height / wordmark.height,
          rect.x + shares.x * rect.width,
          rect.y + shares.y * rect.height
        ].join(" ") + ")",
        "aria-hidden": "true",
        "pointer-events": "none",
        "data-cover-back-logo": colour
      }));
    }

    appendElasticPreview(artwork, defs, state, rect) {
      var colourId = state.cover.elastic.colourId || "navy";
      var colour = this.contract.ELASTIC_COLOURS[colourId] || this.contract.ELASTIC_COLOURS.navy;
      var metallic = /-metallic$/.test(colourId);
      var band = ELASTIC_BANDS[colourId];
      var bandUrl = band && this.elasticBands ? this.elasticBands[colourId] : "";
      this.svgSequence = (this.svgSequence || 0) + 1;
      var width = clamp(rect.width * 0.032, 16, 25);
      var centerX = rect.x + rect.width * 0.92;
      var x = centerX - width / 2;
      var y = rect.y - 3;
      var height = rect.height + 6;
      var group = svgElement("g", {
        class: "dyof-cover-elastic",
        "data-elastic-colour": colourId,
        "data-elastic-material": metallic ? "glitter" : "woven",
        "data-elastic-texture": bandUrl ? "karen-email-band" : "base-colour-fallback",
        "pointer-events": "none"
      });
      if (bandUrl) {
        var bandWidth = height * band.sourceWidth / band.sourceHeight;
        group.appendChild(svgElement("image", {
          href: bandUrl,
          x: centerX - bandWidth / 2,
          y: y,
          width: bandWidth,
          height: height,
          preserveAspectRatio: "none",
          "data-elastic-band-image": "",
          "data-email-source-asset": band.assetName,
          "data-email-source-sha256": band.sha256
        }));
      } else {
        group.appendChild(svgElement("rect", {
          x: x - 3,
          y: y + 2,
          width: width + 6,
          height: height,
          rx: (width + 6) / 2,
          fill: "#000000",
          opacity: 0.2
        }));
        var gradientId = "dyof-elastic-gradient-" + this.svgSequence;
        var gradient = svgElement("linearGradient", {
          id: gradientId,
          x1: "0%",
          y1: "0%",
          x2: "100%",
          y2: "0%"
        });
        gradient.appendChild(svgElement("stop", { offset: "0%", "stop-color": colour, "stop-opacity": 0.76 }));
        gradient.appendChild(svgElement("stop", { offset: "38%", "stop-color": colour, "stop-opacity": 1 }));
        gradient.appendChild(svgElement("stop", { offset: "58%", "stop-color": "#FFFFFF", "stop-opacity": 0.3 }));
        gradient.appendChild(svgElement("stop", { offset: "76%", "stop-color": colour, "stop-opacity": 1 }));
        gradient.appendChild(svgElement("stop", { offset: "100%", "stop-color": "#000000", "stop-opacity": 0.28 }));
        defs.appendChild(gradient);
        group.appendChild(svgElement("rect", {
          x: x,
          y: y,
          width: width,
          height: height,
          rx: width / 2,
          fill: "url(#" + gradientId + ")",
          stroke: colour,
          "stroke-width": 1.5
        }));
        group.appendChild(svgElement("line", {
          x1: x + width * 0.34,
          x2: x + width * 0.34,
          y1: y + width / 2,
          y2: y + height - width / 2,
          stroke: "#FFFFFF",
          "stroke-width": Math.max(1, width * 0.08),
          opacity: metallic ? 0.42 : 0.24
        }));
      }
      artwork.appendChild(group);
    }

    appendTextObject(group, object, safe, warpY) {
      var layout = this.textLayout(object, safe, warpY);
      var shapeWidth = layout.shapeWidth;
      var shapeHeight = layout.shapeHeight;
      group.setAttribute("data-cover-box-width", layout.boxWidth);
      group.setAttribute("data-cover-box-height", layout.boxHeight);
      if (layout.overflowSignature) {
        group.setAttribute("data-cover-overflow-signature", layout.overflowSignature);
      }
      if (object.shape !== "none") {
        var shape = shapeSvgElement(object.shape, shapeWidth, shapeHeight, object.shapeColour);
        shape.setAttribute("data-cover-text-shape", "");
        group.appendChild(shape);
      }
      layout.lines.forEach(function(item) {
        var line = item.line;
        group.appendChild(svgElement("text", {
          x: 0,
          y: item.y,
          fill: line.colour,
          "font-family": this.fontFamily(line.fontId),
          "font-size": item.fontSize,
          "font-kerning": "none",
          style: UNKERNED_TEXT_STYLE,
          "data-cover-text-line": item.index,
          "text-anchor": "middle",
          "dominant-baseline": "middle"
        }, line.text));
      }, this);
    }

    imageObjectDimensions(object, asset, crop) {
      var width = 220;
      var height = 220;
      if (object.activeMask === "original") {
        var sourceWidth = Number(asset && asset.width) || 1;
        var sourceHeight = Number(asset && asset.height) || 1;
        var cropWidth = crop && crop.width ? crop.width : 1;
        var cropHeight = crop && crop.height ? crop.height : 1;
        var ratio = sourceWidth * cropWidth / (sourceHeight * cropHeight);
        if (ratio >= 1) {
          width = 277;
          height = 277 / ratio;
        } else {
          height = 277;
          width = 277 * ratio;
        }
      }
      return { width: width, height: height };
    }

    applyImageCropGeometry(image, object, asset, crop, dimensions) {
      var sourceWidth = Number(asset && asset.width) || 1;
      var sourceHeight = Number(asset && asset.height) || 1;
      var size = dimensions || this.imageObjectDimensions(object, asset, crop);
      if (crop.width && crop.height) {
        var scale = Math.max(size.width / (sourceWidth * crop.width), size.height / (sourceHeight * crop.height));
        var drawWidth = sourceWidth * scale;
        var drawHeight = sourceHeight * scale;
        image.setAttribute("x", -size.width / 2 - (crop.x - crop.width / 2) * drawWidth);
        image.setAttribute("y", -size.height / 2 - (crop.y - crop.height / 2) * drawHeight);
        image.setAttribute("width", drawWidth);
        image.setAttribute("height", drawHeight);
        image.setAttribute("preserveAspectRatio", "none");
        image.setAttribute("transform", crop.rotation ? "rotate(" + crop.rotation + ")" : "");
        return;
      }
      image.setAttribute("x", -size.width / 2);
      image.setAttribute("y", -size.height / 2);
      image.setAttribute("width", size.width);
      image.setAttribute("height", size.height);
      image.setAttribute("preserveAspectRatio", object.activeMask === "original" ? "none" : "xMidYMid slice");
      if (object.activeMask === "original") {
        image.removeAttribute("transform");
        return;
      }
      image.setAttribute(
        "transform",
        "translate(" + ((0.5 - crop.x) * size.width * (crop.zoom - 1)) + " " + ((0.5 - crop.y) * size.height * (crop.zoom - 1)) + ") rotate(" + crop.rotation + ") scale(" + crop.zoom + ")"
      );
    }

    appendImageObject(group, object, asset, defs) {
      var url = typeof asset === "string" ? asset : asset && asset.url;
      if (!url) { return; }
      var crop = object.crops[object.activeMask];
      var dimensions = this.imageObjectDimensions(object, asset, crop);
      var width = dimensions.width;
      var height = dimensions.height;
      group.setAttribute("data-cover-box-width", width);
      group.setAttribute("data-cover-box-height", height);
      var clipId = "mask-" + object.id.replace(/[^a-zA-Z0-9_-]/g, "");
      var clip = svgElement("clipPath", { id: clipId });
      clip.appendChild(object.activeMask === "circle"
        ? svgElement("circle", { cx: 0, cy: 0, r: width / 2 })
        : svgElement("rect", { x: -width / 2, y: -height / 2, width: width, height: height }));
      defs.appendChild(clip);
      var cropGroup = svgElement("g", { "clip-path": "url(#" + clipId + ")" });
      var image = svgElement("image", {
        href: url,
        "data-cover-crop-image": ""
      });
      this.applyImageCropGeometry(image, object, asset, crop, dimensions);
      cropGroup.appendChild(image);
      group.appendChild(cropGroup);
    }
  }

  return {
    Controller: Controller,
    AssetStore: AssetStore,
    normalizeProductionUrl: normalizeProductionUrl,
    themeFilterMatches: themeFilterMatches,
    colourFilterMatches: colourFilterMatches,
    availableFilterGroups: availableFilterGroups,
    stablePatternSlug: stablePatternSlug,
    normalizeCatalogueItem: normalizeCatalogueItem,
    normalizeCatalogue: normalizeCatalogue,
    normalizePrintPreviewManifest: normalizePrintPreviewManifest,
    resolveCoverPreviewTemplate: resolveCoverPreviewTemplate,
    resolveRefillPreviewTemplate: resolveRefillPreviewTemplate,
    resolveJournalPreviewScene: resolveJournalPreviewScene,
    resolveDesignPalettes: resolveDesignPalettes,
    designColourTable: designColourTable,
    snapRotation: snapRotation,
    encodeCartThumbnail: encodeCartThumbnail,
    createCartThumbnailDiagnostics: createCartThumbnailDiagnostics,
    cartThumbnailWasDiagnosed: cartThumbnailWasDiagnosed,
    CART_THUMBNAIL_MAX_EDGE: CART_THUMBNAIL_MAX_EDGE,
    CART_THUMBNAIL_MAX_BYTES: CART_THUMBNAIL_MAX_BYTES,
    ELASTIC_TEXTURE_SOURCE: ELASTIC_TEXTURE_SOURCE,
    ELASTIC_TEXTURES: ELASTIC_TEXTURES,
    ELASTIC_BANDS: ELASTIC_BANDS,
    TEXT_LINE_HEIGHT_MULTIPLIER: TEXT_LINE_HEIGHT_MULTIPLIER,
    TEXT_LINE_GAP: TEXT_LINE_GAP,
    TEXT_BASELINE_MIDDLE_EM: Object.assign({}, TEXT_BASELINE_MIDDLE_EM),
    TEXT_INK_MARGIN: TEXT_INK_MARGIN,
    THEME_FILTER_GROUPS: THEME_FILTER_GROUPS,
    COLOUR_FILTER_GROUPS: COLOUR_FILTER_GROUPS,
    PREVIEW_TEMPLATES: PREVIEW_TEMPLATES.slice()
  };
});
